import { useState, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass, faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";
import { faHeart as faHeartSolid } from "@fortawesome/free-solid-svg-icons";
import { faHeart as faHeartRegular } from "@fortawesome/free-regular-svg-icons";
import { motion, useReducedMotion } from "framer-motion";
import supabase from "./supabaseClient";
import { useAuth } from "./AuthContext";

const faExternalLink = faExternalLinkAlt;

const researcherProfiles = [
  {
    id: 381,
    name: "Sanjeev Arora",
    category: "Internships",
    field: "STEM",
    description:
      "Researches theoretical computer science, machine learning theory, optimization, and deep learning foundations.",
    email: "arora@cs.princeton.edu",
  },
  {
    id: 382,
    name: "Sebastian Bubeck",
    category: "Internships",
    field: "STEM",
    description:
      "Works on machine learning theory, optimization, online algorithms, and foundations of artificial intelligence.",
    email: "sb2592@cs.princeton.edu",
  },
  {
    id: 383,
    name: "Danqi Chen",
    category: "Internships",
    field: "STEM",
    description:
      "Focuses on natural language processing, large language models, and machine reasoning systems.",
    email: "danqic@cs.princeton.edu",
  },
  {
    id: 384,
    name: "Thomas D.",
    category: "Internships",
    field: "STEM",
    description:
      "Conducts research in artificial intelligence, machine learning systems, and computational modeling.",
    email: "td8762@cs.princeton.edu",
  },
  {
    id: 385,
    name: "Vijay D.",
    category: "Internships",
    field: "STEM",
    description:
      "Researches machine learning, data-driven systems, and applied artificial intelligence.",
    email: "vd2713@cs.princeton.edu",
  },
  {
    id: 386,
    name: "Jia Deng",
    category: "Internships",
    field: "STEM",
    description:
      "Specializes in computer vision, visual recognition, and large-scale image understanding systems.",
    email: "jiadeng@cs.princeton.edu",
  },
  {
    id: 387,
    name: "Adam Dziedzic",
    category: "Internships",
    field: "STEM",
    description:
      "Studies privacy-preserving machine learning, differential privacy, and secure AI systems.",
    email: "ad6725@cs.princeton.edu",
  },
  {
    id: 388,
    name: "Benjamin Eysenbach",
    category: "Internships",
    field: "STEM",
    description:
      "Focuses on reinforcement learning, robotics, and autonomous decision-making systems.",
    email: "eysenbach@cs.princeton.edu",
  },
  {
    id: 389,
    name: "Jeffrey Fisac",
    category: "Internships",
    field: "STEM",
    description:
      "Researches robotics, control theory, and safe human-AI interaction systems.",
    email: "jfisac@princeton.edu",
  },
  {
    id: 390,
    name: "Aarti F.",
    category: "Internships",
    field: "STEM",
    description:
      "Works on artificial intelligence, machine learning applications, and data-driven modeling.",
    email: "af@cs.princeton.edu",
  },
  {
    id: 391,
    name: "Ruth Fong",
    category: "Internships",
    field: "STEM",
    description:
      "Studies interpretability, robustness, and explainability in deep learning systems.",
    email: "ruthfong@cs.princeton.edu",
  },
  {
    id: 392,
    name: "Daniel Funk",
    category: "Internships",
    field: "STEM",
    description:
      "Researches applied machine learning, AI systems, and computational data analysis.",
    email: "funk@cs.princeton.edu",
  },
  {
    id: 393,
    name: "Tom Goldstein",
    category: "Internships",
    field: "STEM",
    description:
      "Specializes in optimization, deep learning efficiency, and large-scale neural network training.",
    email: "tomg@cs.princeton.edu",
  },
  {
    id: 394,
    name: "Elad Hazan",
    category: "Internships",
    field: "STEM",
    description:
      "Focuses on optimization theory, online learning, and theoretical foundations of machine learning.",
    email: "ehazan@cs.princeton.edu",
  },
  {
    id: 395,
    name: "Michael Hopkins",
    category: "Internships",
    field: "STEM",
    description:
      "Researches natural language processing, language modeling, and AI reasoning systems.",
    email: "mh2913@cs.princeton.edu",
  },
  {
    id: 396,
    name: "Felix Heide",
    category: "Internships",
    field: "STEM",
    description:
      "Works in computational imaging, computer vision, and next-generation camera systems.",
    email: "fheide@cs.princeton.edu",
  },
  {
    id: 397,
    name: "Peter Henderson",
    category: "Internships",
    field: "STEM",
    description:
      "Studies reinforcement learning, robotics, and interactive AI systems.",
    email: "ph4162@cs.princeton.edu",
  },
  {
    id: 398,
    name: "Matthew Hu",
    category: "Internships",
    field: "STEM",
    description:
      "Researches machine learning systems and large-scale AI training pipelines.",
    email: "mh3586@cs.princeton.edu",
  },
  {
    id: 399,
    name: "Chi Jin",
    category: "Internships",
    field: "STEM",
    description:
      "Focuses on optimization, non-convex learning, and theoretical machine learning.",
    email: "chij@princeton.edu",
  },
  {
    id: 400,
    name: "Arpita Korolova",
    category: "Internships",
    field: "STEM",
    description:
      "Studies algorithmic fairness, privacy, and the societal impacts of AI systems.",
    email: "korolova@cs.princeton.edu",
  },
  {
    id: 401,
    name: "Bo Li",
    category: "Internships",
    field: "STEM",
    description:
      "Researches trustworthy machine learning, adversarial robustness, and AI security.",
    email: "bl8144@cs.princeton.edu",
  },
  {
    id: 402,
    name: "Liqiang Liu",
    category: "Internships",
    field: "STEM",
    description:
      "Focuses on machine learning theory, optimization algorithms, and scalable AI systems.",
    email: "ltliu@cs.princeton.edu",
  },
  {
    id: 403,
    name: "Zhuang Liu",
    category: "Internships",
    field: "STEM",
    description:
      "Specializes in computer vision, neural network efficiency, and deep learning optimization.",
    email: "zhuangl@cs.princeton.edu",
  },
  {
    id: 404,
    name: "Ani Majumdar",
    category: "Internships",
    field: "STEM",
    description:
      "Works in robotics, autonomous systems, and control theory for safe human-robot interaction.",
    email: "ani.majumdar@princeton.edu",
  },
  {
    id: 405,
    name: "Andrés Monroy-Hernández",
    category: "Internships",
    field: "STEM",
    description:
      "Researches human-computer interaction, social computing, and online community design.",
    email: "andresmh@cs.princeton.edu",
  },
  {
    id: 406,
    name: "Rohit Nair",
    category: "Internships",
    field: "STEM",
    description:
      "Focuses on machine learning systems and large-scale AI deployment.",
    email: "rn1627@cs.princeton.edu",
  },
  {
    id: 407,
    name: "Vijay Ramachandran",
    category: "Internships",
    field: "STEM",
    description:
      "Researches algorithms, graph theory, and high-performance computing systems.",
    email: "vr23@cs.princeton.edu",
  },
  {
    id: 408,
    name: "Shayak Sen",
    category: "Internships",
    field: "STEM",
    description:
      "Studies computer architecture, memory systems, and energy-efficient computing.",
    email: "smr@cs.princeton.edu",
  },
  {
    id: 409,
    name: "Sebastian Seung",
    category: "Internships",
    field: "STEM",
    description:
      "Works on neuroscience, connectomics, and machine learning for brain mapping.",
    email: "sseung@cs.princeton.edu",
  },
  {
    id: 410,
    name: "Benjamin Shapiro",
    category: "Internships",
    field: "STEM",
    description:
      "Researches distributed systems, databases, and large-scale computing infrastructure.",
    email: "bs37@princeton.edu",
  },
  {
    id: 411,
    name: "Zhong Shao",
    category: "Internships",
    field: "STEM",
    description:
      "Focuses on programming languages, secure systems, and formal verification.",
    email: "zhonge@cs.princeton.edu",
  },
  {
    id: 412,
    name: "Jia Duan",
    category: "Internships",
    field: "STEM",
    description:
      "Studies machine learning, statistical modeling, and AI applications in data science.",
    email: "jaduol@cs.princeton.edu",
  },
  {
    id: 413,
    name: "Rohan Anil",
    category: "Internships",
    field: "STEM",
    description:
      "Researches large language models, AI safety, and scalable machine learning systems.",
    email: "ra1336@cs.princeton.edu",
  },
  {
    id: 414,
    name: "Sahil Aggarwal",
    category: "Internships",
    field: "STEM",
    description:
      "Works on deep learning architectures and generative AI systems.",
    email: "sa6924@cs.princeton.edu",
  },
  {
    id: 415,
    name: "Arjun Anand",
    category: "Internships",
    field: "STEM",
    description:
      "Focuses on applied machine learning, optimization, and AI infrastructure.",
    email: "aa1698@cs.princeton.edu",
  },
  {
    id: 416,
    name: "Aman Agarwal",
    category: "Internships",
    field: "STEM",
    description:
      "Researches distributed systems and scalable cloud computing architectures.",
    email: "aa2279@cs.princeton.edu",
  },
  {
    id: 417,
    name: "Gaurav Bansal",
    category: "Internships",
    field: "STEM",
    description:
      "Studies AI interpretability, human-AI interaction, and machine learning robustness.",
    email: "gb5435@cs.princeton.edu",
  },
  {
    id: 418,
    name: "Shivam Bhatia",
    category: "Internships",
    field: "STEM",
    description:
      "Focuses on natural language processing and multimodal AI systems.",
    email: "sb6870@cs.princeton.edu",
  },
  {
    id: 419,
    name: "Ananya Banerjee",
    category: "Internships",
    field: "STEM",
    description:
      "Researches reinforcement learning and intelligent decision-making systems.",
    email: "ab4197@cs.princeton.edu",
  },
  {
    id: 420,
    name: "Alec Brown",
    category: "Internships",
    field: "STEM",
    description:
      "Works on computer vision, representation learning, and deep neural networks.",
    email: "ab7728@cs.princeton.edu",
  },
  [
  {
    "id": 421,
    "name": "Sanjeev Arora",
    "category": "Internships",
    "field": "STEM",
    "email": "arora@cs.princeton.edu"
  },
  {
    "id": 422,
    "name": "sb2592",
    "category": "Internships",
    "field": "STEM",
    "email": "sb2592@cs.princeton.edu"
  },
  {
    "id": 423,
    "name": "Danqi Chen",
    "category": "Internships",
    "field": "STEM",
    "email": "danqic@cs.princeton.edu"
  },
  {
    "id": 424,
    "name": "td8762",
    "category": "Internships",
    "field": "STEM",
    "email": "td8762@cs.princeton.edu"
  },
  {
    "id": 425,
    "name": "vd2713",
    "category": "Internships",
    "field": "STEM",
    "email": "vd2713@cs.princeton.edu"
  },
  {
    "id": 426,
    "name": "Jia Deng",
    "category": "Internships",
    "field": "STEM",
    "email": "jiadeng@cs.princeton.edu"
  },
  {
    "id": 427,
    "name": "ad6725",
    "category": "Internships",
    "field": "STEM",
    "email": "ad6725@cs.princeton.edu"
  },
  {
    "id": 428,
    "name": "Benjamin Eysenbach",
    "category": "Internships",
    "field": "STEM",
    "email": "eysenbach@cs.princeton.edu"
  },
  {
    "id": 429,
    "name": "Jaime Fisac",
    "category": "Internships",
    "field": "STEM",
    "email": "jfisac@princeton.edu"
  },
  {
    "id": 430,
    "name": "af",
    "category": "Internships",
    "field": "STEM",
    "email": "af@cs.princeton.edu"
  },
  {
    "id": 431,
    "name": "Ruth Fong",
    "category": "Internships",
    "field": "STEM",
    "email": "ruthfong@cs.princeton.edu"
  },
  {
    "id": 432,
    "name": "Adam Finkelstein / Thomas Funkhouser",
    "category": "Internships",
    "field": "STEM",
    "email": "funk@cs.princeton.edu"
  },
  {
    "id": 433,
    "name": "Tom Griffiths",
    "category": "Internships",
    "field": "STEM",
    "email": "tomg@cs.princeton.edu"
  },
  {
    "id": 434,
    "name": "Elad Hazan",
    "category": "Internships",
    "field": "STEM",
    "email": "ehazan@cs.princeton.edu"
  },
  {
    "id": 435,
    "name": "mh2913",
    "category": "Internships",
    "field": "STEM",
    "email": "mh2913@cs.princeton.edu"
  },
  {
    "id": 436,
    "name": "Felix Heide",
    "category": "Internships",
    "field": "STEM",
    "email": "fheide@cs.princeton.edu"
  },
  {
    "id": 437,
    "name": "ph4162",
    "category": "Internships",
    "field": "STEM",
    "email": "ph4162@cs.princeton.edu"
  },
  {
    "id": 438,
    "name": "mh3586",
    "category": "Internships",
    "field": "STEM",
    "email": "mh3586@cs.princeton.edu"
  },
  {
    "id": 439,
    "name": "Chi Jin",
    "category": "Internships",
    "field": "STEM",
    "email": "chij@princeton.edu"
  },
  {
    "id": 440,
    "name": "Aleksandra Korolova",
    "category": "Internships",
    "field": "STEM",
    "email": "korolova@cs.princeton.edu"
  },
  {
    "id": 441,
    "name": "bl8144",
    "category": "Internships",
    "field": "STEM",
    "email": "bl8144@cs.princeton.edu"
  },
  {
    "id": 442,
    "name": "Lydia Liu",
    "category": "Internships",
    "field": "STEM",
    "email": "ltliu@cs.princeton.edu"
  },
  {
    "id": 443,
    "name": "Zhuang Liu",
    "category": "Internships",
    "field": "STEM",
    "email": "zhuangl@cs.princeton.edu"
  },
  {
    "id": 444,
    "name": "Anirudha Majumdar",
    "category": "Internships",
    "field": "STEM",
    "email": "ani.majumdar@princeton.edu"
  },
  {
    "id": 445,
    "name": "Andres Monroy-Hernandez",
    "category": "Internships",
    "field": "STEM",
    "email": "andresmh@cs.columbia.edu"
  },
  {
    "id": 446,
    "name": "rn1627",
    "category": "Internships",
    "field": "STEM",
    "email": "rn1627@cs.princeton.edu"
  },
  {
    "id": 447,
    "name": "Karthik Narasimhan",
    "category": "Internships",
    "field": "STEM",
    "email": "karthikn@cs.princeton.edu"
  },
  {
    "id": 448,
    "name": "vr23",
    "category": "Internships",
    "field": "STEM",
    "email": "vr23@cs.princeton.edu"
  },
  {
    "id": 449,
    "name": "Szymon Rusinkiewicz",
    "category": "Internships",
    "field": "STEM",
    "email": "smr@cs.princeton.edu"
  },
  {
    "id": 450,
    "name": "Olga Russakovsky",
    "category": "Internships",
    "field": "STEM",
    "email": "olgarus@cs.princeton.edu"
  },
  {
    "id": 451,
    "name": "Sebastian Seung",
    "category": "Internships",
    "field": "STEM",
    "email": "sseung@cs.princeton.edu"
  },
  {
    "id": 452,
    "name": "bs37",
    "category": "Internships",
    "field": "STEM",
    "email": "bs37@princeton.edu"
  },
  {
    "id": 453,
    "name": "zhonge",
    "category": "Internships",
    "field": "STEM",
    "email": "zhonge@cs.princeton.edu"
  },
  {
    "id": 454,
    "name": "jaduol",
    "category": "Internships",
    "field": "STEM",
    "email": "jaduol@cs.princeton.edu"
  },
  {
    "id": 455,
    "name": "ra1336",
    "category": "Internships",
    "field": "STEM",
    "email": "ra1336@cs.princeton.edu"
  },
  {
    "id": 456,
    "name": "sa6924",
    "category": "Internships",
    "field": "STEM",
    "email": "sa6924@cs.princeton.edu"
  },
  {
    "id": 457,
    "name": "aa1698",
    "category": "Internships",
    "field": "STEM",
    "email": "aa1698@cs.princeton.edu"
  },
  {
    "id": 458,
    "name": "aa2279",
    "category": "Internships",
    "field": "STEM",
    "email": "aa2279@cs.princeton.edu"
  },
  {
    "id": 459,
    "name": "gb5435",
    "category": "Internships",
    "field": "STEM",
    "email": "gb5435@cs.princeton.edu"
  },
  {
    "id": 460,
    "name": "sb6870",
    "category": "Internships",
    "field": "STEM",
    "email": "sb6870@cs.princeton.edu"
  },
  {
    "id": 461,
    "name": "ab4197",
    "category": "Internships",
    "field": "STEM",
    "email": "ab4197@cs.princeton.edu"
  },
  {
    "id": 462,
    "name": "ab7728",
    "category": "Internships",
    "field": "STEM",
    "email": "ab7728@cs.princeton.edu"
  },
  {
    "id": 463,
    "name": "db1224",
    "category": "Internships",
    "field": "STEM",
    "email": "db1224@cs.princeton.edu"
  },
  {
    "id": 464,
    "name": "Christopher Brooks",
    "category": "Internships",
    "field": "STEM",
    "email": "cabrooks@cs.princeton.edu"
  },
  {
    "id": 465,
    "name": "gb4745",
    "category": "Internships",
    "field": "STEM",
    "email": "gb4745@cs.princeton.edu"
  },
  {
    "id": 466,
    "name": "cc4880",
    "category": "Internships",
    "field": "STEM",
    "email": "cc4880@cs.princeton.edu"
  },
  {
    "id": 467,
    "name": "lc1556",
    "category": "Internships",
    "field": "STEM",
    "email": "lc1556@cs.princeton.edu"
  },
  {
    "id": 468,
    "name": "zc5794",
    "category": "Internships",
    "field": "STEM",
    "email": "zc5794@cs.princeton.edu"
  },
  {
    "id": 469,
    "name": "janeec",
    "category": "Internships",
    "field": "STEM",
    "email": "janeec@cs.princeton.edu"
  },
  {
    "id": 470,
    "name": "wc9403",
    "category": "Internships",
    "field": "STEM",
    "email": "wc9403@cs.princeton.edu"
  },
  {
    "id": 471,
    "name": "ac4802",
    "category": "Internships",
    "field": "STEM",
    "email": "ac4802@cs.princeton.edu"
  },
  {
    "id": 472,
    "name": "aa0008",
    "category": "Internships",
    "field": "STEM",
    "email": "aa0008@cs.princeton.edu"
  },
  {
    "id": 473,
    "name": "bc2188",
    "category": "Internships",
    "field": "STEM",
    "email": "bc2188@cs.princeton.edu"
  },
  {
    "id": 474,
    "name": "hc5019",
    "category": "Internships",
    "field": "STEM",
    "email": "hc5019@cs.princeton.edu"
  },
  {
    "id": 475,
    "name": "jc93",
    "category": "Internships",
    "field": "STEM",
    "email": "jc93@cs.princeton.edu"
  },
  {
    "id": 476,
    "name": "yc6206",
    "category": "Internships",
    "field": "STEM",
    "email": "yc6206@cs.princeton.edu"
  },
  {
    "id": 477,
    "name": "sc8637",
    "category": "Internships",
    "field": "STEM",
    "email": "sc8637@cs.princeton.edu"
  },
  {
    "id": 478,
    "name": "jc5933",
    "category": "Internships",
    "field": "STEM",
    "email": "jc5933@cs.princeton.edu"
  },
  {
    "id": 479,
    "name": "od2961",
    "category": "Internships",
    "field": "STEM",
    "email": "od2961@cs.princeton.edu"
  },
  {
    "id": 480,
    "name": "xd7812",
    "category": "Internships",
    "field": "STEM",
    "email": "xd7812@cs.princeton.edu"
  },
  {
    "id": 481,
    "name": "ld3276",
    "category": "Internships",
    "field": "STEM",
    "email": "ld3276@cs.princeton.edu"
  },
  {
    "id": 482,
    "name": "sd9968",
    "category": "Internships",
    "field": "STEM",
    "email": "sd9968@cs.princeton.edu"
  },
  {
    "id": 483,
    "name": "sd0937",
    "category": "Internships",
    "field": "STEM",
    "email": "sd0937@cs.princeton.edu"
  },
  {
    "id": 484,
    "name": "lg2326",
    "category": "Internships",
    "field": "STEM",
    "email": "lg2326@cs.princeton.edu"
  },
  {
    "id": 485,
    "name": "rg9360",
    "category": "Internships",
    "field": "STEM",
    "email": "rg9360@cs.princeton.edu"
  },
  {
    "id": 486,
    "name": "lglisic",
    "category": "Internships",
    "field": "STEM",
    "email": "lglisic@cs.princeton.edu"
  },
  {
    "id": 487,
    "name": "saezdiez",
    "category": "Internships",
    "field": "STEM",
    "email": "saezdiez@cs.princeton.edu"
  },
  {
    "id": 488,
    "name": "jg3190",
    "category": "Internships",
    "field": "STEM",
    "email": "jg3190@cs.princeton.edu"
  },
  {
    "id": 489,
    "name": "ag5008",
    "category": "Internships",
    "field": "STEM",
    "email": "ag5008@cs.princeton.edu"
  },
  {
    "id": 490,
    "name": "wg0420",
    "category": "Internships",
    "field": "STEM",
    "email": "wg0420@cs.princeton.edu"
  },
  {
    "id": 491,
    "name": "bh7032",
    "category": "Internships",
    "field": "STEM",
    "email": "bh7032@cs.princeton.edu"
  },
  {
    "id": 492,
    "name": "lh2046",
    "category": "Internships",
    "field": "STEM",
    "email": "lh2046@cs.princeton.edu"
  },
  {
    "id": 493,
    "name": "yh0068",
    "category": "Internships",
    "field": "STEM",
    "email": "yh0068@cs.princeton.edu"
  },
  {
    "id": 494,
    "name": "rh5555",
    "category": "Internships",
    "field": "STEM",
    "email": "rh5555@cs.princeton.edu"
  },
  {
    "id": 495,
    "name": "hh2605",
    "category": "Internships",
    "field": "STEM",
    "email": "hh2605@cs.princeton.edu"
  },
  {
    "id": 496,
    "name": "rj5498",
    "category": "Internships",
    "field": "STEM",
    "email": "rj5498@cs.princeton.edu"
  },
  {
    "id": 497,
    "name": "mj7341",
    "category": "Internships",
    "field": "STEM",
    "email": "mj7341@cs.princeton.edu"
  },
  {
    "id": 498,
    "name": "aj9792",
    "category": "Internships",
    "field": "STEM",
    "email": "aj9792@cs.princeton.edu"
  },
  {
    "id": 499,
    "name": "hj7206",
    "category": "Internships",
    "field": "STEM",
    "email": "hj7206@cs.princeton.edu"
  },
  {
    "id": 500,
    "name": "sk9014",
    "category": "Internships",
    "field": "STEM",
    "email": "sk9014@cs.princeton.edu"
  },
  {
    "id": 501,
    "name": "skaur",
    "category": "Internships",
    "field": "STEM",
    "email": "skaur@cs.princeton.edu"
  },
  {
    "id": 502,
    "name": "kk2285",
    "category": "Internships",
    "field": "STEM",
    "email": "kk2285@cs.princeton.edu"
  },
  {
    "id": 503,
    "name": "akaz",
    "category": "Internships",
    "field": "STEM",
    "email": "akaz@cs.princeton.edu"
  },
  {
    "id": 504,
    "name": "dk9893",
    "category": "Internships",
    "field": "STEM",
    "email": "dk9893@cs.princeton.edu"
  },
  {
    "id": 505,
    "name": "dk5684",
    "category": "Internships",
    "field": "STEM",
    "email": "dk5684@cs.princeton.edu"
  },
  {
    "id": 506,
    "name": "ak9096",
    "category": "Internships",
    "field": "STEM",
    "email": "ak9096@cs.princeton.edu"
  },
  {
    "id": 507,
    "name": "pk1124",
    "category": "Internships",
    "field": "STEM",
    "email": "pk1124@cs.princeton.edu"
  },
  {
    "id": 508,
    "name": "wk6312",
    "category": "Internships",
    "field": "STEM",
    "email": "wk6312@cs.princeton.edu"
  },
  {
    "id": 509,
    "name": "sk7524",
    "category": "Internships",
    "field": "STEM",
    "email": "sk7524@cs.princeton.edu"
  },
  {
    "id": 510,
    "name": "jl8975",
    "category": "Internships",
    "field": "STEM",
    "email": "jl8975@cs.princeton.edu"
  },
  {
    "id": 511,
    "name": "cl6486",
    "category": "Internships",
    "field": "STEM",
    "email": "cl6486@cs.princeton.edu"
  },
  {
    "id": 512,
    "name": "yl8184",
    "category": "Internships",
    "field": "STEM",
    "email": "yl8184@cs.princeton.edu"
  },
  {
    "id": 513,
    "name": "bl6948",
    "category": "Internships",
    "field": "STEM",
    "email": "bl6948@cs.princeton.edu"
  },
  {
    "id": 514,
    "name": "el1685",
    "category": "Internships",
    "field": "STEM",
    "email": "el1685@cs.princeton.edu"
  },
  {
    "id": 515,
    "name": "kl2471",
    "category": "Internships",
    "field": "STEM",
    "email": "kl2471@cs.princeton.edu"
  },
  {
    "id": 516,
    "name": "xl9353",
    "category": "Internships",
    "field": "STEM",
    "email": "xl9353@cs.princeton.edu"
  },
  {
    "id": 517,
    "name": "pl3522",
    "category": "Internships",
    "field": "STEM",
    "email": "pl3522@cs.princeton.edu"
  },
  {
    "id": 518,
    "name": "rl5886",
    "category": "Internships",
    "field": "STEM",
    "email": "rl5886@cs.princeton.edu"
  },
  {
    "id": 519,
    "name": "sl2998",
    "category": "Internships",
    "field": "STEM",
    "email": "sl2998@cs.princeton.edu"
  },
  {
    "id": 520,
    "name": "yl8744",
    "category": "Internships",
    "field": "STEM",
    "email": "yl8744@cs.princeton.edu"
  },
  {
    "id": 521,
    "name": "tl0463",
    "category": "Internships",
    "field": "STEM",
    "email": "tl0463@cs.princeton.edu"
  },
  {
    "id": 522,
    "name": "Jiawei Zhou / Jiawei Han",
    "category": "Internships",
    "field": "STEM",
    "email": "jiaweim@cs.princeton.edu"
  },
  {
    "id": 523,
    "name": "Zeyu Mao",
    "category": "Internships",
    "field": "STEM",
    "email": "zeyum@cs.princeton.edu"
  },
  {
    "id": 524,
    "name": "km5839",
    "category": "Internships",
    "field": "STEM",
    "email": "km5839@cs.princeton.edu"
  },
  {
    "id": 525,
    "name": "km9567",
    "category": "Internships",
    "field": "STEM",
    "email": "km9567@cs.princeton.edu"
  },
  {
    "id": 526,
    "name": "rm9688",
    "category": "Internships",
    "field": "STEM",
    "email": "rm9688@cs.princeton.edu"
  },
  {
    "id": 527,
    "name": "mg7411",
    "category": "Internships",
    "field": "STEM",
    "email": "mg7411@cs.princeton.edu"
  },
  {
    "id": 528,
    "name": "lm5483",
    "category": "Internships",
    "field": "STEM",
    "email": "lm5483@cs.princeton.edu"
  },
  {
    "id": 529,
    "name": "em4240",
    "category": "Internships",
    "field": "STEM",
    "email": "em4240@cs.princeton.edu"
  },
  {
    "id": 530,
    "name": "lm2052",
    "category": "Internships",
    "field": "STEM",
    "email": "lm2052@cs.princeton.edu"
  },
  {
    "id": 531,
    "name": "mm2608",
    "category": "Internships",
    "field": "STEM",
    "email": "mm2608@cs.princeton.edu"
  },
  {
    "id": 532,
    "name": "nnadeem",
    "category": "Internships",
    "field": "STEM",
    "email": "nnadeem@cs.princeton.edu"
  },
  {
    "id": 533,
    "name": "vn1332",
    "category": "Internships",
    "field": "STEM",
    "email": "vn1332@cs.princeton.edu"
  },
  {
    "id": 534,
    "name": "tn5879",
    "category": "Internships",
    "field": "STEM",
    "email": "tn5879@cs.princeton.edu"
  },
  {
    "id": 535,
    "name": "kn3194",
    "category": "Internships",
    "field": "STEM",
    "email": "kn3194@cs.princeton.edu"
  },
  {
    "id": 536,
    "name": "Vivien",
    "category": "Internships",
    "field": "STEM",
    "email": "vivienn@cs.princeton.edu"
  },
  {
    "id": 537,
    "name": "mn4560",
    "category": "Internships",
    "field": "STEM",
    "email": "mn4560@cs.princeton.edu"
  },
  {
    "id": 538,
    "name": "rn3004",
    "category": "Internships",
    "field": "STEM",
    "email": "rn3004@cs.princeton.edu"
  },
  {
    "id": 539,
    "name": "jn0704",
    "category": "Internships",
    "field": "STEM",
    "email": "jn0704@cs.princeton.edu"
  },
  {
    "id": 540,
    "name": "ro6000",
    "category": "Internships",
    "field": "STEM",
    "email": "ro6000@cs.princeton.edu"
  },
  {
    "id": 541,
    "name": "to6377",
    "category": "Internships",
    "field": "STEM",
    "email": "to6377@cs.princeton.edu"
  },
  {
    "id": 542,
    "name": "jo5483",
    "category": "Internships",
    "field": "STEM",
    "email": "jo5483@cs.princeton.edu"
  },
  {
    "id": 543,
    "name": "ap34",
    "category": "Internships",
    "field": "STEM",
    "email": "ap34@cs.princeton.edu"
  },
  {
    "id": 544,
    "name": "mp8772",
    "category": "Internships",
    "field": "STEM",
    "email": "mp8772@cs.princeton.edu"
  },
  {
    "id": 545,
    "name": "Juhyun Park",
    "category": "Internships",
    "field": "STEM",
    "email": "juhyunp@cs.princeton.edu"
  },
  {
    "id": 546,
    "name": "Rohan Padhye",
    "category": "Internships",
    "field": "STEM",
    "email": "rohanbp@cs.princeton.edu"
  },
  {
    "id": 547,
    "name": "ar8564",
    "category": "Internships",
    "field": "STEM",
    "email": "ar8564@cs.princeton.edu"
  },
  {
    "id": 548,
    "name": "rraghu",
    "category": "Internships",
    "field": "STEM",
    "email": "rraghu@cs.princeton.edu"
  },
  {
    "id": 549,
    "name": "srane",
    "category": "Internships",
    "field": "STEM",
    "email": "srane@cs.princeton.edu"
  },
  {
    "id": 550,
    "name": "lr1151",
    "category": "Internships",
    "field": "STEM",
    "email": "lr1151@cs.princeton.edu"
  },
  {
    "id": 551,
    "name": "nr3764",
    "category": "Internships",
    "field": "STEM",
    "email": "nr3764@cs.princeton.edu"
  },
  {
    "id": 552,
    "name": "nr14",
    "category": "Internships",
    "field": "STEM",
    "email": "nr14@cs.princeton.edu"
  },
  {
    "id": 553,
    "name": "Gisela Roeder",
    "category": "Internships",
    "field": "STEM",
    "email": "groeder@cs.princeton.edu"
  },
  {
    "id": 554,
    "name": "fs4423",
    "category": "Internships",
    "field": "STEM",
    "email": "fs4423@cs.princeton.edu"
  },
  {
    "id": 555,
    "name": "ks7893",
    "category": "Internships",
    "field": "STEM",
    "email": "ks7893@cs.princeton.edu"
  },
  {
    "id": 556,
    "name": "ss3419",
    "category": "Internships",
    "field": "STEM",
    "email": "ss3419@cs.princeton.edu"
  },
  {
    "id": 557,
    "name": "shatford",
    "category": "Internships",
    "field": "STEM",
    "email": "shatford@cs.princeton.edu"
  },
  {
    "id": 558,
    "name": "as1669",
    "category": "Internships",
    "field": "STEM",
    "email": "as1669@cs.princeton.edu"
  },
  {
    "id": 559,
    "name": "zs7353",
    "category": "Internships",
    "field": "STEM",
    "email": "zs7353@cs.princeton.edu"
  },
  {
    "id": 560,
    "name": "Daniel Shustin",
    "category": "Internships",
    "field": "STEM",
    "email": "dshustin@cs.princeton.edu"
  },
  {
    "id": 561,
    "name": "solodova",
    "category": "Internships",
    "field": "STEM",
    "email": "solodova@cs.princeton.edu"
  },
  {
    "id": 562,
    "name": "is7710",
    "category": "Internships",
    "field": "STEM",
    "email": "is7710@cs.princeton.edu"
  },
  {
    "id": 563,
    "name": "js2694",
    "category": "Internships",
    "field": "STEM",
    "email": "js2694@cs.princeton.edu"
  },
  {
    "id": 564,
    "name": "kt4454",
    "category": "Internships",
    "field": "STEM",
    "email": "kt4454@cs.princeton.edu"
  },
  {
    "id": 565,
    "name": "st4629",
    "category": "Internships",
    "field": "STEM",
    "email": "st4629@cs.princeton.edu"
  },
  {
    "id": 566,
    "name": "ct0705",
    "category": "Internships",
    "field": "STEM",
    "email": "ct0705@cs.princeton.edu"
  },
  {
    "id": 567,
    "name": "bt4811",
    "category": "Internships",
    "field": "STEM",
    "email": "bt4811@cs.princeton.edu"
  },
  {
    "id": 568,
    "name": "Jens Tuyls",
    "category": "Internships",
    "field": "STEM",
    "email": "jtuyls@cs.princeton.edu"
  },
  {
    "id": 569,
    "name": "cv6106",
    "category": "Internships",
    "field": "STEM",
    "email": "cv6106@cs.princeton.edu"
  },
  {
    "id": 570,
    "name": "hjwang",
    "category": "Internships",
    "field": "STEM",
    "email": "hjwang@cs.princeton.edu"
  },
  {
    "id": 571,
    "name": "bw5889",
    "category": "Internships",
    "field": "STEM",
    "email": "bw5889@cs.princeton.edu"
  },
  {
    "id": 572,
    "name": "ww0372",
    "category": "Internships",
    "field": "STEM",
    "email": "ww0372@cs.princeton.edu"
  },
  {
    "id": 573,
    "name": "yw7685",
    "category": "Internships",
    "field": "STEM",
    "email": "yw7685@cs.princeton.edu"
  },
  {
    "id": 574,
    "name": "kw2960",
    "category": "Internships",
    "field": "STEM",
    "email": "kw2960@cs.princeton.edu"
  },
  {
    "id": 575,
    "name": "hw0544",
    "category": "Internships",
    "field": "STEM",
    "email": "hw0544@cs.princeton.edu"
  },
  {
    "id": 576,
    "name": "dw1978",
    "category": "Internships",
    "field": "STEM",
    "email": "dw1978@cs.princeton.edu"
  },
  {
    "id": 577,
    "name": "jw4199",
    "category": "Internships",
    "field": "STEM",
    "email": "jw4199@cs.princeton.edu"
  },
  {
    "id": 578,
    "name": "sw2776",
    "category": "Internships",
    "field": "STEM",
    "email": "sw2776@cs.princeton.edu"
  },
  {
    "id": 579,
    "name": "xw5676",
    "category": "Internships",
    "field": "STEM",
    "email": "xw5676@cs.princeton.edu"
  },
  {
    "id": 580,
    "name": "yw1361",
    "category": "Internships",
    "field": "STEM",
    "email": "yw1361@cs.princeton.edu"
  },
  {
    "id": 581,
    "name": "zx8205",
    "category": "Internships",
    "field": "STEM",
    "email": "zx8205@cs.princeton.edu"
  },
  {
    "id": 582,
    "name": "yx2653",
    "category": "Internships",
    "field": "STEM",
    "email": "yx2653@cs.princeton.edu"
  },
  {
    "id": 583,
    "name": "ry9811",
    "category": "Internships",
    "field": "STEM",
    "email": "ry9811@cs.princeton.edu"
  },
  {
    "id": 584,
    "name": "wy9086",
    "category": "Internships",
    "field": "STEM",
    "email": "wy9086@cs.princeton.edu"
  },
  {
    "id": 585,
    "name": "hyen",
    "category": "Internships",
    "field": "STEM",
    "email": "hyen@cs.princeton.edu"
  },
  {
    "id": 586,
    "name": "yy8435",
    "category": "Internships",
    "field": "STEM",
    "email": "yy8435@cs.princeton.edu"
  },
  {
    "id": 587,
    "name": "nobliney",
    "category": "Internships",
    "field": "STEM",
    "email": "nobliney@cs.princeton.edu"
  },
  {
    "id": 588,
    "name": "jiatongy",
    "category": "Internships",
    "field": "STEM",
    "email": "jiatongy@cs.princeton.edu"
  },
  {
    "id": 589,
    "name": "tz6037",
    "category": "Internships",
    "field": "STEM",
    "email": "tz6037@cs.princeton.edu"
  },
  {
    "id": 590,
    "name": "bz1474",
    "category": "Internships",
    "field": "STEM",
    "email": "bz1474@cs.princeton.edu"
  },
  {
    "id": 591,
    "name": "cz1179",
    "category": "Internships",
    "field": "STEM",
    "email": "cz1179@cs.princeton.edu"
  },
  {
    "id": 592,
    "name": "kz9921",
    "category": "Internships",
    "field": "STEM",
    "email": "kz9921@cs.princeton.edu"
  },
  {
    "id": 593,
    "name": "lz3156",
    "category": "Internships",
    "field": "STEM",
    "email": "lz3156@cs.princeton.edu"
  },
  {
    "id": 594,
    "name": "haoyu",
    "category": "Internships",
    "field": "STEM",
    "email": "haoyu@cs.princeton.edu"
  },
  {
    "id": 595,
    "name": "cz8792",
    "category": "Internships",
    "field": "STEM",
    "email": "cz8792@cs.princeton.edu"
  },
  {
    "id": 596,
    "name": "lz5066",
    "category": "Internships",
    "field": "STEM",
    "email": "lz5066@cs.princeton.edu"
  },
  {
    "id": 597,
    "name": "tz9136",
    "category": "Internships",
    "field": "STEM",
    "email": "tz9136@cs.princeton.edu"
  },
  {
    "id": 598,
    "name": "xz4134",
    "category": "Internships",
    "field": "STEM",
    "email": "xz4134@cs.princeton.edu"
  },
  {
    "id": 599,
    "name": "yz7608",
    "category": "Internships",
    "field": "STEM",
    "email": "yz7608@cs.princeton.edu"
  },
  {
    "id": 600,
    "name": "Clark Barrett",
    "category": "Internships",
    "field": "STEM",
    "email": "barrettc@stanford.edu"
  },
  {
    "id": 601,
    "name": "Ehsan Adeli",
    "category": "Internships",
    "field": "STEM",
    "email": "eadeli@stanford.edu"
  },
  {
    "id": 602,
    "name": "Maneesh Agrawala",
    "category": "Internships",
    "field": "STEM",
    "email": "maneesh@cs.stanford.edu"
  },
  {
    "id": 603,
    "name": "Michael S. Bernstein",
    "category": "Internships",
    "field": "STEM",
    "email": "msb@cs.stanford.edu"
  },
  {
    "id": 604,
    "name": "Eunice Chang",
    "category": "Internships",
    "field": "STEM",
    "email": "echang@cs.stanford.edu"
  },
  {
    "id": 605,
    "name": "Dorottya Demszky",
    "category": "Internships",
    "field": "STEM",
    "email": "ddemszky@stanford.edu"
  },
  {
    "id": 606,
    "name": "Adrien Gaidon",
    "category": "Internships",
    "field": "STEM",
    "email": "adrien.gaidon@stanford.edu"
  },
  {
    "id": 607,
    "name": "Carlos Guestrin",
    "category": "Internships",
    "field": "STEM",
    "email": "guestrin@stanford.edu"
  },
  {
    "id": 608,
    "name": "Leonidas Guibas",
    "category": "Internships",
    "field": "STEM",
    "email": "guibas@cs.stanford.edu"
  },
  {
    "id": 609,
    "name": "Tatsunori Hashimoto",
    "category": "Internships",
    "field": "STEM",
    "email": "thashim@stanford.edu"
  },
  {
    "id": 610,
    "name": "Dan Jurafsky",
    "category": "Internships",
    "field": "STEM",
    "email": "jurafsky@stanford.edu"
  },
  {
    "id": 611,
    "name": "Oussama Khatib",
    "category": "Internships",
    "field": "STEM",
    "email": "KHATIB@CS.stanford.edu"
  },
  {
    "id": 612,
    "name": "Sanmi Koyejo",
    "category": "Internships",
    "field": "STEM",
    "email": "sanmi@stanford.edu"
  },
  {
    "id": 613,
    "name": "Monica Lam",
    "category": "Internships",
    "field": "STEM",
    "email": "lam@stanford.edu"
  },
  {
    "id": 614,
    "name": "James Landay",
    "category": "Internships",
    "field": "STEM",
    "email": "landay@stanford.edu"
  },
  {
    "id": 615,
    "name": "Fei-Fei Li",
    "category": "Internships",
    "field": "STEM",
    "email": "feifeili@stanford.edu"
  },
  {
    "id": 616,
    "name": "Percy Liang",
    "category": "Internships",
    "field": "STEM",
    "email": "pliang@cs.stanford.edu"
  },
  {
    "id": 617,
    "name": "C. Karen Liu",
    "category": "Internships",
    "field": "STEM",
    "email": "ckliu38@stanford.edu"
  },
  {
    "id": 618,
    "name": "Christopher Manning",
    "category": "Internships",
    "field": "STEM",
    "email": "manning@stanford.edu"
  },
  {
    "id": 619,
    "name": "Azalia Mirhoseini",
    "category": "Internships",
    "field": "STEM",
    "email": "azalia@stanford.edu"
  },
  {
    "id": 620,
    "name": "Kunle Olukotun",
    "category": "Internships",
    "field": "STEM",
    "email": "kunle@stanford.edu"
  },
  {
    "id": 621,
    "name": "Christopher Ré",
    "category": "Internships",
    "field": "STEM",
    "email": "chrismre@stanford.edu"
  },
  {
    "id": 622,
    "name": "Mehran Sahami",
    "category": "Internships",
    "field": "STEM",
    "email": "sahami@cs.stanford.edu"
  },
  {
    "id": 623,
    "name": "Shuran Song",
    "category": "Internships",
    "field": "STEM",
    "email": "shuran@stanford.edu"
  },
  {
    "id": 624,
    "name": "Hariharan Subramanian",
    "category": "Internships",
    "field": "STEM",
    "email": "harihars@stanford.edu"
  },
  {
    "id": 625,
    "name": "Jiajun Wu",
    "category": "Internships",
    "field": "STEM",
    "email": "jiajunwu@cs.stanford.edu"
  },
  {
    "id": 626,
    "name": "Daniel Yamins",
    "category": "Internships",
    "field": "STEM",
    "email": "yamins@stanford.edu"
  },
  {
    "id": 627,
    "name": "Diyi Yang",
    "category": "Internships",
    "field": "STEM",
    "email": "diyiy@stanford.edu"
  },
  {
    "id": 628,
    "name": "James Zou",
    "category": "Internships",
    "field": "STEM",
    "email": "jamesz@stanford.edu"
  },
  {
    "id": 629,
    "name": "David Alleysson / David Malan",
    "category": "Internships",
    "field": "STEM",
    "email": "dam@seas.harvard.edu"
  },
  {
    "id": 630,
    "name": "Nada Amin",
    "category": "Internships",
    "field": "STEM",
    "email": "namin@seas.harvard.edu"
  },
  {
    "id": 631,
    "name": "Demba Ba",
    "category": "Internships",
    "field": "STEM",
    "email": "demba@seas.harvard.edu"
  },
  {
    "id": 632,
    "name": "Kianté Brantley",
    "category": "Internships",
    "field": "STEM",
    "email": "kdbrantley@seas.harvard.edu"
  },
  {
    "id": 633,
    "name": "Michael P. Brenner",
    "category": "Internships",
    "field": "STEM",
    "email": "brenner@seas.harvard.edu"
  },
  {
    "id": 634,
    "name": "Flavio Calmon",
    "category": "Internships",
    "field": "STEM",
    "email": "fcalmon@fas.harvard.edu"
  },
  {
    "id": 635,
    "name": "Sitan Chen",
    "category": "Internships",
    "field": "STEM",
    "email": "sitan@seas.harvard.edu"
  },
  {
    "id": 636,
    "name": "Yiling Chen",
    "category": "Internships",
    "field": "STEM",
    "email": "yiling@seas.harvard.edu"
  },
  {
    "id": 637,
    "name": "Sunghoon Chung",
    "category": "Internships",
    "field": "STEM",
    "email": "schung@fas.harvard.edu"
  },
  {
    "id": 638,
    "name": "Finale Doshi-Velez",
    "category": "Internships",
    "field": "STEM",
    "email": "finale@seas.harvard.edu"
  },
  {
    "id": 639,
    "name": "Cynthia Dwork",
    "category": "Internships",
    "field": "STEM",
    "email": "dwork@seas.harvard.edu"
  },
  {
    "id": 640,
    "name": "Jonathan Frankle",
    "category": "Internships",
    "field": "STEM",
    "email": "jfrankle@seas.harvard.edu"
  },
  {
    "id": 641,
    "name": "Krzysztof Gajos",
    "category": "Internships",
    "field": "STEM",
    "email": "kgajos@seas.harvard.edu"
  },
  {
    "id": 642,
    "name": "Sanjeev Gil",
    "category": "Internships",
    "field": "STEM",
    "email": "sgil@seas.harvard.edu"
  },
  {
    "id": 643,
    "name": "Elena Glassman",
    "category": "Internships",
    "field": "STEM",
    "email": "glassman@seas.harvard.edu"
  },
  {
    "id": 644,
    "name": "Yanna Igovich",
    "category": "Internships",
    "field": "STEM",
    "email": "yannaigo@fas.harvard.edu"
  },
  {
    "id": 645,
    "name": "Barbara Grosz",
    "category": "Internships",
    "field": "STEM",
    "email": "grosz@g.harvard.edu"
  },
  {
    "id": 646,
    "name": "vj",
    "category": "Internships",
    "field": "STEM",
    "email": "vj@eecs.harvard.edu"
  },
  {
    "id": 647,
    "name": "Lucas Janson",
    "category": "Internships",
    "field": "STEM",
    "email": "ljanson@fas.harvard.edu"
  },
  {
    "id": 648,
    "name": "Sham Kakade",
    "category": "Internships",
    "field": "STEM",
    "email": "sham@seas.harvard.edu"
  },
  {
    "id": 649,
    "name": "Petros Koumoutsakos",
    "category": "Internships",
    "field": "STEM",
    "email": "petros@seas.harvard.edu"
  },
  {
    "id": 650,
    "name": "bkoz",
    "category": "Internships",
    "field": "STEM",
    "email": "bkoz@seas.harvard.edu"
  },
  {
    "id": 651,
    "name": "Himabindu Lakkaraju",
    "category": "Internships",
    "field": "STEM",
    "email": "hlakkaraju@hbs.edu"
  },
  {
    "id": 652,
    "name": "Na Li",
    "category": "Internships",
    "field": "STEM",
    "email": "nali@seas.harvard.edu"
  },
  {
    "id": 653,
    "name": "Jia Liu",
    "category": "Internships",
    "field": "STEM",
    "email": "jia_liu@seas.harvard.edu"
  },
  {
    "id": 654,
    "name": "Yue M. Lu",
    "category": "Internships",
    "field": "STEM",
    "email": "yuelu@seas.harvard.edu"
  },
  {
    "id": 655,
    "name": "Michael Mitzenmacher",
    "category": "Internships",
    "field": "STEM",
    "email": "michaelm@eecs.harvard.edu"
  },
  {
    "id": 656,
    "name": "Susan Murphy",
    "category": "Internships",
    "field": "STEM",
    "email": "samurphy@fas.harvard.edu"
  },
  {
    "id": 657,
    "name": "David C. Parkes",
    "category": "Internships",
    "field": "STEM",
    "email": "parkes@eecs.harvard.edu"
  },
  {
    "id": 658,
    "name": "Cengiz Pehlevan",
    "category": "Internships",
    "field": "STEM",
    "email": "cpehlevan@seas.harvard.edu"
  },
  {
    "id": 659,
    "name": "Hanspeter Pfister",
    "category": "Internships",
    "field": "STEM",
    "email": "pfister@seas.harvard.edu"
  },
  {
    "id": 660,
    "name": "Ariel Procaccia",
    "category": "Internships",
    "field": "STEM",
    "email": "arielpro@seas.harvard.edu"
  },
  {
    "id": 661,
    "name": "Pavlos Protopapas",
    "category": "Internships",
    "field": "STEM",
    "email": "pavlos@seas.harvard.edu"
  },
  {
    "id": 662,
    "name": "Anna Seigal",
    "category": "Internships",
    "field": "STEM",
    "email": "aseigal@seas.harvard.edu"
  },
  {
    "id": 663,
    "name": "Stuart Shieber",
    "category": "Internships",
    "field": "STEM",
    "email": "shieber@seas.harvard.edu"
  },
  {
    "id": 664,
    "name": "slade",
    "category": "Internships",
    "field": "STEM",
    "email": "slade@seas.harvard.edu"
  },
  {
    "id": 665,
    "name": "Michael D. Smith",
    "category": "Internships",
    "field": "STEM",
    "email": "mike_smith@harvard.edu"
  },
  {
    "id": 666,
    "name": "Milind Tambe",
    "category": "Internships",
    "field": "STEM",
    "email": "tambe@seas.harvard.edu"
  },
  {
    "id": 667,
    "name": "Leslie Valiant",
    "category": "Internships",
    "field": "STEM",
    "email": "valiant@seas.harvard.edu"
  },
  {
    "id": 668,
    "name": "Fernanda Viégas",
    "category": "Internships",
    "field": "STEM",
    "email": "fernanda@seas.harvard.edu"
  },
  {
    "id": 669,
    "name": "Martin Wattenberg",
    "category": "Internships",
    "field": "STEM",
    "email": "wattenberg@seas.harvard.edu"
  },
  {
    "id": 670,
    "name": "Melanie Weber",
    "category": "Internships",
    "field": "STEM",
    "email": "mweber@seas.harvard.edu"
  },
  {
    "id": 671,
    "name": "Robert J. Wood",
    "category": "Internships",
    "field": "STEM",
    "email": "rjwood@eecs.harvard.edu"
  },
  {
    "id": 672,
    "name": "Hank Yang",
    "category": "Internships",
    "field": "STEM",
    "email": "hankyang@seas.harvard.edu"
  },
  {
    "id": 673,
    "name": "Wei Yang",
    "category": "Internships",
    "field": "STEM",
    "email": "wyang@seas.harvard.edu"
  },
  {
    "id": 674,
    "name": "Todd Zickler",
    "category": "Internships",
    "field": "STEM",
    "email": "zickler@seas.harvard.edu"
  },
  {
    "id": 675,
    "name": "a2jz",
    "category": "Internships",
    "field": "STEM",
    "email": "a2jz@law.harvard.edu"
  },
  {
    "id": 676,
    "name": "Shriya Srinivasan",
    "category": "Internships",
    "field": "STEM",
    "email": "shriya_srinivasan@fas.harvard.edu"
  },
  {
    "id": 677,
    "name": "Yilun Du",
    "category": "Internships",
    "field": "STEM",
    "email": "ydu@seas.harvard.edu"
  },
  {
    "id": 678,
    "name": "Stratos Idreos",
    "category": "Internships",
    "field": "STEM",
    "email": "stratos@seas.harvard.edu"
  },
  {
    "id": 679,
    "name": "Seth Neel",
    "category": "Internships",
    "field": "STEM",
    "email": "sneel@hbs.edu"
  },
  {
    "id": 680,
    "name": "Kevin Kit Parker",
    "category": "Internships",
    "field": "STEM",
    "email": "kkparker@seas.harvard.edu"
  },
  {
    "id": 681,
    "name": "Martin Bechthold",
    "category": "Internships",
    "field": "STEM",
    "email": "mbechthold@gsd.harvard.edu"
  },
  {
    "id": 682,
    "name": "clarke",
    "category": "Internships",
    "field": "STEM",
    "email": "clarke@seas.harvard.edu"
  },
  {
    "id": 683,
    "name": "howe",
    "category": "Internships",
    "field": "STEM",
    "email": "howe@seas.harvard.edu"
  },
  {
    "id": 684,
    "name": "mas",
    "category": "Internships",
    "field": "STEM",
    "email": "mas@seas.harvard.edu"
  },
  {
    "id": 685,
    "name": "Alexandr Andoni",
    "category": "Internships",
    "field": "STEM",
    "email": "andoni@cs.columbia.edu"
  },
  {
    "id": 686,
    "name": "Elias Bareinboim",
    "category": "Internships",
    "field": "STEM",
    "email": "eb@cs.columbia.edu"
  },
  {
    "id": 687,
    "name": "David Blei",
    "category": "Internships",
    "field": "STEM",
    "email": "david.blei@columbia.edu"
  },
  {
    "id": 688,
    "name": "Adam Cannon",
    "category": "Internships",
    "field": "STEM",
    "email": "cannon@cs.columbia.edu"
  },
  {
    "id": 689,
    "name": "Shih-Fu Chang",
    "category": "Internships",
    "field": "STEM",
    "email": "sfchang@ee.columbia.edu"
  },
  {
    "id": 690,
    "name": "Tony Dear",
    "category": "Internships",
    "field": "STEM",
    "email": "tony.dear@columbia.edu"
  },
  {
    "id": 691,
    "name": "Daniel Hsu",
    "category": "Internships",
    "field": "STEM",
    "email": "djhsu@cs.columbia.edu"
  },
  {
    "id": 692,
    "name": "David Knowles",
    "category": "Internships",
    "field": "STEM",
    "email": "dak2173@columbia.edu"
  },
  {
    "id": 693,
    "name": "Yunzhu Li",
    "category": "Internships",
    "field": "STEM",
    "email": "yunzhu.li@columbia.edu"
  },
  {
    "id": 694,
    "name": "Toniann Pitassi",
    "category": "Internships",
    "field": "STEM",
    "email": "tp2684@columbia.edu"
  },
  {
    "id": 695,
    "name": "Ansaf Salleb-Aouissi",
    "category": "Internships",
    "field": "STEM",
    "email": "as2933@columbia.edu"
  },
  {
    "id": 696,
    "name": "Nakul Verma",
    "category": "Internships",
    "field": "STEM",
    "email": "verma@cs.columbia.edu"
  },
  {
    "id": 697,
    "name": "Carl Vondrick",
    "category": "Internships",
    "field": "STEM",
    "email": "cv2428@columbia.edu"
  },
  {
    "id": 698,
    "name": "Richard Zemel",
    "category": "Internships",
    "field": "STEM",
    "email": "zemel@cs.columbia.edu"
  },
  {
    "id": 699,
    "name": "aa5194",
    "category": "Internships",
    "field": "STEM",
    "email": "aa5194@columbia.edu"
  },
  {
    "id": 700,
    "name": "Mohammed AlQuraishi",
    "category": "Internships",
    "field": "STEM",
    "email": "m.alquraishi@columbia.edu"
  },
  {
    "id": 701,
    "name": "Elham Azizi",
    "category": "Internships",
    "field": "STEM",
    "email": "elham@azizilab.com"
  },
  {
    "id": 702,
    "name": "rac2239",
    "category": "Internships",
    "field": "STEM",
    "email": "rac2239@columbia.edu"
  },
  {
    "id": 703,
    "name": "Noémie Elhadad",
    "category": "Internships",
    "field": "STEM",
    "email": "noemie.elhadad@columbia.edu"
  },
  {
    "id": 704,
    "name": "Micah Goldblum",
    "category": "Internships",
    "field": "STEM",
    "email": "micah.g@columbia.edu"
  },
  {
    "id": 705,
    "name": "Shalmali Joshi",
    "category": "Internships",
    "field": "STEM",
    "email": "shalmali.joshi@columbia.edu"
  },
  {
    "id": 706,
    "name": "Lily Xu",
    "category": "Internships",
    "field": "STEM",
    "email": "lily.x@columbia.edu"
  },
  {
    "id": 707,
    "name": "Daniel Bauer",
    "category": "Internships",
    "field": "STEM",
    "email": "bauer@cs.columbia.edu"
  },
  {
    "id": 708,
    "name": "Peter Belhumeur",
    "category": "Internships",
    "field": "STEM",
    "email": "belhumeur@cs.columbia.edu"
  },
  {
    "id": 709,
    "name": "Julia Hirschberg",
    "category": "Internships",
    "field": "STEM",
    "email": "julia@cs.columbia.edu"
  },
  {
    "id": 710,
    "name": "Kathleen McKeown",
    "category": "Internships",
    "field": "STEM",
    "email": "kathy@cs.columbia.edu"
  },
  {
    "id": 711,
    "name": "Shree Nayar",
    "category": "Internships",
    "field": "STEM",
    "email": "nayar@cs.columbia.edu"
  },
  {
    "id": 712,
    "name": "Brian A. Smith",
    "category": "Internships",
    "field": "STEM",
    "email": "brian@cs.columbia.edu"
  },
  {
    "id": 713,
    "name": "Zhou Yu",
    "category": "Internships",
    "field": "STEM",
    "email": "zy2461@cs.columbia.edu"
  },
  {
    "id": 714,
    "name": "kat2193",
    "category": "Internships",
    "field": "STEM",
    "email": "kat2193@cumc.columbia.edu"
  },
  {
    "id": 715,
    "name": "ct3219",
    "category": "Internships",
    "field": "STEM",
    "email": "ct3219@columbia.edu"
  },
  {
    "id": 716,
    "name": "xx2489",
    "category": "Internships",
    "field": "STEM",
    "email": "xx2489@columbia.edu"
  },
  {
    "id": 717,
    "name": "Changxi Zheng",
    "category": "Internships",
    "field": "STEM",
    "email": "cxz@cs.columbia.edu"
  },
  {
    "id": 718,
    "name": "Xia Zhou",
    "category": "Internships",
    "field": "STEM",
    "email": "xia@cs.columbia.edu"
  },
  {
    "id": 719,
    "name": "Matei Ciocarlie",
    "category": "Internships",
    "field": "STEM",
    "email": "matei.ciocarlie@columbia.edu"
  }
]
];

const toolkitItems = [
  {
    id: 1,
    name: "FIRST Robotics Competition (FRC)",
    category: "Competitions",
    description: "International robotics competition where high school teams design, build, and program industrial-size robots.",
    url: "https://www.firstinspires.org/robotics/frc"
  },
  {
    id: 2,
    name: "FIRST Tech Challenge (FTC)",
    category: "Competitions",
    description: "Mid-sized robotics competition focused on design, coding, and engineering teamwork.",
    url: "https://www.firstinspires.org/robotics/ftc"
  },
  {
    id: 3,
    name: "VEX Robotics Competition",
    category: "Competitions",
    description: "Global robotics competition using VEX kits to solve engineering challenges.",
    url: "https://www.vexrobotics.com/"
  },
  {
    id: 4,
    name: "Technology Student Association (TSA)",
    category: "Clubs",
    description: "National STEM organization offering engineering and technology competitions.",
    url: "https://tsaweb.org/"
  },
  {
    id: 5,
    name: "Science Olympiad",
    category: "Competitions",
    description: "Team-based STEM competition across engineering, biology, chemistry, and physics events.",
    url: "https://www.soinc.org/"
  },
  {
    id: 6,
    name: "Mu Alpha Theta",
    category: "Clubs",
    description: "National high school mathematics honor society promoting excellence in math.",
    url: "https://mualphatheta.org/"
  },
  {
    id: 7,
    name: "Science National Honor Society (SNHS)",
    category: "Clubs",
    description: "Honor society recognizing high achievement in science and STEM leadership.",
    url: "https://www.sciencenhs.org/"
  },
  {
    id: 8,
    name: "Computer Science Honor Society (CSHS)",
    category: "Clubs",
    description: "National organization recognizing achievement in high school computer science.",
    url: "https://www.csteachers.org/page/cshs"
  },
  {
    id: 9,
    name: "CyberPatriot",
    category: "Competitions",
    description: "National youth cybersecurity competition focused on system defense skills.",
    url: "https://www.uscyberpatriot.org/"
  },
  {
    id: 10,
    name: "American Rocketry Challenge (TARC)",
    category: "Competitions",
    description: "National aerospace engineering competition involving rocket design and flight.",
    url: "https://rocketcontest.org/"
  },
  {
    id: 11,
    name: "SeaPerch",
    category: "Competitions",
    description: "Underwater robotics competition focused on engineering and marine technology.",
    url: "https://seaperch.org/"
  },
  {
    id: 12,
    name: "ExploraVision",
    category: "Competitions",
    description: "STEM competition encouraging innovative science and technology research proposals.",
    url: "https://www.exploravision.org/"
  },
  {
    id: 13,
    name: "Distributive Education Clubs of America (DECA)",
    category: "Clubs",
    description: "International business and marketing organization with competitive events and leadership conferences.",
    url: "https://www.deca.org/"
  },
  {
    id: 14,
    name: "FBLA (Future Business Leaders of America)",
    category: "Clubs",
    description: "National business leadership organization with academic competitions and conferences.",
    url: "https://www.fbla-pbl.org/"
  },
  {
    id: 15,
    name: "Business Professionals of America (BPA)",
    category: "Clubs",
    description: "Career and technical student organization focused on business and IT skills.",
    url: "https://bpa.org/"
  },
  {
    id: 16,
    name: "Junior Achievement (JA)",
    category: "Programs",
    description: "Global nonprofit teaching entrepreneurship, financial literacy, and work readiness.",
    url: "https://jausa.ja.org/"
  },
  {
    id: 17,
    name: "Future Farmers of America (FFA)",
    category: "Clubs",
    description: "National agriculture leadership organization with competitions and career pathways.",
    url: "https://www.ffa.org/"
  },
  {
    id: 18,
    name: "SkillsUSA",
    category: "Clubs",
    description: "Career and technical student organization focused on trade, technical, and leadership skills.",
    url: "https://www.skillsusa.org/"
  },
  {
    id: 19,
    name: "Family, Career & Community Leaders of America (FCCLA)",
    category: "Clubs",
    description: "Family, Career & Community Leaders of America; leadership organization with national competitions.",
    url: "https://fcclainc.org/"
  },
  {
    id: 20,
    name: "HOSA (Future Health Professionals)",
    category: "Clubs",
    description: "International health science organization with medical competitions and leadership training.",
    url: "https://hosa.org/"
  },
  {
    id: 21,
    name: "National Beta Club",
    category: "Clubs",
    description: "Academic honor organization promoting leadership, service, and achievement.",
    url: "https://www.betaclub.org/"
  },
  {
    id: 22,
    name: "National Speech & Debate Association (NSDA)",
    category: "Competitions",
    description: "National organization hosting speech and debate tournaments nationwide.",
    url: "https://www.speechanddebate.org/"
  },
  {
    id: 23,
    name: "Model United Nations (MUN)",
    category: "Clubs",
    description: "Global simulation of the United Nations promoting diplomacy and international policy debate.",
    url: "https://www.unausa.org/model-un"
  },
  {
    id: 24,
    name: "YMCA Youth & Government",
    category: "Programs",
    description: "Civic engagement program simulating legislative and judicial government processes.",
    url: "https://www.ymca.org/what-we-do/youth-development/leadership-development/youth-and-government"
  },
  {
    id: 25,
    name: "We the People",
    category: "Competitions",
    description: "National civics competition focused on constitutional knowledge and debate.",
    url: "https://www.civiced.org/we-the-people"
  },
  {
    id: 26,
    name: "Close Up Foundation",
    category: "Programs",
    description: "Civic education organization providing government immersion experiences.",
    url: "https://www.closeup.org/"
  },
  {
    id: 27,
    name: "Regeneron Science Talent Search",
    category: "Competitions",
    description: "Prestigious national science research competition for high school seniors.",
    url: "https://www.societyforscience.org/regeneron-sts/"
  },
  {
    id: 28,
    name: "International Science and Engineering Fair (ISEF)",
    category: "Competitions",
    description: "World’s largest international pre-college science research competition.",
    url: "https://www.societyforscience.org/isef/"
  },
  {
    id: 29,
    name: "National History Day",
    category: "Competitions",
    description: "Research-based history competition culminating in national finals.",
    url: "https://nhd.org/"
  },
  {
    id: 30,
    name: "Academic Decathlon",
    category: "Competitions",
    description: "Team academic competition across ten subject areas including speech and interview.",
    url: "https://usad.org/"
  },
  {
    id: 31,
    name: "National Academic Quiz Tournaments (NAQT)",
    category: "Competitions",
    description: "National quiz bowl competition organization for academic trivia teams.",
    url: "https://www.naqt.com/"
  },
  {
    id: 32,
    name: "National High School Ethics Bowl",
    category: "Competitions",
    description: "Team competition discussing real-world ethical dilemmas.",
    url: "https://nhseb.unc.edu/"
  },
  {
    id: 33,
    name: "National Art Honor Society (NAHS)",
    category: "Clubs",
    description: "Honor society recognizing excellence in visual arts.",
    url: "https://www.arteducators.org/community/nahs-njahs"
  },
  {
    id: 34,
    name: "Tri-M Music Honor Society",
    category: "Clubs",
    description: "National music honor society promoting music education and leadership.",
    url: "https://www.nafme.org/student-opportunities/tri-m-music-honor-society/"
  },
  {
    id: 35,
    name: "International Thespian Society (ITS)",
    category: "Clubs",
    description: "Global honor society for theater students.",
    url: "https://schooltheatre.org/itshome/"
  },
  {
    id: 36,
    name: "Scholastic Art & Writing Awards",
    category: "Competitions",
    description: "National creative competition recognizing student artists and writers.",
    url: "https://www.artandwriting.org/"
  },
  {
    id: 37,
    name: "Key Club International",
    category: "Clubs",
    description: "Largest international high school service organization.",
    url: "https://www.keyclub.org/"
  },
  {
    id: 38,
    name: "Interact Club (Rotary)",
    category: "Clubs",
    description: "Rotary-sponsored high school service organization.",
    url: "https://www.rotary.org/en/get-involved/interact-clubs"
  },
  {
    id: 39,
    name: "Leo Club (Lions Club Youth)",
    category: "Clubs",
    description: "Youth branch of Lions Club focused on service and leadership.",
    url: "https://www.lionsclubs.org/en/start-our-approach/leo-clubs"
  },
  {
    id: 40,
    name: "National Honor Society (NHS)",
    category: "Clubs",
    description: "Prestigious organization recognizing scholarship, leadership, service, and character.",
    url: "https://www.nhs.us/"
  },
  {
    id: 41,
    name: "Girl Up",
    category: "Clubs",
    description: "United Nations Foundation initiative promoting girls’ leadership and advocacy.",
    url: "https://girlup.org/"
  },
  {
    id: 42,
    name: "UNICEF High School Clubs",
    category: "Clubs",
    description: "Student-led chapters supporting global child advocacy and fundraising.",
    url: "https://www.unicefusa.org/get-involved/unicef-clubs"
  },
  {
    id: 43,
    name: "American Red Cross Clubs",
    category: "Clubs",
    description: "Youth chapters organizing humanitarian service projects.",
    url: "https://www.redcross.org/volunteer/become-a-volunteer/youth-high-school-college.html"
  },
  {
    id: 44,
    name: "Habitat for Humanity Youth Programs",
    category: "Clubs",
    description: "Student chapters supporting affordable housing initiatives.",
    url: "https://www.habitat.org/volunteer/near-you/youth-programs"
  },
  {
    id: 45,
    name: "Girls Who Code Clubs",
    category: "Clubs",
    description: "Global coding club network aimed at closing the gender gap in technology.",
    url: "https://girlswhocode.com/programs/clubs-program"
  },
  {
    id: 46,
    name: "Technovation Challenge",
    category: "Competitions",
    description: "Global tech entrepreneurship competition for young innovators.",
    url: "https://technovationchallenge.org/"
  },
  {
    id: 47,
    name: "Congressional App Challenge",
    category: "Competitions",
    description: "U.S. congressional coding competition encouraging app development.",
    url: "https://www.congressionalappchallenge.us/"
  },
  {
    id: 48,
    name: "Hack Club",
    category: "Clubs",
    description: "Global network of student-led coding clubs building real-world projects.",
    url: "https://hackclub.com/"
  },
  {
    id: 49,
    name: "Project Invent",
    category: "Programs",
    description: "Innovation program where students build tech solutions to real community problems.",
    url: "https://projectinvent.org/"
  },
  {
    id: 50,
    name: "USA Computing Olympiad (USACO)",
    category: "Competitions",
    description: "National programming competition leading to the International Olympiad in Informatics.",
    url: "http://www.usaco.org/"
  },
  {
    id: 51,
    name: "American Mathematics Competitions (AMC)",
    category: "Competitions",
    description: "National mathematics exam series feeding into the International Math Olympiad.",
    url: "https://maa.org/math-competitions"
  },
  {
    id: 52,
    name: "Odyssey of the Mind",
    category: "Competitions",
    description: "International creative problem-solving competition.",
    url: "https://www.odysseyofthemind.com/"
  },
  {
    id: 53,
    name: "World Robot Olympiad",
    category: "Competitions",
    description: "International robotics competition promoting STEM innovation.",
    url: "https://wro-association.org/"
  },
  {
    id: 54,
    name: "United Nations Children's Fund (UNICEF)",
    category: "Nonprofits",
    description: "United Nations agency providing humanitarian and developmental aid to children worldwide.",
    url: "https://www.unicef.org/"
  },
  {
    id: 55,
    name: "International Federation of Red Cross and Red Crescent Societies",
    category: "Nonprofits",
    description: "Global humanitarian network coordinating disaster response and relief efforts.",
    url: "https://www.ifrc.org/"
  },
  {
    id: 56,
    name: "Doctors Without Borders (Médecins Sans Frontières)",
    category: "Nonprofits",
    description: "International medical humanitarian organization providing emergency healthcare in crisis zones.",
    url: "https://www.msf.org/"
  },
  {
    id: 57,
    name: "CARE",
    category: "Nonprofits",
    description: "Global organization fighting poverty and advancing women’s empowerment.",
    url: "https://www.care.org/"
  },
  {
    id: 58,
    name: "Save the Children",
    category: "Nonprofits",
    description: "International nonprofit focused on child welfare, education, and emergency relief.",
    url: "https://www.savethechildren.org/"
  },
  {
    id: 59,
    name: "The Nature Conservancy",
    category: "Nonprofits",
    description: "Global environmental nonprofit focused on land, water, and biodiversity conservation.",
    url: "https://www.nature.org/"
  },
  {
    id: 60,
    name: "World Wildlife Fund (WWF)",
    category: "Nonprofits",
    description: "International organization dedicated to wildlife conservation and environmental sustainability.",
    url: "https://worldwildlife.org/"
  },
  {
    id: 61,
    name: "Greenpeace",
    category: "Nonprofits",
    description: "Global environmental advocacy organization campaigning for climate and ecological protection.",
    url: "https://www.greenpeace.org/"
  },
  {
    id: 62,
    name: "Sierra Club",
    category: "Nonprofits",
    description: "U.S.-based environmental organization promoting conservation and climate action.",
    url: "https://www.sierraclub.org/"
  },
  {
    id: 63,
    name: "Environmental Defense Fund (EDF)",
    category: "Nonprofits",
    description: "Science-based environmental advocacy organization focused on climate and sustainability solutions.",
    url: "https://www.edf.org/"
  },
  {
    id: 64,
    name: "St. Jude Children's Research Hospital",
    category: "Nonprofits",
    description: "Leading pediatric research hospital focused on treating and defeating childhood cancer.",
    url: "https://www.stjude.org/"
  },
  {
    id: 65,
    name: "American Cancer Society",
    category: "Nonprofits",
    description: "Nationwide nonprofit funding cancer research and patient support programs.",
    url: "https://www.cancer.org/"
  },
  {
    id: 66,
    name: "Susan G. Komen",
    category: "Nonprofits",
    description: "Global nonprofit dedicated to breast cancer research and awareness.",
    url: "https://www.komen.org/"
  },
  {
    id: 67,
    name: "American Heart Association",
    category: "Nonprofits",
    description: "Organization funding cardiovascular research and promoting heart health education.",
    url: "https://www.heart.org/"
  },
  {
    id: 68,
    name: "National Alliance on Mental Illness (NAMI)",
    category: "Nonprofits",
    description: "Largest U.S. grassroots mental health advocacy organization.",
    url: "https://www.nami.org/"
  },
  {
    id: 69,
    name: "Boys & Girls Clubs of America",
    category: "Nonprofits",
    description: "Youth development organization providing mentorship and enrichment programs.",
    url: "https://www.bgca.org/"
  },
  {
    id: 70,
    name: "Big Brothers Big Sisters of America",
    category: "Nonprofits",
    description: "National mentoring nonprofit creating one-to-one youth mentorship programs.",
    url: "https://www.bbbs.org/"
  },
  {
    id: 71,
    name: "Teach For America",
    category: "Nonprofits",
    description: "Education nonprofit working to expand academic opportunity in underserved communities.",
    url: "https://www.teachforamerica.org/"
  },
  {
    id: 72,
    name: "Khan Academy",
    category: "Nonprofits",
    description: "Global nonprofit providing free online educational resources and courses.",
    url: "https://www.khanacademy.org/"
  },
  {
    id: 73,
    name: "826 National",
    category: "Nonprofits",
    description: "Network of writing centers providing free tutoring and publishing opportunities for students.",
    url: "https://826national.org/"
  },
  {
    id: 74,
    name: "Amnesty International",
    category: "Nonprofits",
    description: "Global human rights organization advocating against injustice and inequality.",
    url: "https://www.amnesty.org/"
  },
  {
    id: 75,
    name: "Human Rights Watch",
    category: "Nonprofits",
    description: "International nonprofit investigating and reporting on human rights abuses.",
    url: "https://www.hrw.org/"
  },
  {
    id: 76,
    name: "American Civil Liberties Union (ACLU)",
    category: "Nonprofits",
    description: "Nonprofit organization defending civil liberties and constitutional rights.",
    url: "https://www.aclu.org/"
  },
  {
    id: 77,
    name: "NAACP",
    category: "Nonprofits",
    description: "Civil rights organization advocating for racial equality and justice.",
    url: "https://naacp.org/"
  },
  {
    id: 78,
    name: "Anti-Defamation League (ADL)",
    category: "Nonprofits",
    description: "Civil rights organization combating antisemitism and hate-based discrimination.",
    url: "https://www.adl.org/"
  },
  {
    id: 79,
    name: "Habitat for Humanity International",
    category: "Nonprofits",
    description: "Global nonprofit building affordable housing for families in need.",
    url: "https://www.habitat.org/"
  },
  {
    id: 80,
    name: "Feeding America",
    category: "Nonprofits",
    description: "Largest hunger-relief organization in the United States.",
    url: "https://www.feedingamerica.org/"
  },
  {
    id: 81,
    name: "World Food Programme (WFP)",
    category: "Nonprofits",
    description: "United Nations agency providing food assistance worldwide.",
    url: "https://www.wfp.org/"
  },
  {
    id: 82,
    name: "Oxfam",
    category: "Nonprofits",
    description: "International nonprofit focused on poverty alleviation and global inequality.",
    url: "https://www.oxfam.org/"
  },
  {
    id: 83,
    name: "Code.org",
    category: "Nonprofits",
    description: "Nonprofit expanding access to computer science education globally.",
    url: "https://code.org/"
  },
  {
    id: 84,
    name: "Black Girls Code",
    category: "Nonprofits",
    description: "Organization empowering girls of color in technology and computer science.",
    url: "https://blackgirlscode.org/"
  },
  {
    id: 85,
    name: "AI4ALL",
    category: "Nonprofits",
    description: "Nonprofit increasing diversity and access in artificial intelligence education.",
    url: "https://ai-4-all.org/"
  },
  {
    id: 86,
    name: "GlobalGiving",
    category: "Nonprofits",
    description: "Global crowdfunding platform connecting donors to grassroots nonprofit projects.",
    url: "https://www.globalgiving.org/"
  },
  {
    id: 87,
    name: "BRAC",
    category: "Nonprofits",
    description: "One of the world’s largest international development organizations.",
    url: "https://www.brac.net/"
  },
  {
    id: 88,
    name: "Heifer International",
    category: "Nonprofits",
    description: "Nonprofit supporting sustainable agriculture and poverty reduction worldwide.",
    url: "https://www.heifer.org/"
  },
  {
    id: 89,
    name: "MITES (MIT Introduction to Technology, Engineering, and Science)",
    category: "Summer Programs",
    description: "MIT’s selective STEM enrichment program for high school students (formerly OEOP/MITES).",
    url: "https://mites.mit.edu/"
  },
  {
    id: 90,
    name: "MITES Semester",
    category: "Summer Programs",
    description: "MIT’s longer-format MITES program variant providing extended STEM learning and support.",
    url: "https://mites.mit.edu/discover-mites/mites-semester/"
  },
  {
    id: 91,
    name: "Simons Summer Research Program",
    category: "Summer Programs",
    description: "Highly selective summer research program placing students in real research environments.",
    url: "https://www.stonybrook.edu/simons/"
  },
  {
    id: 92,
    name: "NASA App Development Challenge",
    category: "Competitions",
    description: "NASA challenge where students build apps/tools to support real NASA mission needs.",
    url: "https://www.nasa.gov/learning-resources/app-development-challenge/"
  },
  {
    id: 93,
    name: "MIT THINK Scholars Program",
    category: "Competitions",
    description: "MIT-sponsored program supporting student research/innovation proposals with mentorship and resources.",
    url: "https://think.mit.edu/"
  },
  {
    id: 94,
    name: "Conrad Challenge",
    category: "Competitions",
    description: "Global innovation and entrepreneurship challenge for student-led teams building impactful solutions.",
    url: "https://www.conradchallenge.org/"
  },
  {
    id: 95,
    name: "Wharton Global Youth Program",
    category: "Summer Programs",
    description: "Business/leadership programs from Wharton for high school students (online and on-campus options).",
    url: "https://globalyouth.wharton.upenn.edu/"
  },
  {
    id: 96,
    name: "Yale Young Global Scholars (YYGS)",
    category: "Summer Programs",
    description: "Selective Yale summer academic program for high school students across multiple tracks.",
    url: "https://globalscholars.yale.edu/"
  },
  {
    id: 97,
    name: "NASA High School Internship Program",
    category: "Internships",
    description: "NASA internship opportunities for high school students (availability varies by year/location).",
    url: "https://intern.nasa.gov/"
  },
  {
    id: 98,
    name: "Bank of America Student Leaders Program",
    category: "Internships",
    description: "Competitive paid summer leadership program including nonprofit work and leadership development.",
    url: "https://about.bankofamerica.com/en-us/what-guides-us/student-leaders"
  },
  {
    id: 99,
    name: "NIST SHIP (Summer High School Internship Program)",
    category: "Internships",
    description: "NIST’s selective STEM internship program for high school students at NIST labs.",
    url: "https://www.nist.gov/iaao/academic-affairs-office/nist-summer-high-school-internship-program-ship"
  },
  {
    id: 100,
    name: "Google Code Next",
    category: "Summer Programs",
    description: "Google program supporting students with coding skills, mentorship, and CS enrichment opportunities.",
    url: "https://codenext.withgoogle.com/"
  },
  {
    id: 101,
    name: "TechGirls",
    category: "Summer Programs",
    description: "International STEM exchange program empowering girls through workshops and leadership experiences.",
    url: "https://www.techgirlsglobal.org/"
  },
  {
    id: 102,
    name: "Disney Dreamers Academy",
    category: "Summer Programs",
    description: "Disney program focused on inspiring student leadership and career exploration experiences.",
    url: "https://www.disneydreamersacademy.com/"
  },
  {
    id: 103,
    name: "CubeSat / NASA CubeSat Opportunities (General)",
    category: "Programs",
    description: "Student opportunities related to CubeSat design/launch pathways (varies by program and eligibility).",
    url: "https://www.nasa.gov/cube-sat-launch-initiative/"
  },
  {
    id: 104,
    name: "Joy of Coding (University of Michigan)",
    category: "Summer Programs",
    description: "UMich computing program(s) for pre-college students focused on programming and CS exploration.",
    url: "https://joyofcoding.org/"
  },
  {
    id: 105,
    name: "SWENext",
    category: "Clubs",
    description: "Society of Women Engineers’ pre-college program with resources, community, and pathways in engineering.",
    url: "https://swe.org/swenext/"
  },
  {
    id: 106,
    name: "SWENext Awards",
    category: "Awards",
    description: "Recognition opportunities associated with SWENext/SWE programs and student achievements.",
    url: "https://swe.org/awards/swenext-awards/"
  },
  {
    id: 107,
    name: "Xerox Award for Innovation and Information Technology",
    category: "Awards",
    description: "Recognition award typically associated with innovation and technology achievement (often school-nominated).",
    url: "https://www.rochester.edu/admissions/high-school-awards/xerox-award/"
  },
  {
    id: 108,
    name: "Congressional Award (STEM-related recognition)",
    category: "Awards",
    description: "U.S. Congressional Award recognizing youth achievement across service, skills, and personal development.",
    url: "https://www.congressionalaward.org/"
  },
  {
    id: 109,
    name: "United States Naval Academy Summer Seminar / STEM Programs",
    category: "Summer Programs",
    description: "USNA summer experiences introducing students to academy life and STEM opportunities (varies by program).",
    url: "https://www.usna.edu/Admissions/Programs/index.php"
  },
  {
    id: 110,
    name: "Society of Women Engineers (SWE)",
    category: "Nonprofits",
    description: "Major engineering nonprofit supporting women in engineering through programs, mentorship, and advocacy.",
    url: "https://swe.org/"
  },
  {
    id: 111,
    name: "UIL Computer Science (Texas UIL)",
    category: "Competitions",
    description: "University Interscholastic League academic competitions in Texas, including computer science events.",
    url: "https://www.uiltexas.org/academics/computer-science"
  },
  {
    id: 112,
    name: "Born This Way Foundation",
    category: "Nonprofits",
    description: "Youth mental health and kindness-focused nonprofit founded to support and empower young people.",
    url: "https://bornthisway.foundation/"
  },
  {
    id: 113,
    name: "WomenTech Network",
    category: "Nonprofits",
    description: "Global network supporting women in tech through community, events, and career opportunities.",
    url: "https://www.womentech.net/"
  },
  {
    id: 114,
    name: "Capital One x NCWIT Programs",
    category: "Programs",
    description: "NCWIT-related opportunities and initiatives supported by Capital One (programs vary).",
    url: "https://www.ncwit.org/"
  },
  {
    id: 115,
    name: "Girls With Impact",
    category: "Summer Programs",
    description: "Business and entrepreneurship programs for girls, including summer intensives and pitch experiences.",
    url: "https://www.girlswithimpact.org/"
  },
  {
    id: 116,
    name: "Broadcom MASTERS",
    category: "Competitions",
    description: "Prestigious middle school STEM competition (note: primarily for grades 6–8).",
    url: "https://www.societyforscience.org/broadcom-masters/"
  },
  {
    id: 117,
    name: "STEM Next Opportunity Fund",
    category: "Nonprofits",
    description: "Nonprofit supporting STEM learning ecosystems and expanded access to STEM opportunities.",
    url: "https://stemnext.org/"
  },
  {
    id: 118,
    name: "MIT Lincoln Laboratory (Programs/Outreach)",
    category: "Programs",
    description: "MIT Lincoln Laboratory outreach and STEM opportunities (specific programs vary by year).",
    url: "https://www.ll.mit.edu/outreach"
  },
  {
    id: 119,
    name: "Summer Science Program (SSP International)",
    category: "Summer Programs",
    description: "Highly selective research-based summer program in areas like astronomy, biochemistry, and more.",
    url: "https://summerscience.org/"
  },
  {
    id: 120,
    name: "LOUDwomen",
    category: "Nonprofits",
    description: "Community/programs supporting women and girls through empowerment and leadership (verify local chapters/opportunities).",
    url: "https://www.loudwomen.org/"
  },
  {
    id: 121,
    name: "Research Made Simple Internship",
    category: "Internships",
    description: "Student research support/internship-style program helping students learn research skills (details vary).",
    url: "https://www.researchmadesimple.org/"
  },
  {
    id: 122,
    name: "Scholastic Art & Writing Awards — Gold Key",
    category: "Awards",
    description: "Top regional award level recognizing outstanding student writing or art submissions.",
    url: "https://www.artandwriting.org/awards/"
  },
  {
    id: 123,
    name: "Broad Institute of MIT and Harvard (Programs)",
    category: "Programs",
    description: "Biomedical research institute; some student programs/opportunities may be available depending on year.",
    url: "https://www.broadinstitute.org/partnerships/education"
  },
  {
    id: 124,
    name: "TikTok (Programs/Initiatives)",
    category: "Programs",
    description: "Company initiatives may include creator, safety, or educational programs (varies; not always a student award).",
    url: "https://www.tiktok.com/safety/en/youth-portal/"
  },
  {
    id: 125,
    name: "National Merit Scholarship Program",
    category: "Awards",
    description: "Highly recognized scholarship program based on PSAT/NMSQT performance.",
    url: "https://www.nationalmerit.org/"
  },
  {
    id: 126,
    name: "NVIDIA (Student/Research Opportunities)",
    category: "Programs",
    description: "Company programs and opportunities related to AI/graphics/compute (availability varies by year).",
    url: "https://www.nvidia.com/en-us/about-nvidia/careers/students-and-grads/"
  },
  {
    id: 127,
    name: "Tuition Solution Scholarship for STEM Students",
    category: "Scholarships",
    description: "Scholarship opportunity for STEM students (details and eligibility vary by sponsor).",
    url: "https://www.tuitionsolution.org/"
  },
  {
    id: 128,
    name: "Hindu Expressions Contest",
    category: "Competitions",
    description: "Student contest focused on Hindu culture/heritage and expression (sponsor/eligibility varies).",
    url: "https://www.hinduamerican.org/"
  },
  {
    id: 129,
    name: "NSLI-Y (National Security Language Initiative for Youth)",
    category: "Summer Programs",
    description: "Highly selective U.S. program for language immersion in critical languages (summer and academic-year options).",
    url: "https://www.nsliforyouth.org/"
  },
  {
    id: 130,
    name: "Carson Scholars Fund Scholarship",
    category: "Scholarships",
    description: "Scholarship recognizing academic excellence and humanitarian qualities in students.",
    url: "https://carsonscholars.org/"
  },
  {
    id: 131,
    name: "Congressional Essay Contest (General)",
    category: "Competitions",
    description: "Essay contests tied to civic engagement or congressional initiatives (varies by district/program).",
    url: "https://www.wetheteachers.org/"
  },
  {
    id: 132,
    name: "Coolidge Scholars (Coolidge Scholarship)",
    category: "Scholarships",
    description: "Highly competitive full-ride scholarship for academic excellence and leadership.",
    url: "https://coolidgescholars.org/"
  },
  {
    id: 134,
    name: "Odenza Marketing Group Scholarship",
    category: "Scholarships",
    description: "Scholarship opportunity offered through Odenza (check eligibility and deadlines).",
    url: "https://odenzascholarships.com/"
  },
  {
    id: 135,
    name: "Varsity Tutors College Scholarship",
    category: "Scholarships",
    description: "Scholarship program with application prompts (eligibility varies).",
    url: "https://www.varsitytutors.com/college-scholarship"
  },
  {
    id: 136,
    name: "BWSI (Beaver Works Summer Institute)",
    category: "Summer Programs",
    description: "MIT-affiliated summer program offering hands-on STEM courses (admissions competitive).",
    url: "https://beaverworks.ll.mit.edu/"
  },
  {
    id: 137,
    name: "GSTEM (General STEM Program)",
    category: "Programs",
    description: "STEM enrichment program(s) under the name GSTEM (verify the exact organization/program you mean).",
    url: "https://cas.nyu.edu/gstem.html"
  },
  {
    id: 138,
    name: "Amazon Future Engineer",
    category: "Programs",
    description: "Amazon program supporting CS education, scholarships, and career pathways for students.",
    url: "https://www.amazonfutureengineer.com/"
  },
  {
    id: 139,
    name: "Women at Microsoft Scholarship",
    category: "Scholarships",
    description: "Microsoft scholarship supporting women and non-binary students pursuing tech-related degrees.",
    url: "https://www.microsoft.com/en-us/diversity/inside-microsoft/scholarships/womens-scholarship"
  },
  {
    id: 140,
    name: "ARISE (NYU STEM)",
    category: "Summer Programs",
    description: "NYU-affiliated STEM/research program for high school students (competitive; details vary by year).",
    url: "https://engineering.nyu.edu/k12/arise"
  },
  {
    id: 141,
    name: "Americanism Essay Contest (Grades 7–12)",
    category: "Competitions",
    description: "National essay contest focused on Americanism/civic themes (often sponsored by service orgs).",
    url: "https://www.fra.org/essay"
  },
  {
    id: 142,
    name: "Technology Addiction Awareness Scholarship",
    category: "Scholarships",
    description: "Scholarship promoting awareness of technology addiction and responsible tech use.",
    url: "https://www.digitalresponsibility.org/technology-addiction-awareness-scholarship"
  },
  {
    id: 143,
    name: "ACF Visionary Scholarship",
    category: "Scholarships",
    description: "Scholarship opportunity for students (sponsor/eligibility varies; verify current details).",
    url: "https://american學習foundation.org/scholarship/"
  },
  {
    id: 144,
    name: "Swift Student Challenge",
    category: "Competitions",
    description: "Apple competition recognizing student coding projects built with Swift Playgrounds.",
    url: "https://developer.apple.com/swift-student-challenge/"
  },
  {
    id: 145,
    name: "American Fire Sprinkler Association Scholarship",
    category: "Scholarships",
    description: "Scholarship opportunity offered by the American Fire Sprinkler Association (application requirements vary).",
    url: "https://www.afsascholarship.org/"
  },
  {
    id: 146,
    name: "Microsoft Discovery Program",
    category: "Summer Programs",
    description: "Microsoft program(s) introducing students to tech careers and skills (availability varies by location/year).",
    url: "https://www.microsoft.com/en-us/diversity/inside-microsoft/scholarships/default.aspx"
  },
  {
    id: 147,
    name: "APIA Scholars",
    category: "Scholarships",
    description: "Scholarship program supporting AANHPI students and others meeting eligibility criteria.",
    url: "https://apiascholars.org/"
  },
  {
    id: 148,
    name: "Breakthrough Junior Challenge",
    category: "Competitions",
    description: "Global science video competition with major scholarship prizes for winners.",
    url: "https://breakthroughjuniorchallenge.org/"
  },
  {
    id: 149,
    name: "RSI (Research Science Institute)",
    category: "Summer Programs",
    description: "Extremely selective research program hosted by MIT for high-achieving high school students.",
    url: "https://www.cee.org/programs/research-science-institute"
  },
  {
    id: 150,
    name: "Columbia University BRAINYAC",
    category: "Internships",
    description: "Competitive summer research apprenticeship placing NYC high school students in Columbia neuroscience labs.",
    url: "https://zuckermaninstitute.columbia.edu/brainyac"
  },
  {
    id: 151,
    name: "Cleveland Clinic CYCE Advanced Technology Summer Internship Program",
    category: "Internships",
    description: "Paid summer internship focused on AI, robotics, cybersecurity, and computing within a healthcare setting.",
    url: "https://my.clevelandclinic.org/departments/civic-education/programs/high-school-internships"
  },
  {
    id: 152,
    name: "Cleveland Clinic CYCE Science Summer Internship Program",
    category: "Internships",
    description: "Paid healthcare-focused internship offering exposure to clinical and public health career pathways.",
    url: "https://my.clevelandclinic.org/departments/civic-education/programs/high-school-internships"
  },
  {
    id: 153,
    name: "Cleveland Clinic CYCE Louis Stokes Summer Internship Program",
    category: "Internships",
    description: "Paid internship exploring business, finance, design, and administrative roles in healthcare systems.",
    url: "https://my.clevelandclinic.org/departments/civic-education/programs/high-school-internships"
  },
  {
    id: 154,
    name: "Rockefeller University Summer Science Outreach Program (SSRP)",
    category: "Summer Programs",
    description: "Selective summer research program providing hands-on lab experience with biomedical mentors.",
    url: "https://www.rockefeller.edu/outreach/ssrp/"
  },
  {
    id: 155,
    name: "Rockefeller University Summer Neuroscience Program",
    category: "Summer Programs",
    description: "Two-week summer program introducing students to brain science and neuroscience concepts.",
    url: "https://www.rockefeller.edu/outreach/snp/"
  },
  {
    id: 156,
    name: "Hofstra University Summer Science Research Program (HUSSRP)",
    category: "Summer Programs",
    description: "Faculty-mentored summer research program culminating in a scientific poster presentation.",
    url: "https://www.hofstra.edu/science-research-program/"
  },
  {
    id: 157,
    name: "Memorial Sloan Kettering Cancer Center Summer Student Program",
    category: "Internships",
    description: "Highly competitive biomedical lab internship offering mentored research experience.",
    url: "https://www.mskcc.org/education-training/high-school-college/summer-student"
  },
  {
    id: 158,
    name: "Georgetown University Biotechnology for Science & Health Academy",
    category: "Summer Programs",
    description: "Hands-on biotechnology academy exploring medical, forensic, and scientific lab applications.",
    url: "https://summer.georgetown.edu/programs/XSTP/biotechnology-for-science-health-academy"
  },
  {
    id: 159,
    name: "Johns Hopkins Online Pre-College: Anatomy, Physiology, & Disease",
    category: "Summer Programs",
    description: "Online pre-college course covering major human body systems and disease processes.",
    url: "https://summerprograms.jhu.edu/"
  },
  {
    id: 160,
    name: "Johns Hopkins Online Pre-College: Applications of Chemistry in Medicine",
    category: "Summer Programs",
    description: "Online course exploring how chemistry supports medicine, pharmaceuticals, and healthcare innovation.",
    url: "https://summerprograms.jhu.edu/"
  },
  {
    id: 161,
    name: "Johns Hopkins Online Pre-College: Epidemics, Pandemics, and Outbreaks",
    category: "Summer Programs",
    description: "Public health-focused online course examining global disease spread and prevention strategies.",
    url: "https://summerprograms.jhu.edu/"
  },
  {
    id: 162,
    name: "Johns Hopkins Online Pre-College: Introduction to Surgery",
    category: "Summer Programs",
    description: "Online introduction to surgical fields, patient care, and medical technologies.",
    url: "https://summerprograms.jhu.edu/"
  },
  {
    id: 163,
    name: "Johns Hopkins Online Pre-College: Medical School Intensive",
    category: "Summer Programs",
    description: "Broad online introduction to core medical disciplines and biomedical science topics.",
    url: "https://summerprograms.jhu.edu/"
  },
  {
    id: 164,
    name: "Johns Hopkins Online Pre-College: Neurobiology (Cellular and Systems)",
    category: "Summer Programs",
    description: "Online neuroscience course covering brain function at cellular and systems levels.",
    url: "https://summerprograms.jhu.edu/"
  },
  {
    id: 165,
    name: "Johns Hopkins Online Pre-College: Population Genomics",
    category: "Summer Programs",
    description: "Online course introducing population genetics, evolution, and disease-related genomics.",
    url: "https://summerprograms.jhu.edu/"
  },
  {
    id: 166,
    name: "Johns Hopkins Pre-College: Anatomy, Physiology, & Disease — Guts, Hormones & Reproduction",
    category: "Summer Programs",
    description: "In-person physiology course focused on digestive, immune, endocrine, and reproductive systems.",
    url: "https://summerprograms.jhu.edu/"
  },
  {
    id: 167,
    name: "Johns Hopkins Pre-College: Introduction to Experimental Design in Biology",
    category: "Summer Programs",
    description: "Hands-on biology course teaching experimental design, lab methods, and hypothesis testing.",
    url: "https://summerprograms.jhu.edu/"
  },
  {
    id: 168,
    name: "Johns Hopkins Pre-College: Medical School Intensive (Maryland)",
    category: "Summer Programs",
    description: "In-person pre-college program offering immersive exposure to medical studies and practice.",
    url: "https://summerprograms.jhu.edu/"
  },
  {
    id: 169,
    name: "Johns Hopkins Pre-College: Social Inequality and the Public's Health",
    category: "Summer Programs",
    description: "Course exploring health disparities, epidemiology, and social determinants of health.",
    url: "https://summerprograms.jhu.edu/"
  },
  {
    id: 170,
    name: "The Anson L. Clark Scholars Program",
    category: "Summer Programs",
    description: "Selective research-intensive summer program pairing students with university faculty mentors.",
    url: "https://www.depts.ttu.edu/honors/academics/clarkscholars/"
  },
  {
    id: 171,
    name: "Stanford Institutes of Medicine Summer Research (SIMR) Program",
    category: "Internships",
    description: "Competitive 8-week biomedical research internship with one-on-one Stanford lab mentorship.",
    url: "https://simr.stanford.edu/"
  },
  {
    id: 172,
    name: "Horizon Academic Research Program",
    category: "Programs",
    description: "Online mentored research program guiding students through independent academic research projects.",
    url: "https://www.horizonacademic.org/"
  },
  {
    id: 173,
    name: "The Jackson Laboratory Summer Student Program (SSP)",
    category: "Summer Programs",
    description: "Selective summer research program focused on genetics and genomics laboratory experience.",
    url: "https://www.jax.org/education-and-learning/high-school-students-and-undergraduates/summer-student-program"
  },
  {
    id: 175,
    name: "YoungArts National Arts Competition (Writing)",
    category: "Awards",
    description: "Prestigious national arts award recognizing exceptional high school writers in poetry, fiction, nonfiction, and spoken word.",
    url: "https://youngarts.org/"
  },
  {
    id: 176,
    name: "Bennington Young Writers Awards",
    category: "Awards",
    description: "Respected literary competition for high school sophomores through seniors, honoring excellence in poetry, fiction, and nonfiction.",
    url: "https://www.bennington.edu/events/young-writers-awards"
  },
  {
    id: 177,
    name: "New York Times Student Essay Contests",
    category: "Awards",
    description: "National essay competitions run throughout the year by The New York Times, with winning entries published online.",
    url: "https://www.nytimes.com/learning/editorial-contests"
  },
  {
    id: 178,
    name: "The Adroit Journal High School Prizes",
    category: "Awards",
    description: "Competitive literary prizes for high school writers in poetry and prose, offering publication in a respected journal.",
    url: "https://theadroitjournal.org/about/prizes/adroit-prizes/"
  },
  {
    id: 179,
    name: "Narrative Magazine High School Writing Contest",
    category: "Awards",
    description: "National short story and essay competition judged by literary professionals, with cash prizes and publication opportunities.",
    url: "https://www.narrativemagazine.com/"
  },
  {
    id: 180,
    name: "Princeton University Poetry Prize",
    category: "Awards",
    description: "Selective poetry award for high school juniors recognizing outstanding original poetry portfolios.",
    url: "https://arts.princeton.edu/visual-arts/high-school-poetry-contest/"
  },
  {
    id: 181,
    name: "Rattle Young Poets Anthology",
    category: "Awards",
    description: "Annual anthology highlighting exceptional poetry written by students ages 15–18.",
    url: "https://www.rattle.com/children/"
  },
  {
    id: 182,
    name: "Journalism Education Association (JEA) Awards",
    category: "Awards",
    description: "National journalism awards recognizing excellence in student reporting, editing, design, and media production.",
    url: "https://jea.org/"
  },
  {
    id: 183,
    name: "Quill and Scroll Writing Awards",
    category: "Awards",
    description: "Student journalism contests and scholarships honoring excellence in high school media and writing.",
    url: "https://quillandscroll.org/"
  },
  {
    id: 184,
    name: "John Locke Institute Essay Competition",
    category: "Awards",
    description: "International academic essay competition in philosophy, politics, economics, history, and related disciplines.",
    url: "https://www.johnlockeinstitute.com/essay-competition"
  },
  {
    id: 185,
    name: "The Concord Review (History Publication)",
    category: "Awards",
    description: "Highly selective journal publishing outstanding high school research papers in history.",
    url: "https://www.tcr.org/"
  },
  {
    id: 186,
    name: "Poetry Out Loud National Recitation Contest",
    category: "Awards",
    description: "National poetry recitation competition where state champions advance to finals for major recognition.",
    url: "https://www.poetryoutloud.org/"
  },
  {
    id: 187,
    name: "Foyle Young Poets of the Year Award",
    category: "Awards",
    description: "International poetry competition recognizing exceptional young poets ages 11–17.",
    url: "https://poetrysociety.org.uk/competitions/foyle-young-poets-of-the-year-award/"
  },
  {
    id: 188,
    name: "Kenyon Review Young Writers Workshop",
    category: "Awards",
    description: "Highly competitive creative writing workshop whose selective admission serves as a strong literary credential.",
    url: "https://kenyonreview.org/workshops/young-writers/"
  },
  {
    id: 189,
    name: "Coca-Cola Scholars Program",
    category: "Scholarships",
    description: "Highly prestigious merit-based scholarship recognizing seniors for leadership, service, and community impact.",
    url: "https://www.coca-colascholarsfoundation.org/"
  },
  {
    id: 190,
    name: "Prudential Emerging Visionaries",
    category: "Scholarships",
    description: "National award honoring teens who create innovative solutions to financial or social challenges.",
    url: "https://www.prudential.com/links/about/emergingvisionaries"
  },
  {
    id: 192,
    name: "Davidson Fellows Scholarship",
    category: "Scholarships",
    description: "Prestigious scholarship rewarding students under 18 for significant original work in STEM or the liberal arts.",
    url: "https://www.davidsonfellows.org/"
  },
  {
    id: 193,
    name: "Echelon Scholars Program",
    category: "Scholarships",
    description: "Merit-based scholarship recognizing high-achieving students for academic excellence and leadership potential.",
    url: "https://echelonfoundation.org/"
  },
  {
    id: 195,
    name: "U.S. Presidential Scholars Program",
    category: "Awards",
    description: "One of the highest national honors recognizing outstanding academic achievement, arts talent, and leadership.",
    url: "https://www2.ed.gov/programs/psp/index.html"
  },
  {
    id: 196,
    name: "College Board National Recognition Programs",
    category: "Awards",
    description: "Academic honors recognizing high-achieving students from underrepresented communities.",
    url: "https://bigfuture.collegeboard.org/communities-awards/national-recognition-programs"
  },
  {
    id: 197,
    name: "AP Scholar Awards",
    category: "Awards",
    description: "Recognition awarded to students who earn high scores across multiple Advanced Placement exams.",
    url: "https://apcentral.collegeboard.org/exam-administration-ordering-scores/scores/awards-honors"
  },
  {
    id: 198,
    name: "The Congressional Award",
    category: "Awards",
    description: "National program encouraging initiative in service, leadership, fitness, and personal development through tiered medal recognition.",
    url: "https://www.congressionalaward.org/"
  },
  {
    id: 199,
    name: "Brower Youth Awards",
    category: "Awards",
    description: "Environmental leadership award honoring youth who lead impactful environmental campaigns or initiatives.",
    url: "http://www.broweryouthawards.org/"
  },
  {
    id: 200,
    name: "International Young Eco-Hero Award",
    category: "Awards",
    description: "Recognizes young environmental advocates for innovative projects that protect the planet.",
    url: "https://actionfornature.org/eco-hero-awards"
  },
  {
    id: 201,
    name: "Princeton Prize in Race Relations",
    category: "Awards",
    description: "Award recognizing high school students who promote racial equity and positive race relations in their communities.",
    url: "https://pprize.princeton.edu/"
  },
  {
    id: 202,
    name: "Profile in Courage Essay Contest",
    category: "Awards",
    description: "National essay contest challenging students to analyze political courage demonstrated by a U.S. elected official.",
    url: "https://www.jfklibrary.org/learn/education/profile-in-courage-essay-contest"
  },
  {
    id: 203,
    name: "Voice of Democracy Audio-Essay Contest",
    category: "Scholarships",
    description: "National patriotic audio-essay competition awarding scholarships for reflections on democratic values.",
    url: "https://www.vfw.org/community/youth-and-education/youth-scholarships"
  },
  {
    id: 205,
    name: "Fountainhead Essay Contest",
    category: "Awards",
    description: "Literary essay competition based on Ayn Rand’s novel *The Fountainhead*.",
    url: "https://aynrand.org/students/essay-contests/"
  },
  {
    id: 206,
    name: "Cirkled In 'No Sweat' Scholarship",
    category: "Scholarships",
    description: "Simple-entry scholarship awarded through a general application without an essay requirement.",
    url: "https://www.cirkledin.com/scholarship/"
  },
  {
    id: 207,
    name: "Niche $40,000 No Essay Scholarship",
    category: "Scholarships",
    description: "Randomly awarded scholarship open to seniors without requiring an essay submission.",
    url: "https://www.niche.com/colleges/scholarships/"
  },
  {
    id: 208,
    name: "'Be Bold' No-Essay Scholarship",
    category: "Scholarships",
    description: "Merit-style scholarship recognizing students who demonstrate confidence and initiative through a short application.",
    url: "https://bold.org/scholarships/the-be-bold-no-essay-scholarship/"
  },
  {
    id: 209,
    name: "MITES (MIT Introduction to Technology, Engineering, and Science)",
    category: "Summer Programs",
    description: "Highly selective MIT summer program offering rigorous STEM coursework and on-campus experience for rising seniors.",
    url: "https://mites.mit.edu/"
  },
  {
    id: 210,
    name: "Stanford AI4ALL",
    category: "Summer Programs",
    description: "Selective AI-focused summer program promoting diversity and leadership in artificial intelligence.",
    url: "https://ai-4-all.org/camp/stanford-university/"
  },
  {
    id: 212,
    name: "NIH Summer Internship Program (SIP)",
    category: "Internships",
    description: "Biomedical research internship allowing high school students to work alongside NIH scientists.",
    url: "https://www.training.nih.gov/programs/sip"
  },
  {
    id: 213,
    name: "Google Computer Science Summer Institute (CSSI)",
    category: "Summer Programs",
    description: "Introductory computer science enrichment program hosted by Google for high school seniors.",
    url: "https://buildyourfuture.withgoogle.com/programs/computer-science-summer-institute"
  },
  {
    id: 215,
    name: "Junior Science and Humanities Symposium (JSHS)",
    category: "Awards",
    description: "National STEM research competition where students present original scientific investigations.",
    url: "https://www.jshs.org/"
  },
  {
    id: 216,
    name: "DOE Computational Science Internship (CCI)",
    category: "Internships",
    description: "Department of Energy program offering research internships in computational science fields.",
    url: "https://science.osti.gov/wdts/cci"
  },
  {
    id: 219,
    name: "Stanford University Mathematics Camp (SUMaC)",
    category: "Summer Programs",
    description: "Advanced mathematics summer program for students with strong proof-based math backgrounds.",
    url: "https://sumac.spcs.stanford.edu/"
  },
  {
    id: 220,
    name: "Amazon Future Engineer Scholarship",
    category: "Scholarships",
    description: "Scholarship supporting students pursuing computer science and technology-related degrees.",
    url: "https://www.amazonfutureengineer.com/scholarship"
  },
  {
    id: 221,
    name: "Generation Google Scholarship",
    category: "Scholarships",
    description: "Technology scholarship for students pursuing degrees in computer science and related fields.",
    url: "https://buildyourfuture.withgoogle.com/scholarships/generation-google-scholarship"
  },
  {
    id: 222,
    name: "Blacks at Microsoft Scholarship",
    category: "Scholarships",
    description: "Merit-based scholarship supporting Black high school seniors pursuing technology careers.",
    url: "https://www.microsoft.com/en-us/diversity/inside-microsoft/scholarships/blacks-scholarship.aspx"
  },
  {
    id: 223,
    name: "Science Ambassador Scholarship",
    category: "Scholarships",
    description: "Full-tuition STEM scholarship awarded for a creative video explaining a scientific concept.",
    url: "https://www.scienceambassadorscholarship.org/"
  },
  {
    id: 224,
    name: "Dell Scholars Program",
    category: "Scholarships",
    description: "Scholarship program recognizing students overcoming significant challenges to pursue higher education.",
    url: "https://www.dellscholars.org/"
  },
  {
    id: 225,
    name: "Hyundai Women in STEM Scholarship",
    category: "Scholarships",
    description: "Scholarship supporting female students planning to pursue STEM degrees.",
    url: "https://www.hyundai-scholarship.com/"
  },
  {
    id: 226,
    name: "Navisite’s Next Steminist Scholarship",
    category: "Scholarships",
    description: "STEM scholarship recognizing young women with strong academic and leadership potential.",
    url: "https://www.navisite.com/steminist-scholarship/"
  },
  {
    id: 227,
    name: "Tuition Solution STEM Scholarship",
    category: "Scholarships",
    description: "Merit-based scholarship awarded to students pursuing degrees in STEM disciplines.",
    url: "https://www.tuitionsolution.org/"
  },
  {
    id: 228,
    name: "Employment BOOST STEM Scholarship",
    category: "Scholarships",
    description: "Quarterly scholarship supporting students entering STEM-related academic programs.",
    url: "https://employmentboost.com/scholarship/"
  },
  {
    id: 229,
    name: "National STEM Excellence Scholarship",
    category: "Scholarships",
    description: "Competitive scholarship recognizing academic achievement and commitment to STEM.",
    url: "https://www.stemexcellence.org/"
  },
  {
    id: 230,
    name: "Lumiere Research Scholar Program",
    category: "Programs",
    description: "Mentored research program connecting students with PhD-level scholars for independent projects.",
    url: "https://www.lumiere-education.com/"
  },
  {
    id: 231,
    name: "MD Anderson DACCPM Summer Research Program",
    category: "Summer Programs",
    description: "Cancer research summer program offering lab-based experience at a leading medical center.",
    url: "https://www.mdanderson.org/education-training/degrees-programs/summer-research-programs.html"
  },
  {
    id: 232,
    name: "Stanford Pre-Collegiate Studies",
    category: "Summer Programs",
    description: "Academic enrichment programs offering rigorous coursework for advanced high school students.",
    url: "https://spcs.stanford.edu/"
  },
  {
    id: 233,
    name: "NASA OSTEM Paid Internships",
    category: "Internships",
    description: "Paid NASA internships providing hands-on STEM research and engineering experience.",
    url: "https://intern.nasa.gov/"
  },
  {
    id: 234,
    name: "Princeton Plasma Physics Laboratory (PPPL) Internship",
    category: "Internships",
    description: "Research internship at a national laboratory focused on plasma physics and energy science.",
    url: "https://www.pppl.gov/education/science-education-programs"
  },
  {
    id: 235,
    name: "Baylor College of Medicine SMS Summer Research Program",
    category: "Summer Programs",
    description: "Selective biomedical research experience introducing students to laboratory science.",
    url: "https://www.bcm.edu/education/programs/saturday-morning-science"
  },
  {
    id: 237,
    name: "Hutton Junior Fisheries Biology Program",
    category: "Summer Programs",
    description: "Hands-on environmental science internship focused on fisheries biology and aquatic conservation.",
    url: "https://hutton.fisheries.org/"
  },
  {
    id: 238,
    name: "Engineering Summer Academy at Penn (ESAP)",
    category: "Summer Programs",
    description: "Pre-college engineering academy offering immersive courses across multiple engineering disciplines.",
    url: "https://esap.seas.upenn.edu/"
  },
  {
    id: 239,
    name: "BlueStamp Engineering",
    category: "Programs",
    description: "Independent engineering mentorship program guiding students through building a complete technical project.",
    url: "https://bluestampengineering.com/"
  },
  {
    id: 240,
    name: "NASA STEM Enhancement in Earth Science (SEES)",
    category: "Summer Programs",
    description: "Competitive NASA summer program focused on Earth science research and data analysis.",
    url: "https://www.csr.utexas.edu/sees-internship/"
  },
  {
    id: 241,
    name: "AI Scholars by Inspirit AI",
    category: "Summer Programs",
    description: "Project-based artificial intelligence program teaching students real-world machine learning applications.",
    url: "https://www.inspiritai.com/"
  },
  {
    id: 242,
    name: "NYU Tandon School of Engineering K-12 STEM Programs",
    category: "Summer Programs",
    description: "Pre-college engineering and technology programs offering hands-on STEM coursework.",
    url: "https://engineering.nyu.edu/k12"
  },
  {
    id: 243,
    name: "UCSD COSMOS (California State Summer School for Math & Science)",
    category: "Summer Programs",
    description: "Selective residential STEM program offering intensive science and math clusters at UC campuses.",
    url: "https://www.jacobsschool.ucsd.edu/cosmos/"
  },
  {
    id: 244,
    name: "TryEngineering Summer Institute (IEEE)",
    category: "Summer Programs",
    description: "Engineering-focused summer institute introducing students to hands-on design and innovation.",
    url: "https://tryengineeringinstitute.ieee.org/"
  },
  {
    id: 245,
    name: "MathWorks Math Modeling Challenge (M3 Challenge)",
    category: "Competitions",
    description: "National applied math competition where teams solve real-world modeling problems using data analysis and mathematical reasoning.",
    url: "https://m3challenge.siam.org/"
  },
  {
    id: 246,
    name: "National Space Society (NSS) Space Settlement Contest",
    category: "Competitions",
    description: "International design competition challenging students to create detailed space settlement concepts and engineering plans.",
    url: "https://space.nss.org/settlement/nasa/"
  },
  {
    id: 247,
    name: "TechOlympics",
    category: "Competitions",
    description: "Technology-focused competition testing students’ skills in coding, engineering, and problem-solving events.",
    url: "https://techolympics.org/"
  },
  {
    id: 248,
    name: "Young Scientist and Technology Exhibition",
    category: "Competitions",
    description: "STEM innovation exhibition where students present original science and technology research projects.",
    url: "https://btyoungscientist.com/"
  },
  {
    id: 266,
    name: "Act To Change",
    category: "Nonprofits",
    description: "National nonprofit working to end bullying of Asian American and Pacific Islander (AAPI) youth and empower young leaders to build inclusive, supportive communities.",
    url: "https://acttochange.org/"
  },
  {
    id: 269,
    name: "Journals of Justice",
    category: "Programs",
    description: "Youth-led platform encouraging students to explore civic engagement, social justice, and public policy writing.",
    url: "https://www.journalsofjustice.org/"
  },
  {
    id: 270,
    name: "Asian American Progressive Student Union",
    category: "Clubs",
    description: "Student organization promoting Asian American advocacy, cultural awareness, and community leadership.",
    url: "https://aapsu.org/"
  },
  {
    id: 271,
    name: "International Food Policy Research Institute (IFPRI)",
    category: "Nonprofits",
    description: "Global research institute focused on food security, nutrition, and international development policy.",
    url: "https://www.ifpri.org/"
  },
  {
    id: 272,
    name: "Taco Bell Live Más Scholarship",
    category: "Scholarships",
    description: "Merit-based scholarship awarding students who demonstrate passion, creativity, and community impact.",
    url: "https://www.tacobellfoundation.org/live-mas-scholarship/"
  },
  {
    id: 273,
    name: "Google Code Next",
    category: "Programs",
    description: "Computer science enrichment program providing mentorship and coding experience for high school students.",
    url: "https://codenext.withgoogle.com/"
  },
  {
    id: 274,
    name: "Code Ninjas",
    category: "Programs",
    description: "Coding education program teaching students programming, game development, and STEM fundamentals.",
    url: "https://www.codeninjas.com/"
  },
  {
    id: 275,
    name: "Bank of America Internship Program",
    category: "Internships",
    description: "Competitive internship opportunities providing professional experience in finance, business, and community leadership.",
    url: "https://about.bankofamerica.com/en-us/what-guides-us/student-leaders"
  },
  {
    id: 276,
    name: "Stanford High School Summer College",
    category: "Summer Programs",
    description: "College-level academic program allowing high school students to take undergraduate Stanford courses.",
    url: "https://summercollege.stanford.edu/"
  },
  {
    id: 277,
    name: "Harvard Pre-College Program",
    category: "Summer Programs",
    description: "Residential academic program offering college-level courses across a wide range of disciplines at Harvard.",
    url: "https://summer.harvard.edu/high-school-programs/pre-college-program/"
  },
  {
    id: 278,
    name: "Georgetown Hoya Summer High School Sessions",
    category: "Summer Programs",
    description: "Pre-college program providing high school students with college coursework and academic exploration opportunities.",
    url: "https://summer.georgetown.edu/"
  },
  {
    id: 279,
    name: "PROMYS (Program in Mathematics for Young Scientists)",
    category: "Summer Programs",
    description: "Intensive mathematics program focused on proof-based problem solving and advanced number theory.",
    url: "https://promys.org/"
  },
  {
    id: 280,
    name: "Princeton Summer Journalism Program",
    category: "Summer Programs",
    description: "Selective journalism program offering workshops and mentorship for students interested in media and reporting.",
    url: "https://psjp.princeton.edu/"
  },
  {
    id: 281,
    name: "Telluride Association Summer Seminars (TASS)",
    category: "Summer Programs",
    description: "Free, discussion-based academic seminar program emphasizing critical thinking and analytical writing.",
    url: "https://www.tellurideassociation.org/"
  },
  {
    id: 282,
    name: "University of Washington Summer Sessions",
    category: "Summer Programs",
    description: "College credit summer courses taught by university faculty across diverse academic subjects.",
    url: "https://www.summer.uw.edu/"
  },
  {
    id: 283,
    name: "National Science Bowl",
    category: "Competitions",
    description: "Team-based science and mathematics competition with regional and national championship rounds.",
    url: "https://science.osti.gov/wdts/nsb"
  },
  {
    id: 284,
    name: "Modeling the Future Challenge",
    category: "Competitions",
    description: "Data-driven competition where students apply mathematical modeling to analyze real-world issues.",
    url: "https://www.mtfchallenge.org/"
  },
  {
    id: 285,
    name: "American Computer Science League (ACSL)",
    category: "Competitions",
    description: "Programming and computer science contest testing coding, algorithms, and problem-solving skills.",
    url: "https://www.acsl.org/"
  },
  {
    id: 286,
    name: "MATE ROV Competition",
    category: "Competitions",
    description: "Engineering challenge where students design and operate remotely operated underwater vehicles.",
    url: "https://materovcompetition.org/"
  },
  {
    id: 287,
    name: "Horizon Academic Essay Prize",
    category: "Competitions",
    description: "International academic essay competition recognizing analytical research and academic writing.",
    url: "https://www.horizonacademic.org/essay-prize"
  },
  {
    id: 288,
    name: "Princeton Ten-Minute Play Contest",
    category: "Competitions",
    description: "National playwriting contest for high school students judged by university faculty.",
    url: "https://arts.princeton.edu/visual-arts/ten-minute-play-contest/"
  },
  {
    id: 289,
    name: "All-American High School Film Festival",
    category: "Competitions",
    description: "National film competition showcasing student-created short films across multiple genres.",
    url: "https://www.hsfilmfest.com/"
  },
  {
    id: 290,
    name: "Federal Reserve High School Fed Challenge",
    category: "Competitions",
    description: "Economics competition where teams analyze monetary policy and present research to judges.",
    url: "https://www.newyorkfed.org/outreach-and-education/high-school-fed-challenge"
  },
  {
    id: 291,
    name: "Stock Market Challenge",
    category: "Competitions",
    description: "Investment simulation competition where students manage mock portfolios and compete on performance.",
    url: "https://www.stockmarketgame.org/"
  },
  {
    id: 292,
    name: "We The Future Civics Contest",
    category: "Competitions",
    description: "National civic engagement contest encouraging students to explore constitutional principles and policy ideas.",
    url: "https://constitutingamerica.org/"
  },
  {
    id: 293,
    name: "High School Academic Research Competition (SARC)",
    category: "Competitions",
    description: "Independent research competition allowing students to submit original academic projects across disciplines.",
    url: "https://www.sarc-competition.org/"
  },
  {
    id: 294,
    name: "International Science & Mathematics Olympiad Pathway",
    category: "Competitions",
    description: "Elite international academic competitions reached through national qualifying rounds in math and science.",
    url: "https://www.maa.org/math-competitions"
  },
  {
    id: 295,
    name: "Creativity & Innovation Challenges",
    category: "Competitions",
    description: "Project-based competitions encouraging interdisciplinary problem-solving and innovative design.",
    url: "https://www.destinationimagination.org/"
  },
  {
    id: 296,
    name: "Cancer Kids First",
    category: "Nonprofits",
    description: "Youth-led nonprofit organization supporting children affected by cancer through fundraising, advocacy, and community service initiatives.",
    url: "https://www.cancerkidsfirst.org/"
  },
  {
    id: 297,
    name: "National Association of Black Journalists (NABJ) Scholarships",
    category: "Scholarships",
    description: "Merit-based scholarships supporting high school seniors pursuing careers in journalism and media.",
    url: "https://www.nabj.org/page/Scholarships"
  },
  {
    id: 298,
    name: "ScholarshipOwl No-Essay Awards",
    category: "Scholarships",
    description: "Easy-entry scholarships with no essay requirement for high school students.",
    url: "https://scholarshipowl.com/"
  },
  {
    id: 299,
    name: "Cappex Easy College Money Scholarship",
    category: "Scholarships",
    description: "Monthly no-essay scholarship drawing for high school seniors planning to attend college.",
    url: "https://www.cappex.com/"
  },
  {
    id: 300,
    name: "Bold.org No-Essay Scholarships",
    category: "Scholarships",
    description: "Various quick-apply scholarships hosted on Bold.org requiring minimal application materials.",
    url: "https://bold.org/"
  },
  {
    id: 301,
    name: "Carnegie Mellon SAMS (Summer Academy for Math + Science)",
    category: "Summer Programs",
    description: "Selective CMU summer program for rising seniors focused on advanced math, science, and research-style problem solving.",
    url: "https://www.cmu.edu/pre-college/academic-programs/sams.html"
  },
  {
    id: 302,
    name: "Blue Ocean Competition",
    category: "Competitions",
    description: "Global entrepreneurship competition where students develop innovative business ideas addressing unmet market needs.",
    url: "https://blueoceancompetition.org/"
  },
  {
    id: 303,
    name: "UCSB Summer Research Mentorship Program (SRMP)",
    category: "Summer Programs",
    description: "Selective UC Santa Barbara research program pairing high school students with graduate mentors for hands-on academic research projects.",
    url: "https://www.graddiv.ucsb.edu/srmp"
  },
  {
    id: 304,
    name: "APAICS Youth Leadership Academy",
    category: "Summer Programs",
    description: "Civic leadership program by APAICS introducing students to public service, policy, and advocacy through workshops and mentorship.",
    url: "https://apaics.org/"
  },
  {
    id: 305,
    name: "YIELD (Youth Initiative Embracing Lasting Development)",
    category: "Nonprofits",
    description: "Student-founded 501(c)(3) nonprofit working to bridge educational resource gaps and promote global equity through youth-led initiatives.",
    url: "https://www.yieldnonprofit.org/"
  },
  {
    id: 306,
    name: "Isabel’s Seeds of Hope",
    category: "Nonprofits",
    description: "Youth-led nonprofit addressing food insecurity while empowering young people to lead service projects internationally.",
    url: "https://www.isabelsseedsofhope.org/"
  },
  {
    id: 307,
    name: "Climate Cardinals",
    category: "Nonprofits",
    description: "Student-founded climate nonprofit translating environmental information into multiple languages to expand global climate education access.",
    url: "https://www.climatecardinals.org/"
  },
  {
    id: 308,
    name: "Medicine Encompassed",
    category: "Nonprofits",
    description: "Student-run nonprofit increasing access, mentorship, and awareness for students interested in pursuing medical careers.",
    url: "https://www.medicineencompassed.org/"
  },
  {
    id: 309,
    name: "The Giving Game",
    category: "Nonprofits",
    description: "Youth-driven initiative encouraging students to research, present, and support charitable organizations and community causes.",
    url: "https://www.givinggame.org/"
  },
  {
    id: 310,
    name: "Student Philanthropy Council (SPC)",
    category: "Clubs",
    description: "Student-led philanthropy organization focused on community engagement, fundraising, and service leadership.",
    url: "https://www.studentphilanthropy.org/"
  },
  {
    id: 311,
    name: "Youth Climate Lab (YCL)",
    category: "Nonprofits",
    description: "International youth-led nonprofit advancing climate innovation, sustainability projects, and youth empowerment.",
    url: "https://www.youthclimatelab.org/"
  },
  {
    id: 312,
    name: "EcoMoco",
    category: "Nonprofits",
    description: "Youth-driven environmental initiative promoting sustainability, climate awareness, and eco-friendly community action.",
    url: "https://ecomoco.org/"
  },
  {
    id: 313,
    name: "Green Sweep",
    category: "Nonprofits",
    description: "Student-led environmental organization focused on community cleanups, sustainability advocacy, and climate action initiatives.",
    url: "https://www.greensweep.org/"
  },
  {
    id: 314,
    name: "FIRST Dean’s List Award",
    category: "Awards",
    description: "Prestigious FIRST Robotics recognition honoring student leaders for outstanding impact, innovation, and dedication within their robotics team and community.",
    url: "https://www.firstinspires.org/robotics/frc/deans-list"
  },
  {
    id: 315,
    name: "Hack Club",
    category: "Clubs",
    description: "Global network of student-led coding clubs where members build real-world software, collaborate on projects, and grow as young developers.",
    url: "https://hackclub.com/"
  },
  {
    id: 316,
    name: "Johns Hopkins ASPIRE Program",
    category: "Summer Programs",
    description: "Selective Johns Hopkins summer research program providing high school students with mentored laboratory experience in biomedical and health-related fields.",
    url: "https://www.jhuapl.edu/about/outreach/aspire"
  },
  {
    id: 317,
    name: "Broad Horizons Scholarship",
    category: "Scholarships",
    description: "Merit-based scholarship supporting students who demonstrate academic excellence, leadership, and commitment to expanding opportunities in their communities.",
    url: "https://www.broadhorizons.org/"
  },
  {
    id: 318,
    name: "Xerox Award for Innovation and Information Technology",
    category: "Awards",
    description: "School-nominated award recognizing high-achieving students with exceptional accomplishments in innovation, engineering, and information technology.",
    url: "https://www.rochester.edu/admissions/high-school-awards/xerox-award/"
  },
  {
    id: 319,
    name: "Tuition Solution Scholarship for STEM Students",
    category: "Scholarships",
    description: "Merit-based scholarship supporting high school seniors planning to pursue degrees in science, technology, engineering, or mathematics.",
    url: "https://www.tuitionsolution.org/"
  },
  {
    id: 320,
    name: "Kode With Klossy",
    category: "Summer Programs",
    description: "Free coding camps founded by Karlie Kloss that teach high school girls and gender-expansive students web and app development skills.",
    url: "https://www.kodewithklossy.com/"
  },
  {
    id: 321,
    name: "Girls in Technology (GIT)",
    category: "Programs",
    description: "DC-area tech initiative offering virtual and in-person programs with hands-on challenges, presentations, and networking opportunities for girls.",
    url: "https://www.womenintechnology.org/girls-in-technology"
  },
  {
    id: 322,
    name: "SheTech Summer Internship Exploration",
    category: "Summer Programs",
    description: "Career exploration program allowing high school girls to visit tech companies, meet women in STEM, and learn about technology pathways.",
    url: "https://shetechexplorer.com/"
  },
  {
    id: 323,
    name: "Girls Inc. Girls in STEM",
    category: "Programs",
    description: "Year-round enrichment program providing hands-on STEM learning experiences and mentorship to support girls in science and technology.",
    url: "https://girlsinc.org/"
  },
  {
    id: 324,
    name: "Diamond Challenge",
    category: "Competitions",
    description: "International entrepreneurship competition where students develop and pitch innovative business ideas to expert judges.",
    url: "https://diamondchallenge.org/"
  },
  {
    id: 325,
    name: "VEX Robotics Competition",
    category: "Competitions",
    description: "Global robotics competition where teams design, build, and program robots to complete engineering-based challenges.",
    url: "https://www.vexrobotics.com/competition"
  },
  {
    id: 326,
    name: "Zero Robotics",
    category: "Competitions",
    description: "Space-based programming competition where students write code to control satellites aboard the International Space Station.",
    url: "http://zerorobotics.mit.edu/"
  },
  {
    id: 327,
    name: "The Diana Award",
    category: "Awards",
    description: "International award recognizing young people for exceptional social action, leadership, and community impact.",
    url: "https://diana-award.org.uk/"
  },
  {
    id: 328,
    name: "LaunchX",
    category: "Summer Programs",
    description: "Entrepreneurship program where students build and launch real startups with mentorship from industry leaders.",
    url: "https://launchx.com/"
  },
  {
    id: 329,
    name: "Veritas AI",
    category: "Programs",
    description: "Mentored artificial intelligence research program connecting students with experts to complete independent AI projects.",
    url: "https://www.veritasai.com/"
  },
  {
    id: 330,
    name: "The Gates Scholarship",
    category: "Scholarships",
    description: "Highly selective full-ride scholarship for outstanding, low-income, minority high school seniors.",
    url: "https://www.thegatesscholarship.org/"
  },
  {
    id: 331,
    name: "Jack Kent Cooke College Scholarship Program",
    category: "Scholarships",
    description: "Prestigious scholarship providing significant financial support and advising to high-achieving students with financial need.",
    url: "https://www.jkcf.org/our-scholarships/college-scholarship-program/"
  },
  {
    id: 332,
    name: "Elks National Foundation Most Valuable Student Scholarship",
    category: "Scholarships",
    description: "National scholarship recognizing leadership, academic achievement, and community involvement among high school seniors.",
    url: "https://www.elks.org/scholars/scholarships/mvs.cfm"
  },
  {
    id: 333,
    name: "MEDLIFE Student Chapters",
    category: "Nonprofits",
    description: "Student-led chapters of MEDLIFE where youth volunteer for global health and medical outreach, service, and education projects in local and international communities.",
    url: "https://www.medlifemovement.org/"
  },
  {
    id: 334,
    name: "High School Alliance of Future Physicians (HSAFP)",
    category: "Nonprofits",
    description: "Nationwide organization empowering high school students with healthcare exposure, medical skills workshops, physician mentorship, and college pathway support toward future medical careers.",
    url: "https://www.hsafp.org/"
  },
  {
    id: 335,
    name: "H.E.A.L.T.H. for Youths, Inc.",
    category: "Nonprofits",
    description: "501(c)(3) nonprofit focused on youth wellness education, life-skills training, and improving health care access and quality for young people.",
    url: "https://www.healthforyouths.org/"
  },
  {
    id: 336,
    name: "HealthCorps",
    category: "Nonprofits",
    description: "National nonprofit educating and empowering teens on nutrition, fitness, and mental well-being to improve health outcomes in youth communities.",
    url: "https://www.healthcorps.org/"
  },
  {
    id: 337,
    name: "Active Minds (Mental Health Advocacy)",
    category: "Nonprofits",
    description: "Student-led mental health advocacy nonprofit supporting youth mental wellness and reducing stigma through peer education and chapter networks.",
    url: "https://www.activeminds.org/"
  },
  {
    id: 338,
    name: "Peer Health Exchange",
    category: "Nonprofits",
    description: "Health education organization training volunteers to teach comprehensive, life-skills-based health classes in underserved high schools.",
    url: "https://www.peerhealthexchange.org/"
  },
  {
    id: 339,
    name: "St. Jude Children’s Research Hospital Volunteer Programs",
    category: "Nonprofits",
    description: "Volunteer opportunities where high school students can engage with hospital fundraising, awareness campaigns, and community health outreach.",
    url: "https://www.stjude.org/get-involved/other-ways/volunteer.html"
  },
  {
    id: 340,
    name: "AHEC Health Career Programs",
    category: "Programs",
    description: "Area Health Education Center (AHEC) initiatives providing hands-on health career education and mentorship pathways for high school students.",
    url: "https://www.nationalahec.org/"
  },
  {
    id: 341,
    name: "West Central Alabama Health Professions Academy (HPA)",
    category: "Programs",
    description: "Creative, hands-on health career enrichment program for grades 9-12 exposing students to a variety of healthcare professions and medical skills.",
    url: "https://www.wcalahc.org/hpa"
  },
  {
    id: 342,
    name: "BioGENEius Challenge",
    category: "Competitions",
    description: "Journals; research-based science competition where students submit independent biomedical and life-science research projects for national recognition.",
    url: "https://www.biotechinstitute.org/"
  },
  {
    id: 343,
    name: "International Genetically Engineered Machine (iGEM) Competition",
    category: "Competitions",
    description: "Team competition challenging students to design synthetic biology solutions with real-world medical, environmental, or health applications.",
    url: "https://igem.org/"
  },
  {
    id: 344,
    name: "National Institutes of Health (NIH) Design by Biomedical Undergraduate Teams (DEBUT) Challenge",
    category: "Competitions",
    description: "Biomedical design contest where students create solutions addressing clinical/biomedical challenges (open to high school teams in some years).",
    url: "https://www.nibib.nih.gov/training-careers/undergraduate-graduate/design-biomedical-undergraduate-teams-debut-challenge"
  },
  {
    id: 345,
    name: "Siemens (Discovery Education) 21st Century Science Competition",
    category: "Competitions",
    description: "Research competition where students explore science topics — including biomedicine, health sciences, and medical innovation — through project submissions.",
    url: "https://www.discoveryeducation.com/"
  },
  {
    id: 346,
    name: "American Red Cross Student Challenge",
    category: "Competitions",
    description: "Annual challenge with health-oriented missions including first aid, CPR activities, blood donation awareness, and community health campaigns.",
    url: "https://www.redcross.org/"
  },
  {
    id: 347,
    name: "Johns Hopkins University Neuroscience Competition",
    category: "Competitions",
    description: "High school neuroscience competition focused on brain science research challenges and experimental design.",
    url: "https://ba.jhu.edu/"
  },
  {
    id: 348,
    name: "Biomedical Innovation Challenge (varies by sponsor)",
    category: "Competitions",
    description: "Competitions where teams propose and pitch biomedical device innovations with real clinical impact.",
    url: "https://ebme.org/"
  },
  {
    id: 349,
    name: "International Biology Olympiad (IBO)",
    category: "Competitions",
    description: "Elite biology competition where students complete theory and practical exams — often including medicine-related biology content.",
    url: "https://www.ibo-info.org/"
  },
  {
    id: 350,
    name: "Global Health Case Challenge",
    category: "Competitions",
    description: "Case-based competition where students analyze global health problems and propose evidence-based public health or healthcare solutions.",
    url: "https://globalhealth.ku.dk/educational-activities/case-challenge/"
  },
  {
    id: 372,
    name: "NIH High School Summer Internship Program (HS-SIP)",
    category: "Summer Programs",
    description: "Competitive research internship placing high school students in NIH biomedical labs for hands-on scientific experience.",
    url: "https://www.training.nih.gov/programs/hs-sip"
  },
  {
    id: 373,
    name: "CDC Museum Disease Detective Camp",
    category: "Summer Programs",
    description: "Public health immersion program introducing high school students to epidemiology, outbreak investigation, and disease prevention.",
    url: "https://www.cdc.gov/museum/camp/detective/"
  },
  {
    id: 374,
    name: "Johns Hopkins Internship in Brain Sciences (JHIBS)",
    category: "Summer Programs",
    description: "Neuroscience-focused research internship for high school students interested in brain science and medicine.",
    url: "https://www.hopkinsmedicine.org/neurology_neurosurgery/about_us/jhibs.html"
  },
  {
    id: 375,
    name: "Hofstra University Summer Science Research Program (HUSSRP)",
    category: "Summer Programs",
    description: "Faculty-mentored biomedical research program for high school students culminating in a scientific poster presentation.",
    url: "https://www.hofstra.edu/science-research-program/"
  },
  {
    id: 376,
    name: "Rockefeller University Summer Science Research Program (SSRP)",
    category: "Summer Programs",
    description: "Highly selective biomedical lab research program for high school students in New York City.",
    url: "https://www.rockefeller.edu/outreach/ssrp/"
  },
  {
    id: 377,
    name: "Georgetown University Medical Academy",
    category: "Summer Programs",
    description: "Pre-medical summer academy offering hands-on lab work and exposure to clinical and health sciences careers.",
    url: "https://summer.georgetown.edu/programs/XSTP/medical-academy"
  },
  {
    id: 378,
    name: "UCLA Pre-Medical and Health Professions Program (High School Track)",
    category: "Summer Programs",
    description: "Summer program introducing high school students to medicine, healthcare careers, and foundational biomedical sciences.",
    url: "https://www.uclaextension.edu/"
  },
  {
    id: 379,
    name: "Baylor College of Medicine SMS Summer Research Program",
    category: "Summer Programs",
    description: "Selective biomedical research experience providing high school students with laboratory exposure and mentorship.",
    url: "https://www.bcm.edu/education/programs/saturday-morning-science"
  },
  {
    id: 380,
    name: "Children’s Hospital Los Angeles High School Summer Program",
    category: "Summer Programs",
    description: "Clinical and biomedical research exposure program designed for high school students interested in pediatric medicine.",
    url: "https://www.chla.org/high-school-summer-program"
  },
  {
    id: 381,
    name: "Fred Hutch Summer High School Internship",
    category: "Internships",
    description: "Provides high schoolers hands-on laboratory training and research experience in cancer biology.",
    url: "https://www.fredhutch.org/en/education-training/high-school-students.html"
  },
  {
    id: 382,
    name: "RockEDU Summer Science Research Program",
    category: "Summer Program",
    description: "Offers students an immersive seven-week hands-on biomedical research experiences at The Rockefeller University, focusing on collaboration and scientific communication",
    url: "https://www.rockefeller.edu/outreach/ssrp/"
  },
  {
    id: 383,
    name: "Broad Summer Scholars Program",
    category: "Summer Programs",
    description: "Selective biomedical research program offering high school students hands-on lab experience in genetics and genomics.",
    url: "https://www.broadinstitute.org/bssp"
  },
  {
    id: 384,
    name: "CMU AI Scholars",
    category: "Summer Programs",
    description: "Computer science and artificial intelligence enrichment program introducing students to foundational AI concepts and research at Carnegie Mellon.",
    url: "https://www.cmu.edu/pre-college/academic-programs/ai-scholars.html"
  },
  {
    id: 385,
    name: "CMU CS Scholars",
    category: "Summer Programs",
    description: "Access-focused computer science program at Carnegie Mellon providing rigorous coursework and mentorship in programming and computational thinking.",
    url: "https://www.cmu.edu/pre-college/academic-programs/computer-science-scholars.html"
  },
  {
    id: 386,
    name: "Girls Who Code Summer Immersion Program",
    category: "Summer Programs",
    description: "Intensive coding program empowering high school students with programming skills, real-world projects, and exposure to careers in technology.",
    url: "https://girlswhocode.com/programs/summer-immersion-program"
  },
  {
    id: 387,
    name: "NASA SEES Internship",
    category: "Summer Programs",
    description: "STEM research internship where students collaborate with scientists on Earth and space science projects using authentic NASA data.",
    url: "https://www.csr.utexas.edu/sees-internship/"
  },
  {
    id: 388,
    name: "Fermilab PRISM Internship",
    category: "Summer Programs",
    description: "Paid research internship introducing students to particle physics, accelerator science, and hands-on laboratory work.",
    url: "https://ed.fnal.gov/programs/prism/"
  },
  {
    id: 389,
    name: "Fermilab VALOR JROTC Internship",
    category: "Summer Programs",
    description: "Research and leadership internship for JROTC students focused on physics, engineering, and national laboratory research exposure.",
    url: "https://ed.fnal.gov/programs/valor/"
  },
  {
    id: 390,
    name: "Smithsonian NMNH Summer High School Internship",
    category: "Summer Programs",
    description: "Museum-based research internship at the National Museum of Natural History offering mentorship in biological and earth sciences.",
    url: "https://naturalhistory.si.edu/education/programs/high-school-programs"
  },
  {
    id: 391,
    name: "Smithsonian Air and Space Museum Summer Internship",
    category: "Summer Programs",
    description: "Hands-on internship at the National Air and Space Museum focused on aerospace research, history, and STEM outreach initiatives.",
    url: "https://airandspace.si.edu/support/get-involved/internships"
  },
  {
    id: 392,
    name: "American Regions Mathematics League (ARML)",
    category: "Competitions",
    description: "Prestigious team-based mathematics competition bringing together top high school students for advanced problem-solving challenges.",
    url: "https://www.arml.com/"
  },
  {
    id: 393,
    name: "American Invitational Mathematics Examination (AIME)",
    category: "Competitions",
    description: "Selective national mathematics exam for high-scoring AMC participants, serving as a pathway to USA Mathematical Olympiad qualification.",
    url: "https://www.maa.org/math-competitions/about-amc"
  },
  {
    id: 394,
    name: "National High School Essay Contest",
    category: "Competitions",
    description: "Nationwide essay competition encouraging students to explore historical and civic themes through analytical writing.",
    url: "https://afsa.org/essay-contest"
  },
  {
    id: 395,
    name: "Interlochen Summer Arts Camp",
    category: "Summer Programs",
    description: "Renowned multidisciplinary arts camp offering intensive training in music, theatre, visual arts, dance, and creative writing.",
    url: "https://www.interlochen.org/arts-camp"
  },
  {
    id: 396,
    name: "Blue Lake Fine Arts Camp",
    category: "Summer Programs",
    description: "Arts-focused summer camp providing immersive instruction in music, visual arts, theatre, and dance.",
    url: "https://bluelake.org/"
  },
  {
    id: 397,
    name: "National Student Nurses’ Association (NSNA)",
    category: "Clubs & Organizations",
    description: "Pre-professional organization supporting nursing students through leadership development, advocacy, and career resources.",
    url: "https://www.nsna.org/"
  },
  {
    id: 398,
    name: "Boy Scouts",
    category: "Clubs & Organizations",
    description: "Youth leadership and character-development organization focused on outdoor skills, community service, and merit-based achievement.",
    url: "https://www.scouting.org/"
  },
  {
    id: 399,
    name: "Girl Scouts",
    category: "Clubs & Organizations",
    description: "Leadership and service organization empowering girls through community projects, entrepreneurship, and skill-building programs.",
    url: "https://www.girlscouts.org/"
  },
  {
    id: 400,
    name: "Microsoft Imagine Cup",
    category: "Competitions",
    description: "Global technology competition challenging students to develop innovative software solutions to real-world problems.",
    url: "https://imaginecup.microsoft.com/"
  },
  {
    id: 401,
    name: "WWDC Scholar (Apple Developer Conference)",
    category: "Awards & Honors",
    description: "Competitive scholarship recognition for student developers selected to participate in Apple’s Worldwide Developers Conference.",
    url: "https://developer.apple.com/wwdc/"
  },
  {
    id: 402,
    name: "Washington Journalism and Media Conference",
    category: "Summer Programs",
    description: "Selective journalism program introducing students to media ethics, reporting, and communication careers.",
    url: "https://wjmc.gmu.edu/"
  },
  {
    id: 403,
    name: "Euro Challenge",
    category: "Competitions",
    description: "Economics competition where students analyze European Union member economies and present policy recommendations.",
    url: "https://www.euro-challenge.org/"
  },
  {
    id: 404,
    name: "American Legion Oratorical Contest",
    category: "Competitions",
    description: "National speech competition focused on the U.S. Constitution, promoting public speaking and civic understanding.",
    url: "https://www.legion.org/oratorical"
  },
  {
    id: 405,
    name: "Young Orators of America",
    category: "Competitions",
    description: "Public speaking organization providing students opportunities to develop persuasive speaking and leadership skills.",
    url: "https://www.youngorators.org/"
  },
  {
    id: 406,
    name: "Young Reporters for the Environment",
    category: "Competitions",
    description: "International environmental journalism competition encouraging investigative reporting on sustainability issues.",
    url: "https://www.yre.global/"
  },
  {
    id: 407,
    name: "Washington Youth Summit on the Environment",
    category: "Summer Programs",
    description: "Leadership conference focused on environmental science, policy, and sustainability careers.",
    url: "https://wyse.gmu.edu/"
  },
  {
    id: 408,
    name: "National Forensics League (Speech & Debate)",
    category: "Clubs & Organizations",
    description: "Competitive speech and debate organization fostering critical thinking, argumentation, and public speaking skills.",
    url: "https://www.speechanddebate.org/"
  },
  {
    id: 409,
    name: "Stanford Summer Humanities Institute (SHI)",
    category: "Summer Programs",
    description: "Three-week residential program where rising juniors and seniors explore big historical and philosophical questions through faculty-led seminars.",
    url: "https://summerhumanities.spcs.stanford.edu/"
  },
  {
    id: 410,
    name: "Iowa Young Writers' Studio",
    category: "Summer Programs",
    description: "Highly selective creative writing residency hosted by the University of Iowa, focusing on poetry, fiction, and creative nonfiction.",
    url: "https://iyws.creativewriting.uiowa.edu/"
  },
  {
    id: 411,
    name: "Pioneer Academics Research Program",
    category: "Programs",
    description: "Fully accredited online research institute offering high schoolers the chance to conduct original research under 1-on-1 university faculty mentorship.",
    url: "https://pioneeracademics.com/"
  },
  {
    id: 412,
    name: "Genes in Space",
    category: "Competitions",
    description: "STEM competition where students design DNA analysis experiments to be carried out by astronauts aboard the International Space Station.",
    url: "https://www.genesinspace.org/"
  },
  {
    id: 413,
    name: "Boston Children's Hospital COACH Program",
    category: "Internships",
    description: "Paid summer internship placing students in active healthcare support, administrative, and clinical department resource roles.",
    url: "https://www.childrenshospital.org/"
  },
  {
    id: 414,
    name: "Health Professions Recruitment and Exposure Program (HPREP)",
    category: "Programs",
    description: "Nationally recognized pipeline initiative exposing minority and underrepresented students to medical and public health professions.",
    url: "https://www.hprep.org/"
  },
  {
    id: 415,
    name: "Stanford Medical Youth Science Program (SMYSP)",
    category: "Summer Programs",
    description: "Selective low-income pipeline program offering immersive hospital internships, anatomy lab access, and college guidance.",
    url: "https://smysp.stanford.edu/"
  },
  {
    id: 416,
    name: "Solar Car Challenge",
    category: "Competitions",
    description: "Project-based STEM initiative challenging high school teams to build, test, and safely race roadworthy solar-powered vehicles.",
    url: "https://www.solarcarchallenge.org/"
  },
  {
    id: 417,
    name: "National STEM Challenge",
    category: "Competitions",
    description: "National project challenge focused on using the scientific method or engineering design process to solve major global themes.",
    url: "https://www.nationalstemchallenge.com/"
  },
  {
    id: 418,
    name: "Aerial Drone Competition",
    category: "Competitions",
    description: "Dynamic team competition testing students on drone piloting, autonomous programming, flight physics, and tactical communication.",
    url: "https://www.roboticseducation.org/aerial-drone-competition/"
  },
  {
    id: 419,
    name: "Georgetown Gateway Exploration Program (GEP) in Medicine",
    category: "Internships",
    description: "Summer medical internship offering students structural physician shadowing, clinical department matching, and systemic mentorship.",
    url: "https://som.georgetown.edu/"
  },
  {
    id: 420,
    name: "Baltimore Alliance for Careers in Healthcare (BACH) Fellows",
    category: "Internships",
    description: "Six-week paid summer fellowship introducing local youth to hospital workflows, allied health settings, and professional medical skills.",
    url: "https://www.baltimorealliancemed.org/"
  },
  {
    id: 421,
    name: "RISE Research Mentorship Program",
    category: "Programs",
    description: "Selective 1-on-1 mentorship program pairing students with PhD mentors to write peer-reviewed papers for independent academic journals.",
    url: "https://www.riseresearch.org/"
  },
  {
    id: 422,
    name: "Notre Dame Leadership Seminars",
    category: "Summer Programs",
    description: "Free, highly selective residential program exploring global issues, ethics, and philosophy through rigorous Socratic discussion.",
    url: "https://precollege.nd.edu/leadership-seminars/"
  },
  {
    id: 423,
    name: "Envirothon",
    category: "Competitions",
    description: "North American environmental science competition testing team knowledge in aquatics, forestry, soils, wildlife, and current environmental issues.",
    url: "https://envirothon.org/"
  },
  {
    id: 424,
    name: "Shell Eco-marathon",
    category: "Competitions",
    description: "Global student engineering challenge focused on energy optimization where teams design, build, and drive ultra-efficient vehicles.",
    url: "https://www.shell.com/make-the-future/shell-eco-marathon.html"
  },
  {
    id: 425,
    name: "Physics Unlimited Premier Competition",
    category: "Competitions",
    description: "International physics competition challenging students to solve advanced, university-level physics problems within a limited timeframe.",
    url: "https://www.physicsunlimited.org/"
  },
  {
    id: 426,
    name: "The Concord Review History Camp",
    category: "Summer Programs",
    description: "Intensive workshops focused on equipping high school students with advanced skills for primary-source history research and academic writing.",
    url: "https://www.tcr.org/camp"
  },
  {
    id: 427,
    name: "USC Annenberg Youth Academy for Media and Civic Engagement",
    category: "Summer Programs",
    description: "Five-week program introducing students to the role of journalism, media communications, and free speech in shaping civic thought leaders.",
    url: "https://annenberg.usc.edu/aya"
  },
  {
    id: 428,
    name: "Foundation for Teaching Economics (FTE) Economics for Leaders",
    category: "Summer Programs",
    description: "Selective program teaching high school leaders how to apply economic analysis and reasoning to difficult public policy challenges.",
    url: "https://www.fte.org/students/economics-for-leaders/"
  },
  {
    id: 429,
    name: "BEST Robotics Challenge",
    category: "Competitions",
    description: "National robotics competition that tasks students with designing a robot while teaching execution, technical communication, and professional presentation skills.",
    url: "https://www.bestrobotics.org/"
  },
  {
    id: 430,
    name: "International Bridge Building Contest",
    category: "Competitions",
    description: "Engineering competition testing critical thinking and spatial design as teams construct a functional bridge out of specified materials.",
    url: "https://bridgecontest.phys.iit.edu/"
  },
  {
    id: 431,
    name: "International Psychology Olympiad (IPHO)",
    category: "Competitions",
    description: "Global competition testing participants' advanced knowledge of psychological concepts and their application to real-world behavioral issues.",
    url: "https://www.internationalpsychologyolympiad.org/"
  },
  {
    id: 432,
    name: "Minds Underground Psychology Essay Competition",
    category: "Competitions",
    description: "Writing competition inviting high school students to submit researched essays responding to changing annual psychology prompts.",
    url: "https://www.mindsunderground.com/psychology-essay-competitions"
  },
  {
    id: 433,
    name: "Harvard National Speech and Debate Tournament",
    category: "Competitions",
    description: "Highly prestigious national tournament held over President's Day Weekend in Cambridge, showcasing elite speech and debate talent.",
    url: "https://harvarddebate.org/"
  },
  {
    id: 434,
    name: "World Schools Debating Championships (WSDC)",
    category: "Competitions",
    description: "Elite global debate tournament featuring international teams debating complex motions ranging from sports ethics to global policy.",
    url: "https://www.wsdcdebate.org/"
  },
  {
    id: 435,
    name: "Brain Awareness Video Contest",
    category: "Competitions",
    description: "Creative digital media challenge where students produce short, engaging, and educational videos explaining neuroscience concepts.",
    url: "https://www.brainfacts.org/in-the-lab/video-contest"
  },
  {
    id: 436,
    name: "National Personal Finance Challenge",
    category: "Competitions",
    description: "Multi-tiered tournament and quiz-bowl testing student knowledge on budgeting, saving, investing, and financial literacy.",
    url: "https://www.councilforeconed.org/national-personal-finance-challenge/"
  },
  {
    id: 437,
    name: "Illinois State University Fashion Design Competition",
    category: "Competitions",
    description: "Design and business competition showcasing student creative talent in fashion, merchandising, and apparel design with scholarship prizes.",
    url: "https://illinoisstate.edu/"
  },
  {
    id: 438,
    name: "Ned Smith Center Youth Art Contest",
    category: "Competitions",
    description: "Annual 2-D art competition supporting the next generation of wildlife and nature artists through rotating annual themes.",
    url: "https://nedsmithcenter.org/"
  },
  {
    id: 439,
    name: "Doodle for Google",
    category: "Competitions",
    description: "Massive national student art contest where K-12 creators redesign the Google homepage logo around a central theme for scholarship prizes.",
    url: "https://doodles.google.com/d4g/"
  },
  {
    id: 440,
    name: "The Fresh Voices Poetry Competition",
    category: "Competitions",
    description: "Poetry Society of America competition connecting high school poets with established writers and literary communities.",
    url: "https://poetrysociety.org/"
  },
  {
    id: 441,
    name: "National Young Composers Challenge",
    category: "Competitions",
    description: "Acoustic composition contest for students ages 13-18 who submit original musical works for full orchestra or small chamber ensembles.",
    url: "https://youngcomposerschallenge.org/"
  },
  {
    id: 442,
    name: "American String Teachers Association (ASTA) Competitions",
    category: "Competitions",
    description: "Performance competition offering soloists and chamber ensembles national recognition and critique from elite string musicians.",
    url: "https://www.astastrings.org/"
  },
  {
    id: 443,
    name: "National Youth Orchestra of the United States (NYO-USA)",
    category: "Competitions",
    description: "Highly selective and prestigious youth orchestra program culminating in national training residencies and global concert tours.",
    url: "https://www.carnegiehall.org/Education/Programs/National-Youth-Orchestras"
  },
  {
    id: 444,
    name: "Taiwan Fashion Design Award Competition (TFDA)",
    category: "Competitions",
    description: "International fashion showcase open to student designers that highlights sustainable, creative, and high-performance design innovations.",
    url: "http://www.tfdaward.com/"
  },
  {
    id: 445,
    name: "Youth Fashion Show (East Texas State Fair)",
    category: "Competitions",
    description: "Apparel construction and design competition featuring traditional and non-traditional styling categories for youth.",
    url: "https://www.etstatefair.com/"
  },
  {
    id: 446,
    name: "Fashion Reimagined (Long Island University)",
    category: "Competitions",
    description: "Fashion business pitch contest where students submit video proposals outlining their original brand concepts and business models.",
    url: "https://liu.edu/"
  },
  {
    id: 447,
    name: "Virtual Enterprises International Business Plan Competition",
    category: "Competitions",
    description: "Entrepreneurial challenge simulating the business startup phase, where teams defend an original corporate strategy to a panel of corporate judges.",
    url: "https://veinternational.org/"
  },
  {
    id: 448,
    name: "Stevens Institute Trading Day",
    category: "Competitions",
    description: "Month-long investment simulation challenge using virtual portfolios to test asset management strategies, culminating in live finals.",
    url: "https://www.stevens.edu/"
  },
  {
    id: 449,
    name: "National Economics Challenge",
    category: "Competitions",
    description: "Multi-tiered academic tournament testing high school students on microeconomics, macroeconomics, and global economic concepts.",
    url: "https://www.councilforeconed.org/national-economics-challenge/"
  },
  {
    id: 450,
    name: "The Stock Market Game (SIFMA)",
    category: "Competitions",
    description: "Capital markets simulation introducing high schoolers to equity trading, capital allocation, and portfolio diversity mechanics.",
    url: "https://www.stockmarketgame.org/"
  },
  {
    id: 451,
    name: "Annual DNA Day Essay Contest",
    category: "Competitions",
    description: "Genetics writing competition where high school students construct well-reasoned argumentative papers on major research and bioethics themes.",
    url: "https://www.ashg.org/education/dna-day-essay-contest/"
  },
  {
    id: 452,
    name: "Lumiere Scholars Essay Award",
    category: "Competitions",
    description: "International essay competition challenging students to write formal, research-backed argumentative papers based on prompts provided by top-tier university professors.",
    url: "https://www.lumiere-education.com/"
  },
  {
    id: 453,
    name: "Singularity: AI Essay Contest",
    category: "Competitions",
    description: "Global writing challenge hosted by Veritas AI where students analyze the future societal risks, ethical questions, and technical possibilities of artificial intelligence.",
    url: "https://www.veritasai.com/"
  },
  {
    id: 454,
    name: "Learning to Be Heard – Global Public Speaking Challenge",
    category: "Competitions",
    description: "International public speaking competition by Immerse Education requiring a 60-second video response on an impactful message the speaker wishes to share with the world.",
    url: "https://www.immerse.education/"
  },
  {
    id: 455,
    name: "International Physics Olympiad (IPhO)",
    category: "Competitions",
    description: "Elite global physics competition testing students on rigorous theoretical and experimental physics challenges through multi-tiered national qualifying pathways.",
    url: "https://www.ipho-official.org/"
  },
  {
    id: 456,
    name: "International History Olympiad",
    category: "Competitions",
    description: "Global academic tournament featuring diverse competitive formats including written examinations, fast-paced buzzer rounds, and interactive historical simulation games.",
    url: "https://www.historyolympiad.com/"
  },
  {
    id: 457,
    name: "Genius Olympiad",
    category: "Competitions",
    description: "International high school competition focusing on global environmental issues, sustainability, and green innovation across science, art, writing, and design project categories.",
    url: "https://geniusolympiad.org/"
  },
  {
    id: 458,
    name: "International Economics Olympiad (IEO)",
    category: "Competitions",
    description: "Global tournament bringing together top national high school qualifiers to compete in economic theory exams, financial literacy assessments, and business case challenges.",
    url: "https://ecolympiad.org/"
  },
  {
    id: 459,
    name: "Rise Global Challenge",
    category: "Programs",
    description: "Global initiative by Schmidt Futures and the Rhodes Trust identifying exceptional 15-to-17-year-olds, providing lifetime access to university scholarships, funding, and mentorship.",
    url: "https://www.risefortheworld.org/"
  },
  {
    id: 460,
    name: "Stockholm Junior Water Prize (SJWP)",
    category: "Competitions",
    description: "Prestigious international water-science competition where students present innovative, research-based solutions to global water quality, sustainability, and resource management issues.",
    url: "https://www.siwi.org/stockholm-junior-water-prize/"
  },
  {
    id: 461,
    name: "National High School Journal of Science (NHSJS) Awards",
    category: "Awards",
    description: "Annual recognition honoring exceptional peer-reviewed scientific research papers submitted and published by high school students within the official NHSJS journal.",
    url: "https://nhsjs.org/"
  },
  {
    id: 462,
    name: "Gloria Barron Prize for Young Heroes",
    category: "Awards",
    description: "National award celebrating inspiring young leaders ages 8-18 who have developed and sustained high-impact community service, humanitarian, or environmental conservation projects.",
    url: "https://barronprize.org/"
  },
  {
    id: 463,
    name: "NASC National Council of Excellence (NOCE) Award",
    category: "Awards",
    description: "National institutional recognition program honoring active student councils that demonstrate exceptional year-round operations, civic engagement, structured student voice, and robust community service portfolios.",
    url: "https://www.natstuco.org/awards/national-councils-of-excellence/"
  },
  {
    id: 464,
    name: "American Legion Boys State / American Legion Auxiliary Girls State",
    category: "Summer Programs",
    description: "Intensive week-long civic leadership program where students learn the mechanics of local and state governance by running mock elections, introducing bills, and operating legislative bodies.",
    url: "https://www.legion.org/boysstate"
  },
  {
    id: 465,
    name: "American Legion Boys Nation / American Legion Auxiliary Girls Nation",
    category: "Summer Programs",
    description: "Elite week-long civic summit in Washington, D.C., where two selected senators from each state form a mock federal senate, debate policy, and study federal government operations.",
    url: "https://www.legion.org/boysnation"
  },
  {
    id: 466,
    name: "UC Santa Cruz Science Internship Program (SIP)",
    category: "Summer Programs",
    description: "Nine-week scientific research program matching high schoolers with faculty and graduate researchers to collaborate on open-ended, authentic university research projects.",
    url: "https://ucsc-sip.org/"
  },
  {
    id: 467,
    name: "Economics for Leaders (EFL)",
    category: "Summer Programs",
    description: "Selective week-long program hosted at various universities teaching high school leaders how to incorporate economic analysis and reasoning into public policy choices.",
    url: "https://www.fte.org/students/economics-for-leaders/"
  },
  {
    id: 468,
    name: "Hampshire College Summer Studies in Mathematics (HCSSiM)",
    category: "Summer Programs",
    description: "Six-week intensive residential program where mathematically talented students from around the world live and work on advanced mathematical exploration alongside university faculty.",
    url: "https://hcssim.org/"
  },
  {
    id: 469,
    name: "AAJA JCamp",
    category: "Summer Programs",
    description: "Six-day national journalism training program designed to promote media diversity by pairing student reporters with top industry professionals for workshops in broadcasting, photography, and print.",
    url: "https://www.aaja.org/programs-and-initiatives/jcamp/"
  },
  {
    id: 470,
    name: "MathILy",
    category: "Summer Programs",
    description: "Highly selective five-week residential math program at Bryn Mawr College centered on advanced mathematical problem-solving, proof-writing, and independent inquiry mixed with a humorous learning culture.",
    url: "http://www.mathily.org/"
  },
  {
    id: 471,
    name: "University of Notre Dame Summer Scholars",
    category: "Summer Programs",
    description: "Two-week pre-college experience where students take rigorous, faculty-led courses across nearly 30 disciplines, earn college credit, and experience residential campus life.",
    url: "https://precollege.nd.edu/summer-scholars/"
  },
  {
    id: 472,
    name: "Hk Maker Lab (Columbia University)",
    category: "Summer Programs",
    description: "Five-week engineering design experience for New York City students from underrepresented backgrounds, focusing on biomedical innovation and tech prototyping.",
    url: "https://hypothekids.org/hk-maker-lab"
  },
  {
    id: 473,
    name: "Tufts University Biomedical Engineering Research Scholars (TUBERS)",
    category: "Summer Programs",
    description: "Six-week commuter research immersion where local students learn experimental protocols and complete biomedical engineering projects suitable for national science fairs.",
    url: "https://engineering.tufts.edu/bme/tubers"
  },
  {
    id: 474,
    name: "Pomona College Academy for Youth Success (PAYS)",
    category: "Summer Programs",
    description: "Four-week intensive residential academic preparation pipeline supporting local, traditionally underrepresented, or first-generation students through seminars, workshops, and research projects.",
    url: "https://www.pomona.edu/administration/pays"
  },
  {
    id: 475,
    name: "Berkeley Business Academy for Youth (B-BAY)",
    category: "Summer Programs",
    description: "Immersive business development program at UC Berkeley where student teams build, research, and present a complete corporate or entrepreneurial business plan.",
    url: "https://haas.berkeley.edu/b-bay/"
  },
  {
    id: 476,
    name: "Bow Seat Ocean Awareness Contest",
    category: "Competitions",
    description: "Global creative competition inviting students to raise awareness for critical environmental and marine conservation issues through art, writing, film, and performance.",
    url: "https://bowseat.org/programs/ocean-awareness-contest/"
  },
  {
    id: 477,
    name: "Music No Boundaries Chapter President Program",
    category: "Programs",
    description: "Student leadership pathway focused on establishing local chapters, organizing inclusive music performance events, and promoting community arts equity.",
    url: "https://www.musicnoboundaries.org/"
  },
  {
    id: 478,
    name: "Lead4Change Student Leadership Program",
    category: "Programs",
    description: "Free project-based curriculum that empowers teams of 6th-12th grade students to design, fund, and execute impactful community service learning campaigns.",
    url: "https://www.lead4change.org/"
  },
  {
    id: 479,
    name: "Children's Health Student Volunteer Program",
    category: "Programs",
    description: "Hospital service track offering high school students foundational exposure to patient care, clinical environments, and healthcare support workflows.",
    url: "https://www.childrens.com/get-involved/volunteer"
  },
  {
    id: 480,
    name: "Houston Food Bank Student Heroes Program",
    category: "Programs",
    description: "Year-round tiered leadership initiative training high schoolers in food logistics, volunteer management, and localized public service operations.",
    url: "https://www.houstonfoodbank.org/reproductivedesigntracker/studentheroes/"
  },
  {
    id: 481,
    name: "Byte Into STEM Scholarship",
    category: "Scholarships",
    description: "Merit-based financial award recognizing high school students demonstrating strong academic commitment and leadership potential in computer science or technology.",
    url: "https://www.byteintostem.org/"
  },
  {
    id: 482,
    name: "Open Door Scholarship by Kitware",
    category: "Scholarships",
    description: "Renewable multi-year scholarship and corporate mentorship program supporting high school seniors pursuing a bachelor's degree in computer science.",
    url: "https://www.kitware.com/open-door-scholarship/"
  },
  {
    id: 483,
    name: "Future Leaders in Tech Scholarship",
    category: "Scholarships",
    description: "Financial award supporting innovative pre-college students who show a strong commitment to technological entrepreneurship and community problem-solving.",
    url: "https://bold.org/"
  },
  {
    id: 484,
    name: "BWISE Scholarship Program",
    category: "Scholarships",
    description: "Merit-based financial award and mentorship initiative hosted by Black Women Instilling Scholarly Excellence, recognizing high-achieving high school seniors for leadership, community involvement, and essay writing.",
    url: "https://www.bwiseinc.org/"
  },
  {
    id: 485,
    name: "Fifth Month Scholarship",
    category: "Scholarships",
    description: "Annual creative writing scholarship managed by Unigo that challenges students ages 14 and older to compose an imaginative, short essay centered on a lighthearted prompt about the calendar year.",
    url: "https://www.unigo.com/scholarships/our-scholarships/fifth-month-scholarship"
  },
  {
    id: 486,
    name: "Delete Cyberbullying Scholarship",
    category: "Scholarships",
    description: "National award program selecting high school and college students based on a written personal statement detailing their commitment to anti-bullying advocacy, mental health awareness, and digital safety.",
    url: "http://www.deletecyberbullying.eu/scholarship.html"
  },
  {
    id: 487,
    name: "Tufts University Mini-Med School",
    category: "Summer Programs",
    description: "Two-week pre-college program providing high school students with immersive, clinical exposure to the field of medicine, veterinary sciences, and healthcare research through lectures and case studies.",
    url: "https://universitycollege.tufts.edu/high-school-programs/programs/mini-med-school"
  },
  {
    id: 488,
    name: "Oxford Summer Courses (High School Track)",
    category: "Summer Programs",
    description: "Prestigious international academic program hosting students for multi-week tutorials and Socratic seminars taught by university scholars across the humanities, business, and STEM tracks.",
    url: "https://oxfordsummercourses.com/"
  },
  {
    id: 489,
    name: "Salk Institute High School Scholars Program",
    category: "Summer Programs",
    description: "Paid eight-week biology research internship at the Salk Institute for Biological Studies where students gain hands-on experience working directly inside active scientific laboratories.",
    url: "https://www.salk.edu/about/education-outreach/high-school-scholars-program/"
  },
  {
    id: 490,
    name: "Watershed Academy",
    category: "Summer Programs",
    description: "Applied environmental science and sustainability program where students conduct field research alongside working scientists and architects on real-world ecological design projects.",
    url: "https://www.watershedacademy.org/"
  },
  {
    id: 491,
    name: "QCaMP (Quantum Computing, Math & Physics Camp)",
    category: "Summer Programs",
    description: "Niche specialized summer camp providing high school students with hands-on, foundational exposure to quantum technologies, advanced physics concepts, and computational mathematics.",
    url: "https://qcamp.org/"
  },
  {
    id: 492,
    name: "Management & Technology Summer Institute (M&TSI)",
    category: "Summer Programs",
    description: "Rigorous summer program at the University of Pennsylvania combining engineering principles, corporate business concepts, and technological entrepreneurship.",
    url: "https://www.andlinger.upenn.edu/"
  },
  {
    id: 493,
    name: "Wonderworks Summer Programs",
    category: "Summer Programs",
    description: "Pre-college academic enrichment courses held in partnership with Rice University and the University of Houston, focusing on creative writing, architecture, filmmaking, law, and economics.",
    url: "http://www.wonderworkshouston.org/"
  },
  {
    id: 494,
    name: "RISD Summer Teen Art Institute",
    category: "Summer Programs",
    description: "Intensive studio program at the Rhode Island School of Design tailored for advanced visual arts students focusing on rigorous technical portfolio development.",
    url: "https://precollege.risd.edu/"
  },
  {
    id: 495,
    name: "Juilliard Summer Music",
    category: "Summer Programs",
    description: "Elite music conservatory program providing instrumentalists, composers, and vocalists with high-level technical training and mentorship from master faculty.",
    url: "https://www.juilliard.edu/stage-beyond/summer-programs"
  },
  {
    id: 496,
    name: "The Posse Foundation Scholarship",
    category: "Scholarships",
    description: "Highly respected merit and leadership scholarship providing full-tuition college funding to outstanding student cohorts via a specialized organizational nomination process.",
    url: "https://www.possefoundation.org/"
  },
  {
    id: 497,
    name: "DAR Scholarships",
    category: "Scholarships",
    description: "Diverse national scholarship pool hosted by the Daughters of the American Revolution covering niche academic tracks including medicine, STEM, law, nursing, and education.",
    url: "https://www.dar.org/national-society/scholarships"
  },
  {
    id: 498,
    name: "Student Press Law Center Student Press Freedom Awards",
    category: "Awards",
    description: "National journalism award honoring high school and college student journalists who have demonstrated outstanding commitment to challenging censorship and supporting free expression.",
    url: "https://splc.org/awards/"
  },
  {
    id: 499,
    name: "Miami Herald Silver Knight Awards",
    category: "Awards",
    description: "Highly regarded regional awards program recognizing high school seniors for major achievements in community service coupled with outstanding academic excellence.",
    url: "https://www.miamiherald.com/news/local/education/silver-knight/"
  },
  {
    id: 500,
    name: "Student Peace Awards",
    category: "Awards",
    description: "Specialized award program recognizing high school students who display active leadership in promoting peace, inclusivity, and positive social impact within their communities.",
    url: "https://www.studentpeaceawards.org/"
  },
  {
    id: 501,
    name: "A.H. Nickless Innovation Award",
    category: "Competitions",
    description: "Regional STEM innovation competition challenging team-based high school innovators to develop and pitch technical solutions to real-world problems for scholarship funding.",
    url: "https://ahnicklessinnovationaward.com/"
  },
  {
    id: 502,
    name: "Student Code Alliance",
    category: "Nonprofits",
    description: "Student-led computer science organization where members build socially impactful software projects, lead regional branches, and teach coding fundamentals to others.",
    url: "https://www.studentcodealliance.org/"
  },
  {
    id: 503,
    name: "Project WORTHY",
    category: "Nonprofits",
    description: "Youth-led civic advocacy nonprofit focused on providing measurable, on-the-ground support, public awareness campaigns, and resources for homeless youth populations.",
    url: "https://www.projectworthy.org/"
  },
  {
    id: 504,
    name: "Hidden Gems Inc.",
    category: "Nonprofits",
    description: "Unique youth development nonprofit utilizing archery instruction alongside core educational enrichment modules to build leadership skills among underserved youth.",
    url: "https://www.hiddengemsarchery.com/"
  },
  {
    id: 505,
    name: "STEMist Education",
    category: "Nonprofits",
    description: "Student-led STEM outreach organization providing high schoolers with chapter leadership opportunities to organize community science events and educational workshops.",
    url: "https://www.stemisteducation.org/"
  },
  {
    id: 506,
    name: "SODA: Student Organ Donation Advocates",
    category: "Nonprofits",
    description: "Advocacy nonprofit where students establish local chapters to educate communities about organ donation, backed by institutional grants and scholarship opportunities.",
    url: "https://www.sodauational.org/"
  },
  {
    id: 507,
    name: "Project EDSA",
    category: "Nonprofits",
    description: "Youth-led health organization focused on eating disorder awareness, body positivity, and implementing structured peer support networks in high schools.",
    url: "https://www.projectedsa.org/"
  },
  {
    id: 508,
    name: "To Write Love on Her Arms (TWLOHA) Student Ambassadors",
    category: "Nonprofits",
    description: "Mental health and suicide prevention network offering dedicated student ambassador pathways to reduce stigma and coordinate local community mental health awareness campaigns.",
    url: "https://twloha.withintent.io/get-involved/student-ambassadors/"
  },
  {
    id: 509,
    name: "Students Rebuild",
    category: "Nonprofits",
    description: "Global youth service program that challenges students to design and execute creative community service projects addressing hunger, refugee crises, and climate changes.",
    url: "https://www.studentsrebuild.org/"
  },
  {
    id: 510,
    name: "buildOn",
    category: "Nonprofits",
    description: "Global service learning organization developing high school leaders through intensive local community tutoring, urban service initiatives, and international school construction projects.",
    url: "https://www.buildon.org/"
  },
  {
    id: 511,
    name: "Student Voice Initiative",
    category: "Nonprofits",
    description: "Youth advocacy platform empowering students to conduct independent, youth-led sociological research and advocate for public education and community policy reform.",
    url: "https://www.stuvoice.org/"
  },
  {
    id: 512,
    name: "ThinkGive National Student Advisory Board",
    category: "Nonprofits",
    description: "National leadership board where selected high school students collaborate to shape corporate nonprofit strategy, pilot empathy curricula, and guide community impact projects.",
    url: "https://www.thinkgiveproject.org/"
  },
  {
    id: 513,
    name: "Impact Lynk",
    category: "Nonprofits",
    description: "Civic matchmaking network that assists high school students in launching local chapters to coordinate, track, and execute thousands of community volunteer hours.",
    url: "https://www.impactlynk.org/"
  },
  {
    id: 514,
    name: "Youth Service America (YSA)",
    category: "Nonprofits",
    description: "Massive youth service coalition offering organizational activation grants, national campaigns, and resource toolkits for student-led localized public service projects.",
    url: "https://ysa.org/"
  },
  {
    id: 515,
    name: "The Civic Circle",
    category: "Nonprofits",
    description: "Civic engagement organization utilizing creative arts and educational modules to teach young students the foundational principles of local government, democracy, and advocacy.",
    url: "https://theciviccircle.org/"
  },
  {
    id: 516,
    name: "DoSomething.org",
    category: "Nonprofits",
    description: "Large-scale digital youth activism platform mobilizing millions of students to execute targeted micro-volunteer campaigns centered on climate, voting rights, and poverty alleviation.",
    url: "https://www.dosomething.org/"
  },
  {
    id: 517,
    name: "California Association of Student Councils (CASC)",
    category: "Nonprofits",
    description: "Student-led leadership institute providing structured training in peer advocacy, legislative lobbying, and comprehensive student government mechanics.",
    url: "https://www.casc.net/"
  },
  {
    id: 518,
    name: "Give SevenDays",
    category: "Nonprofits",
    description: "Anti-hate and interfaith youth organization providing student leadership opportunities, community kindness workshops, and annual competitive scholarship programs.",
    url: "https://givesevendays.org/"
  },
  {
    id: 519,
    name: "Sunrise Movement",
    category: "Nonprofits",
    description: "Youth-led political climate movement organizing local community hubs to advocate for renewable energy policy, green jobs, and systemic climate action.",
    url: "https://www.sunrisemovement.org/"
  },
  {
    id: 520,
    name: "Citizens’ Climate Lobby Youth Action Team",
    category: "Nonprofits",
    description: "Policy-focused youth network training high schoolers in non-partisan political lobbying, congressional communications, and carbon fee policy advocacy.",
    url: "https://citizensclimatelobby.org/youth/"
  },
  {
    id: 521,
    name: "LEAP: Leaders for Ethics, Animals, and the Planet",
    category: "Nonprofits",
    description: "Specialized humane education and agricultural program pairing students with rescue sanctuaries to study environmental ethics, animal welfare, and sustainable food systems.",
    url: "https://www.leapprogram.org/"
  },
  {
    id: 522,
    name: "Letters Connect",
    category: "Nonprofits",
    description: "Student-led outreach initiative combating senior citizen isolation and loneliness through structured, handwritten intergenerational letter-writing campaigns.",
    url: "https://www.lettersconnect.org/"
  },
  {
    id: 523,
    name: "Young Investors Society Stock Pitch Competition",
    category: "Competitions",
    description: "Global finance competition where students write comprehensive equity research reports, perform corporate valuations, and pitch a selected public company to a panel of investment professionals.",
    url: "https://younginvestorssociety.org/"
  },
  {
    id: 524,
    name: "Wall Street South Stock Pitch Competition",
    category: "Competitions",
    description: "Niche finance challenge simulating collegiate-level stock pitching where student teams defend financial analysis and investment theses before industry leaders.",
    url: "https://www.wallstreetsouth.org/"
  },
  {
    id: 525,
    name: "International Finance Challenge (IFC)",
    category: "Competitions",
    description: "Student-run global finance competition providing early leadership and involvement opportunities alongside professional market analysis and evaluation.",
    url: "https://www.finance-challenge.org/"
  },
  {
    id: 526,
    name: "Tim Draper High School Utah Entrepreneur Challenge",
    category: "Competitions",
    description: "Venture-backed business competition offering high school startup founders seed funding, capital exposure, and structured corporate mentorship.",
    url: "https://ecenter.utah.edu/"
  },
  {
    id: 527,
    name: "Verge Challenge",
    category: "Competitions",
    description: "Emerging youth entrepreneurship and business pitch tournament focused on highlighting grassroots student startups and early-stage innovations.",
    url: "https://www.vergechallenge.com/"
  },
  {
    id: 528,
    name: "Ladder Internships",
    category: "Internships",
    description: "Project-based remote internship program matching high school students with active startups for mentored sprints in consulting, finance, business strategy, and technology.",
    url: "https://www.ladderinternships.com/"
  },
  {
    id: 529,
    name: "DECA Scholarships Program",
    category: "Scholarships",
    description: "Institutional merit awards distributing substantial corporate funding annually to high school seniors demonstrating excellence in marketing, finance, hospitality, and management.",
    url: "https://www.deca.org/high-school-programs/scholarships/"
  },
  {
    id: 530,
    name: "FBLA Business Achievement Awards (BAA)",
    category: "Awards",
    description: "Tiered leadership development and business recognition program evaluating high schoolers on corporate education, civic service, and commercial literacy milestones.",
    url: "https://www.fbla-pbl.org/high-school/learning-programs/baa/"
  },
  {
    id: 531,
    name: "BeyondQuantum Program",
    category: "Programs",
    description: "Immersive 12-week online research initiative teaching advanced high school students quantum computing mechanics, foundational physics, and scientific research methodologies.",
    url: "https://www.beyondquantum.org/"
  },
  {
    id: 532,
    name: "Pathways to Quantum Immersion Program",
    category: "Programs",
    description: "George Mason University-connected summer immersion experience exposing students to quantum computing careers, research labs, and academic mentorship pipelines.",
    url: "https://quantum.gmu.edu/"
  },
  {
    id: 533,
    name: "daeZERO Quantum Program",
    category: "Programs",
    description: "Nonprofit quantum education initiative operating in partnership with Yale University that provides accessible instruction in quantum circuits, basic coding, and cryptography.",
    url: "https://www.daezero.org/"
  },
  {
    id: 534,
    name: "Global Quantum Mechanics Challenge (GQMC)",
    category: "Competitions",
    description: "International physics competition featuring multi-round qualifying exams and research-paper-based semifinals focused on theoretical quantum mechanics problems.",
    url: "https://www.gqmc-competition.org/"
  },
  {
    id: 535,
    name: "MIT iQuHACK",
    category: "Competitions",
    description: "MIT's annual quantum computing hackathon offering remote participation tracks, beginner-friendly programming workshops, and corporate-sponsored coding challenges.",
    url: "https://iquhack.mit.edu/"
  },
  {
    id: 536,
    name: "QuSTEAM",
    category: "Nonprofits",
    description: "National quantum education non-profit organization collaborating with academic institutions to design undergraduate-level curricula and student workforce outreach programs.",
    url: "https://qusteam.org/"
  },
  {
    id: 537,
    name: "Qollider",
    category: "Nonprofits",
    description: "Specialized social-impact non-profit dedicated to expanding quantum computing literacy and STEM career exposure pathways for youth in rural and underserved communities.",
    url: "https://www.qollider.org/"
  },
  {
    id: 538,
    name: "BU Quantum Student Initiatives",
    category: "Clubs",
    description: "Student-led physics and technology collective at Boston University that organizes public research symposia, introductory quantum programming hackathons, and networking forums.",
    url: "https://www.bu.edu/"
  },
  {
    id: 539,
    name: "Quantum Computing Student Association (QCSA UCLA)",
    category: "Clubs",
    description: "Pre-professional collegiate engineering association partnering with major hardware developers to host quantum logic workshops and commercial research panels.",
    url: "https://qcsa.ucla.edu/"
  },
  {
    id: 540,
    name: "Quantum Information Science Club Association (QISCA)",
    category: "Clubs",
    description: "Nationwide student coalition coordinating advanced high school and undergraduate research circles focused on quantum machine learning and cryptographic network standards.",
    url: "https://www.qisca.org/"
  },
  {
    id: 541,
    name: "Lumiere Research Inclusion Foundation",
    category: "Programs",
    description: "Fully funded, 1-on-1 remote research program pairing talented low-income high school students with PhD mentors to complete independent research projects.",
    url: "https://www.lumiere-education.com/"
  },
  {
    id: 542,
    name: "Heithoff-Brody High School Scholars Program at the Salk Institute",
    category: "Summer Programs",
    description: "Paid 8-week summer biology internship for San Diego County high school residents to gain practical lab experience inside active scientific research facilities.",
    url: "https://www.salk.edu/about/education-outreach/high-school-scholars-program/"
  },
  {
    id: 543,
    name: "The Johns Hopkins Internship in Brain Science Program (JHIBS)",
    category: "Internships",
    description: "Research program offering stipends to high school juniors and seniors from underrepresented backgrounds to conduct research under leading neuroscientists.",
    url: "https://www.hopkinsmedicine.org/neurology_neurosurgery/about_us/jhibs.html"
  },
  {
    id: 544,
    name: "The Mary Miller Summer Program by the PHC Group",
    category: "Internships",
    description: "Paid remote summer marketing and public health research internship helping high school juniors and seniors build leadership and community project skills.",
    url: "https://www.thephcgroup.com/"
  },
  {
    id: 545,
    name: "Northwestern University Center for Talent Development Summer Camp",
    category: "Summer Programs",
    description: "Intensive summer academic camp offering high-achieving high school students university-level instruction and research opportunities across STEM and humanities tracks.",
    url: "https://www.ctd.northwestern.edu/"
  },
  {
    id: 546,
    name: "EnergyMag Research Internship",
    category: "Internships",
    description: "Flexible, remote research internship for sophomores, juniors, and seniors focused on analyzing emerging clean technology and renewable energy market trends.",
    url: "https://www.energymag.org/"
  },
  {
    id: 547,
    name: "Science and Engineering Apprenticeship Program (SEAP) by the Department of Navy",
    category: "Internships",
    description: "Competitive 8-week paid STEM apprenticeship placing high school students directly into Department of Navy laboratories nationwide for mentored engineering and physics research.",
    url: "https://www.seap.asee.org/"
  },
  {
    id: 548,
    name: "Seattle Children’s Research Institute Virtual Research Training Program",
    category: "Summer Programs",
    description: "Free summer training program introducing high school students to global health concepts, research methodologies, and college readiness skills.",
    url: "https://www.seattlechildrens.org/research/"
  },
  {
    id: 549,
    name: "National Institute on Drug Abuse (NIDA) RTURP Program",
    category: "Summer Programs",
    description: "Free summer research opportunity prioritizing underrepresented high school and college students to study the scientific basis of drug abuse at the Johns Hopkins campus.",
    url: "https://nida.nih.gov/"
  },
  {
    id: 550,
    name: "UC Irvine Math ExpLR Summer Research Program",
    category: "Summer Programs",
    description: "Free computational biology summer research program pairing high school students with principal investigators and undergraduates to develop quantitative scientific modeling skills.",
    url: "https://www.math.uci.edu/"
  },
  {
    id: 551,
    name: "SHTEM: Summer Internships for High Schoolers at Stanford University",
    category: "Summer Programs",
    description: "Free online research internship program exploring the intersections of information science, engineering, linguistics, arts, and psychology under Stanford mentors.",
    url: "https://tsf.stanford.edu/"
  },
  {
    id: 552,
    name: "Internships at the American Psychological Association (APA)",
    category: "Internships",
    description: "Year-round remote and in-person opportunities for high schoolers to participate in research, professional writing, and public policy administration within professional psychology.",
    url: "https://www.apa.org/about/governance/bdarch/internships"
  },
  {
    id: 553,
    name: "NIH High School Scientific Training and Enrichment Program (HiSTEP)",
    category: "Summer Programs",
    description: "Fully funded 5-week biomedical and healthcare career exploration program on the main NIH campus, complete with a monthly stipend for local high school juniors.",
    url: "https://www.training.nih.gov/programs/histep"
  },
  {
    id: 554,
    name: "QuarkNet Summer Research Program",
    category: "Summer Programs",
    description: "Paid hourly physics research internship for Illinois high schoolers to collaborate with Fermilab scientists on quantum computing, detectors, and accelerator data systems.",
    url: "https://quarknet.org/"
  },
  {
    id: 555,
    name: "Maternal Child Health Careers/Research Initiatives for Student Enhancement-Undergraduate Program (MCHC-RISE-UP)",
    category: "Summer Programs",
    description: "A 9-to-10-week summer public health leadership program providing undergraduate juniors and seniors with mentored research, clinical exposure, and advocacy training to promote health equity.",
    url: "https://www.kennedykrieger.org/professional-training/professional-training-programs/rise-programs/mchc-rise-up"
  },
  {
    id: 560,
    name: "Summer Undergraduate Research or Internship Experience in Acoustics (SURIEA)",
    category: "Summer Programs",
    description: "A paid 10-week summer undergraduate research program hosted by the Acoustical Society of America for students interested in physical, architectural, or biomedical acoustics.",
    url: "https://acousticalsociety.org/summer-internship-mentor-application/"
  },
  {
    id: 561,
    name: "Air Force Science & Technology Fellowship Program",
    category: "Programs",
    description: "National Academies-administered postdoctoral and senior fellowship awards providing opportunities to conduct advanced physics, engineering, and digital science research at the U.S. Air Force Research Laboratory.",
    url: "https://www.nationalacademies.org/our-work/air-force-science-and-technology-fellowship-program"
  },
  {
    id: 562,
    name: "5 Strong - College Ready Prep Scholarship",
    category: "Scholarships",
    description: "Partnership with Historically Black Colleges and Universities (HBCUs) offering full-tuition scholarships, bimonthly preparation sessions, and academic cohort mentorship to student leaders.",
    url: "https://5strongscholars.org"
  },
  {
    id: 563,
    name: "STEM Summer Scholars Institute (SSI)",
    category: "Summer Programs",
    description: "An 8-week continuous research and academic training program hosted at IU Bloomington and IUPUI for select undergraduate students originating from Minority Serving Institutions.",
    url: "https://graduate.indiana.edu/admissions/diversity/summer-scholars/"
  },
  {
    id: 564,
    name: "Powering Education Scholarship by Alpha One",
    category: "Scholarships",
    description: "An annual merit award providing individual $2,000 college scholarships to selected student candidates with disabilities residing within the state of Maine.",
    url: "https://alphaonenow.org/"
  },
  {
    id: 565,
    name: "Entry Point! Program for Students with Disabilities by AAAS",
    category: "Internships",
    description: "National identification and screening program placing undergraduate and graduate STEM students with apparent or non-apparent disabilities into competitive 10-week summer research internships.",
    url: "https://www.aaas.org/programs/project-science-technology-and-disability"
  },
  {
    id: 566,
    name: "L’Oréal USA Fellowships for Women in Science Program",
    category: "Scholarships",
    description: "A prestigious national awards program administered alongside AAAS that annually grants $60,000 to five exceptional post-doctoral women scientists conducting biomedical or engineering research.",
    url: "https://www.aaas.org/awards/loreal-usa-fellowships-women-science-program"
  },
  {
    id: 567,
    name: "AAPD Summer Internship Program",
    category: "Internships",
    description: "A comprehensive summer corporate and congressional placement program in Washington, D.C., helping college students with disabilities cultivate public policy and community advocacy skills.",
    url: "https://www.aapd.com/summer-internship-program/"
  },
  {
    id: 568,
    name: "AAUW Community Action Grants",
    category: "Programs",
    description: "National funding resources distributed to individuals, local branches, and community nonprofits to launch innovative, non-partisan projects supporting equity for women and girls.",
    url: "https://www.aauw.org/resources/programs/fellowships-grants/"
  },
  {
    id: 569,
    name: "U.S. Geological Survey Mendenhall Postdoctoral Research Fellowships",
    category: "Programs",
    description: "Advanced research fellowships enabling recent doctoral graduates to conduct high-impact, concentrated science investigations utilizing specialized USGS laboratory systems and geographic data.",
    url: "https://www.usgs.gov/mendenhall-research-fellowship-program"
  },
  {
    id: 570,
    name: "Center for OLDest Ice EXploration (COLDEX) REU",
    category: "Summer Programs",
    description: "An immersive summer undergraduate research experience focusing on ice core processing, polar climate systems analysis, and tracking paleoclimate history.",
    url: "https://coldex.org/"
  },
  {
    id: 571,
    name: "APSA Ralph Bunche Summer Institute",
    category: "Summer Programs",
    description: "An intensive 5-week graduate-level summer program simulating political science scholarship, data analysis, and professional academic career paths for underrepresented undergraduate juniors.",
    url: "https://www.apsanet.org/rbsi"
  },
  {
    id: 572,
    name: "Naval Research Laboratory (NRL) Summer Internship Program for HBCU/MI Students",
    category: "Internships",
    description: "A 10-week engineering and applied sciences laboratory internship in Washington, D.C., tailored to introduce undergraduate students from Minority Serving Institutions to experimental naval research fields.",
    url: "https://www.asee.org/"
  },
  {
    id: 573,
    name: "NSF Center for Computer Assisted Synthesis (C-CAS) Summer Research Fellowship",
    category: "Summer Programs",
    description: "A mentored undergraduate chemistry fellowship enabling students to complete research at the intersection of organic chemical synthesis, computer data science, and machine learning models.",
    url: "https://ccas.nd.edu/"
  },
  {
    id: 574,
    name: "Katz and Manne Research Institute Summer Scholars Program",
    category: "Summer Programs",
    description: "A highly interactive scientific summer cohort initiative at Lurie Children’s Hospital introducing early-career students to pediatric medicine, hospital data methodologies, and clinical inquiry.",
    url: "https://www.luriechildrens.org/en/research/katz-manne-research-institute/"
  },
  {
    id: 575,
    name: "Appleton-Whittell Research Ranch Audubon Fellowship",
    category: "Programs",
    description: "Field-based conservation and ecology fellowship inviting researchers to execute data-driven environmental studies on grassland avian wildlife, local ecosystems, and desert habitats.",
    url: "https://researchranch.audubon.org/"
  },
  {
    id: 576,
    name: "Arizona NASA Space Grant Program",
    category: "Scholarships",
    description: "Statewide academic program distributing targeted undergraduate research scholarships and graduate outreach fellowships focused on aerospace engineering, planetary geology, and data systems.",
    url: "https://spacegrant.arizona.edu/"
  },
  {
    id: 577,
    name: "BioXFEL Undergraduate Summer Internship",
    category: "Summer Programs",
    description: "An NSF-supported summer program focused on teaching students biological data analysis techniques, molecular structural biology, and research workflows using high-speed X-ray Free Electron Lasers.",
    url: "https://www.bioxfel.org/"
  },
  {
    id: 578,
    name: "FORCE High Pressure Summer School",
    category: "Summer Programs",
    description: "A specialized multi-day hands-on educational laboratory session at Arizona State University exploring compressed physical environments, high-pressure earth science materials testing, and multi-anvil system mechanics.",
    url: "https://force.asu.edu/"
  },
  {
    id: 579,
    name: "Presidential Graduate Assistantship Program",
    category: "Scholarships",
    description: "A highly selective, merit-based internal recruitment award at ASU providing continuous financial stipend aid, faculty mentoring, and doctoral cohort support to incoming research scholars.",
    url: "https://graduate.asu.edu/pga"
  },
  {
    id: 580,
    name: "Applied Data Institute by Equitech Futures",
    category: "Programs",
    description: "An advanced global professional upskilling cohort designed to train data-driven leaders, recent graduates, and technical professionals on applied machine learning analytics.",
    url: "https://www.equitechfutures.com/"
  },
  {
    id: 581,
    name: "Equitech Futures Institute Undergraduate Fellowship",
    category: "Programs",
    description: "A global online academic fellowship providing undergraduate students with rigorous structured tutorials in statistical modeling, ethics in artificial intelligence, and data tool engineering.",
    url: "https://www.equitechfutures.com/"
  },
  {
    id: 582,
    name: "Collaborative Approaches Among Scientists and Engineers (CASE) REU",
    category: "Summer Programs",
    description: "A paid 10-week interdisciplinary NSF research experience at Auburn University bringing science and engineering undergrads together for joint laboratory projects and data optimization tracks.",
    url: "https://www.auburn.edu/"
  }
];

function classifyTrack(item) {
  const text = `${item.name} ${item.description} ${item.category}`.toLowerCase();
  const medicalHints = [
    "medical", "medicine", "health", "hospital", "biomed", "neuro", "genom",
    "cancer", "surgery", "clinical", "physiology", "nih", "nami", "st. jude",
    "sloan kettering", "rockefeller", "georgetown biotechnology", "hosa"
  ];
  const stemHints = [
    "robot", "coding", "computer", "cs", "math", "engineering", "science",
    "cyber", "ai", "programming", "physics", "chemistry", "rocket", "olympiad",
    "vex", "frc", "ftc", "tarc", "sees", "usaco", "amc", "iss", "satellite", "hack"
  ];

  if (medicalHints.some((k) => text.includes(k))) return "Medical";
  if (stemHints.some((k) => text.includes(k))) return "STEM";
  return "Humanities";
}

function getTrack(item) {
  return item.track || item.field || "Humanities";
}

function addTrackToItems(items) {
  if (!Array.isArray(items)) return [];
  return items.map((item) => ({
    ...item,
    track: item.track || item.field || classifyTrack(item),
  }));
}

function evenlyInterleaveByTag(items) {
  const buckets = { STEM: [], Humanities: [], Medical: [] };
  for (const item of items) {
    const track = getTrack(item);
    (buckets[track] || buckets.Humanities).push(item);
  }

  const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  buckets.STEM = shuffle(buckets.STEM);
  buckets.Humanities = shuffle(buckets.Humanities);
  buckets.Medical = shuffle(buckets.Medical);

  const order = ["STEM", "Humanities", "Medical"];
  const out = [];

  while (buckets.STEM.length || buckets.Humanities.length || buckets.Medical.length) {
    let pushed = false;
    for (const key of order) {
      if (buckets[key].length) {
        out.push(buckets[key].shift());
        pushed = true;
      }
    }
    if (!pushed) break;
  }
  return out;
}

// ================== SEARCH BAR ==================
function SearchBar({ searchTerm, setSearchTerm, handleActionIntercept }) {
  return (
    <div className="w-full relative z-10">
      <FontAwesomeIcon
        icon={faMagnifyingGlass}
        className="absolute left-4 top-[18px] text-slate-400 text-sm"
      />
      <input
        id="toolkit-search"
        type="text"
        placeholder="Search activities..."
        value={searchTerm}
        onChange={(e) => {
          if (handleActionIntercept()) return;
          setSearchTerm(e.target.value);
        }}
        onClick={() => handleActionIntercept()}
        className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-white/40 bg-white/70 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-300 transition text-sm text-slate-900 shadow-sm backdrop-blur-md"
      />
    </div>
  );
}

function FilterBar({ categories, selectedFilters, toggleFilter, isAuthenticated }) {
  return (
    <div className="rounded-[24px] bg-white/65 border border-white/60 p-5 shadow-[0_8px_30px_rgba(0,0,0,0.03)] backdrop-blur-md flex flex-col gap-2">
      <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 px-1 mb-2">
        Filter Categories
      </h3>
      {categories.map((cat) => {
        const isSelected = selectedFilters.includes(cat);
        const isInternships = cat === "Internships";

        const base =
          "w-full text-left px-3.5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 focus:outline-none tracking-wide shadow-sm flex items-center justify-between";

        let normal = isSelected
          ? "bg-[#1e3a5f] text-white"
          : "bg-white/50 text-slate-800 hover:bg-white/90 border border-white/20";

        let internships = isSelected
          ? `text-white bg-gradient-to-r from-[#1e3a5f] via-[#2c4f7c] to-[#5c83b0] shadow-md ring-2 ring-blue-300`
          : `text-white bg-gradient-to-r from-[#2c4f7c] to-[#3f6696] shadow-sm hover:brightness-110`;

        if (!isAuthenticated) {
          normal = "bg-slate-200/60 text-slate-400 border border-slate-300/40 cursor-pointer";
          internships = "bg-slate-300/50 text-slate-400 border border-slate-400/30 cursor-pointer";
        }

        return (
          <button
            key={cat}
            type="button"
            onClick={() => toggleFilter(cat)}
            className={`${base} ${isInternships ? internships : normal}`}
          >
            {isInternships ? (
              <span className="inline-flex items-center gap-1.5">
                <span className="text-xs">✨</span>
                {cat}
              </span>
            ) : (
              <span>{cat}</span>
            )}
            {isSelected && <span className="text-xs opacity-80">✓</span>}
          </button>
        );
      })}
    </div>
  );
}

const TRACK_STYLES = {
  STEM: "bg-green-50 text-green-700 border border-green-200",
  Humanities: "bg-amber-50 text-amber-700 border border-amber-200",
  Medical: "bg-blue-50 text-blue-700 border border-blue-200",
};

function ToolkitCard({ item, isSaved, toggleSave, onFindChapter, handleActionIntercept, isAuthenticated }) {
  const reduceMotion = useReducedMotion();
  const showChapterButton = item.category === "Clubs" || item.category === "Clubs & Organizations";
  const track = getTrack(item);
  
  const targetIcon = faExternalLinkAlt;

  const trackClass = isAuthenticated 
    ? (TRACK_STYLES[track] || "bg-slate-50 text-slate-700 border border-slate-200")
    : "bg-slate-200 text-slate-400 border border-slate-300";

  const cardBackgroundClass = isAuthenticated 
    ? "bg-white/65 hover:shadow-[0_20px_40px_rgba(30,58,95,0.08)] border-white/60" 
    : "bg-slate-100/50 border-slate-200/80 saturate-[0.6]";

  return (
    <motion.div
      whileHover={reduceMotion ? undefined : { y: -4 }}
      initial={reduceMotion ? undefined : { opacity: 0, y: 10 }}
      whileInView={reduceMotion ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      onClick={() => handleActionIntercept()}
      className={`relative rounded-[24px] shadow-[0_8px_30px_rgba(0,0,0,0.04)] border p-6 transition backdrop-blur-md flex flex-col justify-between min-h-[240px] cursor-pointer ${cardBackgroundClass}`}
    >
      <div>
        <div className="flex justify-between items-start gap-3">
          <div className="min-w-0">
            <h3 className={`font-bold text-sm leading-snug truncate ${!isAuthenticated ? "text-slate-500" : "text-slate-900"}`}>
              {item.name}
            </h3>
            <p className="text-[11px] font-bold tracking-wide uppercase text-slate-400 mt-1">{item.category}</p>
          </div>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (handleActionIntercept()) return;
              toggleSave(item);
            }}
            className="shrink-0 p-1 hover:bg-slate-100 rounded-full transition"
            aria-label={isSaved ? "Unsave activity" : "Save activity"}
          >
            <FontAwesomeIcon
              icon={isSaved ? faHeartSolid : faHeartRegular}
              className={`${isSaved ? "text-red-500" : "text-slate-300"} text-sm`}
            />
          </button>
        </div>

        <p className={`text-sm mt-3 leading-relaxed font-medium line-clamp-4 ${!isAuthenticated ? "text-slate-400/90" : "text-slate-600"}`}>
          {item.description}
        </p>

        {/* Dynamic Action Link Section */}
        {isAuthenticated && item.url && (
          <div className="mt-3">
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-[#1e3a5f] hover:text-[#2c4f7c] transition underline underline-offset-2"
            >
              <span>Official Website</span>
              <FontAwesomeIcon icon={targetIcon} className="text-[10px]" />
            </a>
          </div>
        )}

        {item.email && (
          <a
            href={`mailto:${item.email}`}
            onClick={(e) => {
              e.stopPropagation();
              if (handleActionIntercept()) e.preventDefault();
            }}
            className="inline-block mt-3 text-xs text-[#2c4f7c] hover:text-[#1e3a5f] font-semibold transition break-all underline underline-offset-2"
          >
            {item.email}
          </a>
        )}
      </div>

      <div className="mt-5 flex items-center justify-between pt-2">
        {showChapterButton ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (handleActionIntercept()) return;
              onFindChapter(item);
            }}
            className="inline-flex items-center justify-center rounded-xl border border-black/10 px-4 py-2 text-xs font-bold text-slate-800 bg-white/80 hover:bg-white transition shadow-sm"
          >
            Find local chapter →
          </button>
        ) : (
          <div />
        )}

        <div className={`px-2.5 py-0.5 rounded-md text-[10px] font-bold tracking-wider uppercase ${trackClass}`}>
          {track}
        </div>
      </div>
    </motion.div>
  );
}

// ================== MAIN COMPONENT ==================
export default function AdmissionsToolkit() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // 🟢 Live Auth Implementation values sourced directly from context hooks
  const { user, loading } = useAuth();
  const isAuthenticated = !!user; 
  const gridRef = useRef(null);

  const [chapterModalOpen, setChapterModalOpen] = useState(false);
  const [authRequiredModalOpen, setAuthRequiredModalOpen] = useState(false);
  const [chapterOrg, setChapterOrg] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [savedItems, setSavedItems] = useState([]);

  const categories = useMemo(() => [
    "Nonprofits",
    "Competitions",
    "Awards",
    "Summer Programs",
    "Internships",
    "Clubs",
    "Programs",
    "Scholarships"
  ], []);

  const internshipsSelected = selectedFilters.includes("Internships");
  const baseBtnClass = "text-sm font-semibold transition-all duration-200 px-4 py-2 rounded-xl text-slate-700 hover:bg-white/70 hover:text-slate-950";
  const activeBtnClass = "text-sm font-semibold transition-all duration-200 px-4 py-2 rounded-xl bg-white text-slate-900 shadow-sm";

  const navItems = [
    { name: "Extracurriculars", link: "/admissionstoolkit" },
    { name: "Saved Activities", link: "/profile" },
  ];

  // 🟢 Sign Out Trigger Functionality
  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/", { replace: true });
  };

  const requireAuthAction = () => {
    if (!isAuthenticated) {
      setAuthRequiredModalOpen(true);
      return true;
    }
    return false;
  };

  const toggleFilter = (category) => {
    if (requireAuthAction()) return;
    setSelectedFilters((prev) =>
      prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]
    );
  };

  const toggleSave = async (item) => {
    if (requireAuthAction()) return;
    const alreadySaved = savedItems.includes(item.name);
    setSavedItems(alreadySaved ? savedItems.filter(n => n !== item.name) : [...savedItems, item.name]);
  };

  const scrambledItems = useMemo(() => {
  // Combine both arrays (and use .flat() because your researcherProfiles has a nested array near the end)
  const combinedData = [
    ...researcherProfiles.flat(),
    ...toolkitItems
  ];

  return evenlyInterleaveByTag(addTrackToItems(combinedData));
}, []);

const filteredItemsFull = useMemo(() => {
  let sourceItems = scrambledItems;
  const search = searchTerm.toLowerCase();

  return sourceItems.filter((item) => {
    const matchesSearch =
      (item.name && item.name.toLowerCase().includes(search)) ||
      (item.description && item.description.toLowerCase().includes(search));

    // Normalize or match both strict category variations and fallback track groupings
    const matchesFilter =
      selectedFilters.length === 0 ||
      selectedFilters.includes(item.category) ||
      (selectedFilters.includes("Clubs") && item.category === "Clubs & Organizations") ||
      (selectedFilters.includes("Awards") && item.category === "Awards & Honors") ||
      // Safeguard: Allows STEM/Medical researcher internships to show up under programmatic filters
      (selectedFilters.includes("Summer Programs") && item.category === "Internships") ||
      (selectedFilters.includes("Programs") && item.category === "Internships");

    return matchesSearch && matchesFilter;
  });
}, [scrambledItems, searchTerm, selectedFilters]);

  const visibleItems = useMemo(() => {
    if (!isAuthenticated) return scrambledItems.slice(0, 9);
    return filteredItemsFull;
  }, [filteredItemsFull, scrambledItems, isAuthenticated]);

  // 🟢 Structural loading guard condition added for initial fetch sequences
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#edf4ff] text-slate-700">
        Loading...
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#edf4ff] text-slate-900 font-sans selection:bg-blue-100 flex flex-col">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_#ffffff,_#dbeafe_35%,_#c7dcff_65%,_#b5cbf4_100%)]" />
      <div className="absolute inset-0 opacity-[0.04] bg-[linear-gradient(to_right,#1e3a5f_1px,transparent_1px),linear-gradient(to_bottom,#1e3a5f_1px,transparent_1px)] bg-[size:70px_70px]" />
      <div className="absolute inset-0 noise opacity-[0.035] mix-blend-overlay" />

      {/* ===== NAVBAR ===== */}
      <nav className="absolute top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-7 flex items-center justify-between">
          <div onClick={() => navigate("/")} className="flex items-center gap-3 cursor-pointer">
            <div className="w-9 h-9 rounded-2xl bg-[#1e3a5f] text-white flex items-center justify-center font-black shadow-lg">C</div>
            <div className="font-bold tracking-tight text-lg">CollegeNavi</div>
          </div>
          <div className="flex items-center gap-3 md:gap-5">
            {navItems.map((item) => {
              const active = location.pathname === item.link;
              return (
                <Link 
                  key={item.name} 
                  to={item.link} 
                  className={active ? activeBtnClass : baseBtnClass}
                >
                  {item.name}
                </Link>
              );
            })}

            {/* 🟢 DASHBOARD CONDITIONAL SIGN IN/OUT INTERFACE ROUTER LINKS */}
            {isAuthenticated ? (
              <button
                onClick={handleLogout}
                className="text-sm font-semibold text-red-600 hover:text-red-700 px-4 py-2 transition-all duration-200"
              >
                Logout
              </button>
            ) : (
              <Link
                to="/login"
                className={location.pathname === "/login" ? activeBtnClass : baseBtnClass}
              >
                Log In
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* ===== HERO HEADLINE ===== */}
      <section className="relative z-10 w-full flex flex-col items-center justify-center text-center px-6 max-w-7xl mx-auto pt-36 pb-6">
        <motion.h1 
          className="text-4xl md:text-6xl font-black tracking-tight leading-[1.05]"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
        >
          <span className="inline-block text-transparent bg-clip-text text-glaze-sweep">Admissions Toolkit</span>
        </motion.h1>
        <p className="mt-3 max-w-xl text-slate-600 text-sm md:text-base font-medium leading-relaxed">
          Explore and filter the latest curated extracurricular opportunities.
        </p>
      </section>

      {/* ===== GRID CONTAINER ===== */}
      <main className="relative z-10 max-w-7xl w-full mx-auto px-6 pb-24 grid grid-cols-1 md:grid-cols-[280px_1fr] gap-8 flex-grow">
        <aside className="flex flex-col gap-4">
          <FilterBar categories={categories} selectedFilters={selectedFilters} toggleFilter={toggleFilter} isAuthenticated={isAuthenticated} />
        </aside>

        <section className="flex flex-col gap-6">
          <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} handleActionIntercept={requireAuthAction} />
          
          {/* Static Sponsored Content Box */}
          {isAuthenticated && selectedFilters.includes("Programs") && (
            <div className="w-full relative">
              <div className="relative rounded-[32px] overflow-hidden shadow-xl border border-white/60 bg-white/80 backdrop-blur-xl">
                <div className="bg-gradient-to-r from-[#1e3a5f] via-[#2c4f7c] to-[#3f6696] px-6 py-6">
                  <h2 className="text-2xl md:text-3xl font-black text-white tracking-tight">Lumiere Research Scholar Program</h2>
                </div>
                <div className="px-6 py-6 space-y-4">
                  <p className="text-slate-700 text-sm leading-relaxed font-medium">
                    The Lumiere Research Scholar Program provides high school students the chance to conduct hands-on research with leading scholars.
                  </p>
                  <div className="pt-2">
                    <a
                      href="https://www.lumiere-education.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#1e3a5f] text-white text-xs font-bold px-5 py-3 rounded-xl shadow-md hover:bg-[#2c4f7c] transition"
                    >
                      <span>Visit Official Website</span>
                      <FontAwesomeIcon icon={faExternalLink} />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div ref={gridRef} className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
            {visibleItems.map((item) => (
              <ToolkitCard
                key={item.id}
                item={item}
                isSaved={savedItems.includes(item.name)}
                toggleSave={toggleSave}
                handleActionIntercept={requireAuthAction}
                isAuthenticated={isAuthenticated}
                onFindChapter={() => {
                  setChapterOrg(item.name);
                  setChapterModalOpen(true);
                }}
              />
            ))}
          </div>
        </section>
      </main>

      {/* ===== REGISTRY NOTIFICATION MODAL ===== */}
      {chapterModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/20 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-[28px] bg-white/95 backdrop-blur-xl p-6 shadow-xl border border-white/60">
            <h3 className="text-base font-bold text-slate-900 tracking-tight">Find a local chapter</h3>
            <p className="mt-1 text-xs font-bold uppercase text-slate-400">{chapterOrg}</p>
            <div className="mt-4 rounded-xl bg-slate-50 p-4 text-xs font-medium text-slate-600">
              We’re building a comprehensive local chapter registry framework and will release it in an update.
            </div>
            <button onClick={() => setChapterModalOpen(false)} className="mt-4 w-full rounded-xl bg-[#1e3a5f] py-2.5 text-xs font-bold text-white">Got it</button>
          </div>
        </div>
      )}

      {/* ===== AUTH REQUIRED MODAL ===== */}
      {authRequiredModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/20 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-[28px] bg-white/95 backdrop-blur-xl p-6 shadow-xl border border-white/60">
            <h3 className="text-base font-bold text-slate-900 tracking-tight">Authentication Required</h3>
            <p className="mt-2 text-xs font-medium text-slate-600">
              Please log into your account to filter categories, search activities, or save tracks.
            </p>
            <div className="mt-5 flex gap-3">
              <button 
                onClick={() => setAuthRequiredModalOpen(false)} 
                className="w-1/2 rounded-xl border border-slate-200 py-2.5 text-xs font-bold text-slate-700 hover:bg-slate-50 transition"
              >
                Cancel
              </button>
              <button 
                onClick={() => {
                  setAuthRequiredModalOpen(false);
                  navigate("/login");
                }} 
                className="w-1/2 rounded-xl bg-[#1e3a5f] py-2.5 text-xs font-bold text-white hover:bg-[#2c4f7c] transition"
              >
                Log In
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .noise { background-image: url("https://www.transparenttextures.com/patterns/noise.png"); }
        .text-glaze-sweep {
          background-image: linear-gradient(110deg, #1e3a5f 35%, #8bb4f7 48%, #ffffff 50%, #8bb4f7 52%, #1e3a5f 65%);
          background-size: 200% 100%;
          animation: glaze 3s linear infinite;
          -webkit-background-clip: text;
        }
        @keyframes glaze {
          0% { background-position: 100% 0; }
          100% { background-position: -100% 0; }
        }
      `}</style>
    </div>
  );
}